/*
  Flower Box Sale: meadow engine (flower-box-sale.js)
  Reads the [data-bloom-data] JSON printed by the theme section, works out the
  campaign phase in America/New_York, draws the shared meadow on one canvas and
  keeps the count live from the Railway service (or the store baseline).
  Round 2 adds the numbered flower label, the "Just planted" line and the
  "See your flower" card for ?bloom_order links from the order email, where the
  shopper can also give their flower a name that shows in the tap label.
  Plain script, no libraries. Load after bloom-config.js and bloom-flowers.js.
*/
(function (w, d) {
  'use strict';

  var TAU = Math.PI * 2;
  var SPROUT_MS = 3600, GLOW_MS = 2600, SETTLE_SPAN = 250, SETTLE_MS = 240, LEAVE_MS = 700, GUST_MS = 1500;
  var GROW_STEPS = 24, DEPTHS = 6, VARIANTS = 3, STREAM_RETRY_MS = 120000;
  // See your flower: wait for the first settle, retry a missed lookup after 2.5 s and 10 s,
  // start the replay once the scroll has landed, and stagger the replayed flowers.
  var MINE_SETTLE_MS = 900, MINE_RETRY_MS = [2500, 10000], MINE_DELAY_MS = 700, MINE_STAGGER_MS = 400;
  // Flower names: the longest name the service accepts, how long a save may take before it
  // counts as failed, and how long a just-saved name outlives a /counts body that predates it.
  var NAME_MAX = 24, NAME_SAVE_MS = 15000, NAME_HOLD_MS = 15000;
  var PHASES = ['tease', 'bloom', 'full', 'pressed'];
  // Defaults for every word the meadow shows. bloom-config.js labels override these (same keys).
  var LABELS = {
    plantedBy: '{species} {number}, planted by a {box}',
    plantedByPlain: 'Planted by a {box}', // used when a flower has no species name
    plantedByNamed: "{name}'s {species}, planted by a {box}", // a named flower: Salon Luna's tulip, planted by a Sculpt Studio Box
    countOne: 'flower planted',
    countMany: 'flowers planted',
    flowerOne: 'flower',
    flowerMany: 'flowers',
    milestoneReached: 'Milestone reached: {count} boxes. {reward}',
    milestoneItem: '{count} boxes',
    milestoneDone: 'Reached',
    recent: 'Just planted: a {species} for a {box}, {ago}.',
    agoNow: 'just now',
    agoMinute: '1 minute ago',
    agoMinutes: '{n} minutes ago',
    agoHour: '1 hour ago',
    agoHours: '{n} hours ago',
    agoDay: 'yesterday',
    agoDays: '{n} days ago',
    mineTitleOne: 'Your {flowers} is growing in the meadow.',
    mineTitleMany: 'Your {flowers} are growing in the meadow.',
    minePressedOne: 'Your {flowers} is pressed in the meadow.',
    minePressedMany: 'Your {flowers} are pressed in the meadow.',
    mineLine: 'Flower {number} of the {box}, planted {date}.',
    mineLineTwo: 'Flowers {first} and {last} of the {box}, planted {date}.',
    mineLineMany: 'Flowers {first} to {last} of the {box}, planted {date}.',
    minePinned: 'Your {flowers}',
    mineFallbackTitleOne: 'Your {flowers} is in this meadow.',
    mineFallbackTitleMany: 'Your {flowers} are in this meadow.',
    mineFallbackLine: 'It is one of the {count} planted by the {box} so far.',
    mineCopied: 'Link copied',
    shareCaption: 'My {species} in the Flower Box Sale',
    shareCaptionNamed: "{name}'s {species} in the Flower Box Sale",
    nameSaved: 'Saved. Your flower is named {name}.',
    nameRemoved: 'Name removed.',
    nameRejected: "That name can't be shown here. Try another.",
    nameTooLong: 'Keep it to 24 characters.',
    nameFailed: "We couldn't save that right now. Try again in a moment.",
    shareSite: 'wildflowersnailshop.com/pages/flower-box-sale',
    nextMilestone: '{left} more {boxes} unlock the next community reward',
    allMilestones: 'Every community reward is unlocked. Thank you.',
    seedsSown: 'seeds sown'
  };
  // Used only when bloom-config.js is missing these tables (an older cached copy).
  var SPECIES_NAMES = { tulip: 'Tulip', daffodil: 'Daffodil', cosmos: 'Cosmos', poppy: 'Poppy', iris: 'Iris', rose: 'Rose', zinnia: 'Zinnia' };
  var SPECIES_PLURAL = { tulip: 'tulips', daffodil: 'daffodils', cosmos: 'cosmos', poppy: 'poppies', iris: 'irises', rose: 'roses', zinnia: 'zinnias' };
  var SPECIES_ALIASES = { 'peony': 'tulip', 'daisy': 'tulip', 'peony-bud': 'daffodil', 'garden-rose': 'rose' };
  // Daylight meadow (Bloom). bloom-config.js carries the full set per phase and overrides these.
  var MEADOW = {
    skyTop: '#5F6FC9', skyMid: '#9DAEEA', skyBottom: '#EAEEF8', light: '#FFF8EC',
    mountains: '#8B93BE', cliff: '#9AA1B3', hills: '#7FB478', hillsFar: '#A9C9A0',
    soil: '#5A4636', soilHighlight: '#7A5F4A', grassBack: '#8FC27E', grassMid: '#4F9F55', grassFront: '#2F7A3E',
    road: '#E7B93A', roadDeep: '#C78E22', roadLine: '#8A5A14',
    city: '#2E9E6A', cityLight: '#9FF2C8', cityGlow: '#8DF0B8',
    haze: '#DCE4F3', tint: '', tintAmount: 0, rainbow: 0.17, stars: false
  };
  // Theme editor sky values that mean "use the phase palette": the original cream default and the current default.
  var SKY_DEFAULTS = ['#f6f1ea', '#5f6fc9'];
  var FALLBACK_COLORS = [{ hex: '#E99A8C' }, { hex: '#F0B9C4', alpha: 0.82 }, { hex: '#FBF8F3' }];

  // Seeds bouquet: a hand-tied bunch of the seven box flowers beside the Seeds title, drawn once
  // into its own canvas (no animation loop; the CSS gives it a slow float). Composed in design units
  // with the tie at BQ_TIE. Each slot: a is the tilt and the stem angle from the tie, d the tie to
  // head distance, s the size, z the row (0 back, 1 front), seed the flower's own seed.
  // Left to right: poppy, tulip (back), daffodil, cosmos (back, top), rose, iris (back), zinnia.
  var BQ_TIE = [106, 203], BQ_SIZE = 110, BQ_TIME = 4050;
  var BQ_BOX = [28, 10, 186, 229]; // what the bunch covers (left, top, right, bottom), fitted to the canvas
  var BQ_SLOTS = [
    { a: -0.27, d: 154, s: 0.86, z: 0, seed: 11 },
    { a: -0.14, d: 124, s: 1.04, z: 1, seed: 23 },
    { a: 0.01, d: 170, s: 0.84, z: 0, seed: 37 },
    { a: -0.44, d: 118, s: 0.96, z: 1, seed: 41 },
    { a: 0.29, d: 152, s: 0.88, z: 0, seed: 53 },
    { a: 0.16, d: 124, s: 1.06, z: 1, seed: 67 },
    { a: 0.44, d: 116, s: 1.0, z: 1, seed: 71 }
  ];
  var BQ_ORDER = [0, 4, 2, 3, 6, 5, 1]; // paint order: back row, front row edges, then the middle
  var BQ_SPECIES = ['tulip', 'daffodil', 'cosmos', 'poppy', 'iris', 'rose', 'zinnia'];
  // Shade per species that reads best on the twilight card (matched by name, first match wins),
  // and a stand-in when bloom-config.js has no shades for that flower.
  var BQ_PICK = {
    tulip: ['warm pink', 'clear pink'], daffodil: ['^white', 'white'], cosmos: ['gold', 'pink'],
    poppy: ['neon', 'fall'], iris: ['indigo', 'violet', 'cobalt'], rose: ['rose red', 'scarlet', 'crimson'],
    zinnia: ['yellow', 'gold', 'orange', 'red']
  };
  var BQ_HEX = { tulip: '#E99A8C', daffodil: '#FBF8F3', cosmos: '#D9B25C', poppy: '#FE4F6E', iris: '#8E52D3', rose: '#D7263D', zinnia: '#F8F877' };
  // Stem greens (light, dark) as bloom-flowers.js paints them, for the stems below each flower.
  var BQ_GREENS = {
    tulip: [[134, 170, 124], [46, 86, 60]], daffodil: [[122, 158, 140], [46, 84, 72]], iris: [[122, 158, 140], [46, 84, 72]],
    poppy: [[150, 168, 128], [66, 90, 60]], rose: [[112, 150, 92], [38, 70, 40]], other: [[127, 168, 102], [52, 92, 50]]
  };
  var BQ_STEM_W = { tulip: 1.1, cosmos: 0.7, poppy: 0.8, daffodil: 0.9 };

  // Oct 23: the light of each phase, layered over the bloom-config.js palette (which stays the
  // source of every base color). zenith deepens the top of the sky and horizon lights a warm band
  // above the hills. sun is where the light comes from (x of the stage width, y of the horizon
  // height, r of the shorter stage side) with the strength of its rays; moon, milky and beams are
  // the Seeds night sky. rim, sheen and shade light every flower (a bright edge along the top of
  // petals and leaves, a soft gloss on the bloom, shade at the foot of the stem). air is what
  // drifts through it: fireflies at twilight, pollen by day, golden motes at golden hour, plus
  // butterflies while the meadow is in flower. Pressed stays still.
  var MAGIC = {
    tease: {
      zenith: '#120F3A', zenithA: 0.5, horizon: '#FFB08A', horizonA: 0.36, sun: null,
      moon: { x: 0.16, y: 0.24 }, milky: true, beams: true, rainbowBoost: 1,
      rim: '#FFE7C4', rimA: 0.4, sheenA: 0.08, shadeA: 0.28,
      air: 'fireflies', butterflies: false, glints: 0.7
    },
    bloom: {
      zenith: '#2F4FCF', zenithA: 0.34, horizon: '#FFF2DA', horizonA: 0.44,
      sun: { x: 0.2, y: 0.25, r: 0.03, core: '#FFFDF2', rays: 0.45 }, rainbowBoost: 1.9,
      rim: '#FFF7E4', rimA: 0.55, sheenA: 0.15, shadeA: 0.26,
      air: 'pollen', butterflies: true, glints: 1
    },
    full: {
      zenith: '#5E3496', zenithA: 0.44, horizon: '#FFA445', horizonA: 0.46,
      sun: { x: 0.8, y: 0.8, r: 0.05, core: '#FFF3D2', rays: 1 }, rainbowBoost: 2.1,
      rim: '#FFCB6A', rimA: 0.78, sheenA: 0.13, shadeA: 0.3,
      air: 'gold', butterflies: true, glints: 1.5
    },
    pressed: {
      zenith: '#A9ACC4', zenithA: 0.12, horizon: '#FFF8EE', horizonA: 0.3, sun: null, rainbowBoost: 1,
      rim: '#FFFFFF', rimA: 0.22, sheenA: 0.06, shadeA: 0.14,
      air: '', butterflies: false, glints: 0
    }
  };
  // Butterfly wings: [main color, margin color]. A monarch, a blue, a lemon, a rose, a lilac.
  var BUTTERFLIES = [['#F59A2E', '#5A220C'], ['#62AEF7', '#173A86'], ['#F7E07A', '#7E6416'], ['#F6A2CB', '#8E2E62'], ['#BFA2EA', '#46287A']];

  var instances = [];
  var reducedOverride = null;

  // Silent in production. Add ?bloom_debug=1 to the page address to see diagnostics.
  function debug() {
    if (!urlParam('bloom_debug')) return;
    try { console.debug.apply(console, ['[FlowerBoxSale]'].concat([].slice.call(arguments))); } catch (e) { /* ignore */ }
  }
  function c01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function easeOut(t) { t = c01(t); return 1 - (1 - t) * (1 - t) * (1 - t); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function num(v, f) { v = +v; return isFinite(v) ? v : f; }
  function now() { return (w.performance && performance.now) ? performance.now() : Date.now(); }

  // Hashing: the same (seed, box, index) always gives the same flower.
  function hashStr(s) {
    var h = 2166136261;
    s = String(s);
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }
  function hash() {
    var h = 0x9E3779B9;
    for (var i = 0; i < arguments.length; i++) {
      h = Math.imul(h ^ (arguments[i] >>> 0), 0x85EBCA6B);
      h ^= h >>> 13; h = Math.imul(h, 0xC2B2AE35); h ^= h >>> 16;
    }
    return h >>> 0;
  }
  function h01(h, salt) { return hash(h, salt) / 4294967296; }

  var nf = null;
  function fmt(n) {
    try { nf = nf || new Intl.NumberFormat('en-US'); return nf.format(n); }
    catch (e) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  }
  function fill(str, map) {
    return String(str).replace(/\{(\w+)\}/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }
  // English article for a word: "an" before a vowel sound (an Ink Library, an Art Paste Trio, an iris).
  function anWord(word) {
    var s = String(word == null ? '' : word).trim().toLowerCase();
    if (/^(hour|honest|honou?r)/.test(s)) return true;
    if (/^(uni|use|usu|uti|eu|one\b|once)/.test(s)) return false;
    return /^[aeiou]/.test(s);
  }
  // fill(), plus the article rule: "a {key}" in a template becomes "an {key}" when the value
  // starts with a vowel sound. Works for every slot: "a {species} for a {box}".
  function fillA(str, map) {
    var t = String(str).replace(/\b([Aa]) \{(\w+)\}/g, function (m, a, k) {
      return (map[k] != null && anWord(map[k]) ? a + 'n' : a) + ' {' + k + '}';
    });
    return fill(t, map);
  }
  // "tulip", "tulip and daffodil", "tulip, daffodil and iris"
  function joinWords(list) {
    if (list.length < 2) return list.join('');
    return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
  }
  // Month and day of an instant on a clock in New York: "October 14".
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  function dateText(iso) {
    var t = Date.parse(iso);
    if (!isFinite(t)) t = Date.now();
    try { return new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'long', day: 'numeric' }).format(new Date(t)); }
    catch (e) { var dt = new Date(t); return MONTHS[dt.getMonth()] + ' ' + dt.getDate(); }
  }
  // The address to share: the page without its query string (the order link stays private).
  function pageLink() {
    var l = w.location;
    if (l.protocol === 'http:' || l.protocol === 'https:') return l.origin + l.pathname;
    return String(l.href).split(/[?#]/)[0];
  }
  // A flower name as the service stores it: spaces collapsed, trimmed. Shown with textContent only.
  // The cap is a guard for the layout; the service already limits names to NAME_MAX characters.
  function cleanName(v) { return typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, 48) : ''; }
  // Store titles can carry a prefix such as "BUNDLE | Puffy Gel Collection"; labels show only the box name.
  function cleanTitle(t) { return String(t || '').replace(/^\s*bundle\s*[|:\-]\s*/i, '').trim(); }
  function rgbOf(hex) { return w.BloomFlowers ? w.BloomFlowers.rgb(hex) : [128, 128, 128]; }
  function css(c, a) { return 'rgba(' + (c[0] | 0) + ',' + (c[1] | 0) + ',' + (c[2] | 0) + ',' + (a == null ? 1 : a) + ')'; }
  function mixHex(a, b, t) { var A = rgbOf(a), B = rgbOf(b); return [lerp(A[0], B[0], t), lerp(A[1], B[1], t), lerp(A[2], B[2], t)]; }

  /* ---------- phases (SPEC 3.2) ---------- */
  var fmtCache = {};
  function tzOffset(tz, t) {
    var f = fmtCache[tz] || (fmtCache[tz] = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }));
    var p = {};
    f.formatToParts(new Date(t)).forEach(function (x) { p[x.type] = x.value; });
    var asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
    return asUtc - Math.floor(t / 1000) * 1000;
  }
  // "YYYY-MM-DD HH:MM" read on a wall clock in tz, as a UTC instant (ms).
  function zonedInstant(str, tz) {
    var m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/.exec(String(str || '').trim());
    if (!m) return NaN;
    var guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
    try {
      var off = tzOffset(tz, guess), t = guess - off, off2 = tzOffset(tz, t);
      return off2 === off ? t : guess - off2;
    } catch (e) {
      return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0)).getTime();
    }
  }
  function resolvePhase(phases, tz, at) {
    if (!phases.length) return null;
    var cur = phases[0];
    for (var i = 0; i < phases.length; i++) {
      var t = zonedInstant(phases[i].start, tz);
      if (isFinite(t) && t <= at) cur = phases[i];
    }
    return cur;
  }
  function urlParam(name) {
    try { return (new URLSearchParams(w.location.search).get(name) || '').trim(); } catch (e) { return ''; }
  }
  function urlPhase() { return urlParam('bloom_phase'); }

  function prefersReduced() {
    if (reducedOverride !== null) return reducedOverride;
    try { return w.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }

  /* ---------- sprites shared by all instances ---------- */
  var sprites = {};
  function sprite(name, size, paint) {
    var k = name + size;
    if (sprites[k]) return sprites[k];
    var c = d.createElement('canvas');
    c.width = c.height = size;
    paint(c.getContext('2d'), size);
    return (sprites[k] = c);
  }
  function glowSprite() {
    return sprite('glow', 128, function (g, s) {
      var r = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
      r.addColorStop(0, 'rgba(255,248,226,0.95)');
      r.addColorStop(0.4, 'rgba(255,236,190,0.45)');
      r.addColorStop(1, 'rgba(255,236,190,0)');
      g.fillStyle = r; g.fillRect(0, 0, s, s);
    });
  }
  function sparkSprite() {
    return sprite('spark', 64, function (g, s) {
      var c = s / 2;
      var r = g.createRadialGradient(c, c, 0, c, c, c * 0.5);
      r.addColorStop(0, 'rgba(255,255,255,0.9)'); r.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = r; g.fillRect(0, 0, s, s);
      g.fillStyle = '#fff';
      g.beginPath();
      g.moveTo(c, 0); g.lineTo(c + s * 0.06, c - s * 0.06); g.lineTo(s, c); g.lineTo(c + s * 0.06, c + s * 0.06);
      g.lineTo(c, s); g.lineTo(c - s * 0.06, c + s * 0.06); g.lineTo(0, c); g.lineTo(c - s * 0.06, c - s * 0.06);
      g.closePath(); g.fill();
    });
  }
  // Oct 23 sprites for the air: a firefly (a bright core in a yellow-green glow), a speck of
  // pollen or light, and a soft golden bokeh orb, as a lens sees light out of focus.
  function fireflySprite() {
    return sprite('firefly', 64, function (g, s) {
      var c = s / 2, r = g.createRadialGradient(c, c, 0, c, c, c);
      r.addColorStop(0, 'rgba(255,255,226,1)'); r.addColorStop(0.1, 'rgba(250,255,170,0.95)');
      r.addColorStop(0.3, 'rgba(214,250,120,0.38)'); r.addColorStop(1, 'rgba(190,240,110,0)');
      g.fillStyle = r; g.fillRect(0, 0, s, s);
    });
  }
  function moteSprite(warm) {
    return sprite('mote' + (warm ? 'w' : 'c'), 32, function (g, s) {
      var c = s / 2, r = g.createRadialGradient(c, c, 0, c, c, c);
      r.addColorStop(0, warm ? 'rgba(255,248,214,1)' : 'rgba(255,255,255,1)');
      r.addColorStop(0.25, warm ? 'rgba(255,214,128,0.75)' : 'rgba(255,250,226,0.7)');
      r.addColorStop(1, 'rgba(255,220,150,0)');
      g.fillStyle = r; g.fillRect(0, 0, s, s);
    });
  }
  function bokehSprite() {
    return sprite('bokeh', 64, function (g, s) {
      var c = s / 2, r = g.createRadialGradient(c, c, 0, c, c, c * 0.96);
      r.addColorStop(0, 'rgba(255,232,170,0.5)'); r.addColorStop(0.6, 'rgba(255,212,130,0.36)');
      r.addColorStop(0.84, 'rgba(255,226,170,0.3)'); r.addColorStop(1, 'rgba(255,226,170,0)');
      g.fillStyle = r; g.beginPath(); g.arc(c, c, c * 0.96, 0, TAU); g.fill();
    });
  }
  // One side of a butterfly, drawn once per color into a 64 px sprite: the body runs up the left
  // edge (x 2), the forewing reaches up and out, the hindwing rounds off below, each shaded from a
  // deep base through the color to a lighter middle, with a dark margin and a few pale spots.
  function wingSprite(pair) {
    return sprite('wing' + pair[0], 64, function (g) {
      var c = rgbOf(pair[0]), e = rgbOf(pair[1]);
      var fore = function () { g.beginPath(); g.moveTo(2, 31); g.bezierCurveTo(10, 10, 38, 2, 58, 9); g.bezierCurveTo(62, 20, 46, 30, 2, 35); g.closePath(); };
      var hind = function () { g.beginPath(); g.moveTo(2, 33); g.bezierCurveTo(22, 33, 44, 40, 42, 52); g.bezierCurveTo(36, 62, 14, 58, 2, 38); g.closePath(); };
      var paint = function (shape, rr) {
        var f = g.createRadialGradient(2, 33, 0, 2, 33, rr);
        f.addColorStop(0, css([e[0] * 0.85, e[1] * 0.85, e[2] * 0.85])); f.addColorStop(0.2, css(c));
        f.addColorStop(0.58, css([lerp(c[0], 255, 0.3), lerp(c[1], 255, 0.3), lerp(c[2], 255, 0.3)]));
        f.addColorStop(0.86, css(c)); f.addColorStop(1, css(mixHex(c, e, 0.6)));
        shape(); g.fillStyle = f; g.fill();
        g.strokeStyle = css(e, 0.9); g.lineWidth = 2.2; shape(); g.stroke();
      };
      paint(hind, 44);
      paint(fore, 60);
      g.fillStyle = 'rgba(255,255,255,0.85)';
      [[50, 11, 1.6], [55, 15, 1.3], [44, 8, 1.2], [36, 52, 1.4], [28, 55, 1.1]].forEach(function (s) { g.beginPath(); g.arc(s[0], s[1], s[2], 0, TAU); g.fill(); });
    });
  }

  /* ---------- Seeds bouquet painter ---------- */
  // The shade of a box that suits the bouquet (BQ_PICK), else its first shade, else a stand-in.
  function bouquetShade(species, raw) {
    var cols = (Array.isArray(raw) ? raw : []).filter(function (e) { return e && (typeof e === 'string' || e.hex || e.color); });
    var words = BQ_PICK[species] || [];
    for (var i = 0; i < words.length; i++) {
      var re = new RegExp(words[i], 'i');
      for (var j = 0; j < cols.length; j++) if (re.test(String(cols[j].name || ''))) return cols[j];
    }
    return cols[0] || { hex: BQ_HEX[species] || '#E99A8C' };
  }

  // Paints the bouquet into ctx (a canvas of W x H css px at dpr). items: up to seven
  // { species, color, accent, alpha, glitter }. pal: the Seeds meadow palette (light, road, roadDeep).
  function paintBouquet(ctx, W, H, dpr, items, pal) {
    var BF = w.BloomFlowers, mix = BF.mix;
    // Fit the bunch to the canvas, standing on its bottom edge with the tie a little right of centre.
    var bw = BQ_BOX[2] - BQ_BOX[0], bh = BQ_BOX[3] - BQ_BOX[1], k = Math.min(W / bw, H / bh);
    var ox = Math.min(W - BQ_BOX[2] * k, Math.max(-BQ_BOX[0] * k, W * 0.54 - BQ_TIE[0] * k)), oy = H - BQ_BOX[3] * k;
    var frame = function (c) { c.setTransform(dpr, 0, 0, dpr, 0, 0); c.translate(ox, oy); c.scale(k, k); };
    var tx = BQ_TIE[0], ty = BQ_TIE[1], taken = [], fl = [];
    // Every species has its own place; a second flower of the same kind takes the next free one.
    items.slice(0, BQ_SLOTS.length).forEach(function (it) {
      var at = BQ_SPECIES.indexOf(it.species);
      if (at < 0 || taken[at]) { at = 0; while (taken[at]) at++; }
      taken[at] = true;
      var sl = BQ_SLOTS[at], S = BQ_SIZE * sl.s, rest = sl.d - S * 0.84, ux = Math.sin(sl.a), uy = -Math.cos(sl.a);
      fl.push({ it: it, sl: sl, S: S, hx: tx + ux * sl.d, hy: ty + uy * sl.d, bx: tx + ux * rest, by: ty + uy * rest });
    });
    if (!fl.length) return;
    ctx.save();
    frame(ctx);

    // A soft warm glow behind the heads, so the bunch looks lit from within.
    // It is an ellipse that fades out inside the canvas, so it never meets an edge.
    var light = rgbOf(pal.light || '#FFD5A8'), cx = 0, cy = 0;
    fl.forEach(function (f) { cx += f.hx; cy += f.hy; });
    cx /= fl.length; cy = cy / fl.length + 8;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(1, 0.84);
    var glow = ctx.createRadialGradient(0, 0, 0, 0, 0, 84);
    glow.addColorStop(0, css(light, 0.36)); glow.addColorStop(0.45, css(light, 0.15));
    glow.addColorStop(0.8, css(light, 0.03)); glow.addColorStop(1, css(light, 0));
    ctx.fillStyle = glow;
    ctx.fillRect(-84, -84, 168, 168);
    ctx.restore();

    // The stems below each flower: straight from its base through the tie, crossing there (the
    // spiral of a hand-tied bunch), to a cut end.
    fl.slice().sort(function (p, q) { return p.sl.a - q.sl.a; }).forEach(function (f, j, all) {
      f.cross = tx - (j - (all.length - 1) / 2) * 1.1;
    });
    fl.forEach(function (f) {
      var g = BQ_GREENS[f.it.species] || BQ_GREENS.other, w0 = Math.max(1.1, f.S * 0.022 * (BQ_STEM_W[f.it.species] || 1));
      var dx = f.bx - f.cross, dy = f.by - ty, dl = Math.sqrt(dx * dx + dy * dy) || 1;
      var len = 17 + (f.sl.seed * 7) % 6, ex = f.cross - dx / dl * len, ey = ty - dy / dl * len;
      var sx = -dy / dl * w0 * 0.18, sy = dx / dl * w0 * 0.18;
      ctx.lineCap = 'butt';
      ctx.strokeStyle = css(g[1]); ctx.lineWidth = w0;
      ctx.beginPath(); ctx.moveTo(ex, ey); ctx.lineTo(f.bx, f.by + 0.5); ctx.stroke();
      ctx.strokeStyle = css(g[0], 0.6); ctx.lineWidth = Math.max(0.5, w0 * 0.28);
      ctx.beginPath(); ctx.moveTo(ex + sx, ey + sy); ctx.lineTo(f.bx + sx, f.by + sy); ctx.stroke();
      ctx.fillStyle = css(mix(g[0], [236, 238, 206], 0.45));
      ctx.beginPath(); ctx.ellipse(ex, ey, w0 * 0.5, w0 * 0.24, Math.atan2(dy, dx) + Math.PI / 2, 0, TAU); ctx.fill();
    });

    // The flowers, fully open. The back row is painted on its own layer and joins a touch
    // see-through; the front row follows, edges first and the middle last.
    var flower = function (c, f) {
      var it = f.it;
      c.save();
      c.translate(f.bx, f.by);
      BF.drawFlower(c, {
        species: it.species, color: it.color, accent: it.accent, alpha: it.alpha, glitter: it.glitter,
        growth: 1, sway: f.sl.a, seed: f.sl.seed, size: f.S, time: BQ_TIME, shadow: false
      });
      c.restore();
    };
    var bySlot = function (z) {
      return BQ_ORDER.map(function (s) { return fl.filter(function (f) { return f.sl === BQ_SLOTS[s] && f.sl.z === z; })[0]; }).filter(Boolean);
    };
    var back = bySlot(0), front = bySlot(1);
    if (back.length) {
      var layer = d.createElement('canvas'), lc = null;
      layer.width = ctx.canvas.width; layer.height = ctx.canvas.height;
      try { lc = layer.getContext('2d'); } catch (e) { lc = null; }
      if (lc) {
        frame(lc);
        back.forEach(function (f) { flower(lc, f); });
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = 0.9;
        ctx.drawImage(layer, 0, 0);
        ctx.restore();
      } else back.forEach(function (f) { flower(ctx, f); });
    }
    front.forEach(function (f) { flower(ctx, f); });

    // The wrap: two soft bands of muted gold (the road color) around the bundle, no bow.
    var road = rgbOf(pal.road || '#C9A24A'), deep = rgbOf(pal.roadDeep || '#8E6A26');
    var muted = mix(road, [168, 150, 150], 0.22), hi = mix(muted, [255, 244, 214], 0.55), lo = mix(muted, deep, 0.7);
    [ty - 3.3, ty + 2.3].forEach(function (y, n) {
      var hw = 7.4 - n * 0.5, th = 2.4, sag = 3;
      var g2 = ctx.createLinearGradient(tx - hw, 0, tx + hw, 0);
      g2.addColorStop(0, css(lo, 0.92)); g2.addColorStop(0.36, css(hi, 0.96));
      g2.addColorStop(0.64, css(muted, 0.96)); g2.addColorStop(1, css(lo, 0.92));
      ctx.save();
      ctx.shadowColor = 'rgba(24,14,34,0.45)'; ctx.shadowBlur = 2 * k * dpr; ctx.shadowOffsetY = 0.8 * k * dpr;
      ctx.fillStyle = g2;
      ctx.beginPath();
      ctx.moveTo(tx - hw, y - th / 2);
      ctx.quadraticCurveTo(tx, y - th / 2 + sag, tx + hw, y - th / 2);
      ctx.lineTo(tx + hw, y + th / 2);
      ctx.quadraticCurveTo(tx, y + th / 2 + sag, tx - hw, y + th / 2);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      ctx.strokeStyle = css(hi, 0.55); ctx.lineWidth = 0.45; // a fine sheen along the top edge
      ctx.beginPath();
      ctx.moveTo(tx - hw * 0.8, y - th / 2 + 0.5);
      ctx.quadraticCurveTo(tx, y - th / 2 + 0.5 + sag * 0.8, tx + hw * 0.8, y - th / 2 + 0.5);
      ctx.stroke();
    });
    ctx.restore();
  }

  /* ---------- one garden ---------- */
  function Garden(root) {
    var self = this;
    this.root = root;
    this.stats = { fps: 0, frameMs: 0, flowers: 0, bitmaps: 0, quality: 0 };
    this.destroyed = false;
    this.timers = [];
    this.listeners = [];

    var data = {};
    try {
      var el = root.querySelector('[data-bloom-data]');
      data = JSON.parse((el && el.textContent) || '{}') || {};
    } catch (e) { debug('Could not read garden data', e); data = {}; }
    if (typeof data !== 'object') data = {};
    this.data = data;
    var cfg = this.cfg = (w.BloomConfig && typeof w.BloomConfig === 'object') ? w.BloomConfig : {};
    this.labels = Object.assign({}, LABELS, cfg.labels || {}, data.labels || {});
    this.seed = (num(data.seed, num(cfg.seed, 20261013))) >>> 0;
    this.tz = data.timezone || 'America/New_York';
    this.phases = (Array.isArray(data.phases) ? data.phases : []).filter(function (p) { return p && p.key; });
    if (!this.phases.length) this.phases = [{ key: 'bloom', label: 'Bloom', start: '2000-01-01 00:00' }];
    this.milestones = (Array.isArray(data.milestones) ? data.milestones : [])
      .filter(function (m) { return m && num(m.count, 0) > 0; })
      .map(function (m) { return { count: Math.round(+m.count), reward: m.reward || '', detail: m.detail || '' }; })
      .sort(function (a, b) { return a.count - b.count; });
    this.forced = '';
    this.phase = null;
    this.reduced = prefersReduced();

    // Boxes: payload order, merged with BloomConfig.
    var BF = w.BloomFlowers;
    this.boxes = [];
    this.boxByKey = {};
    (Array.isArray(data.boxes) ? data.boxes : []).forEach(function (b) { self.addBox(b); });

    // DOM
    this.stage = root.querySelector('[data-bloom-stage]');

    this.canvas = root.querySelector('[data-bloom-canvas]');
    this.labelEl = root.querySelector('[data-bloom-label]');
    this.liveEl = root.querySelector('[data-bloom-live]');
    this.mineEl = root.querySelector('[data-bloom-mine]');
    this.recentEl = root.querySelector('[data-bloom-recent]');
    this.recent = null;      // newest planting for the "Just planted" line: { box, species, ts }
    this.recentSim = false;  // a simulated sale (preview and QA) shows the line without a service URL
    this.mine = null;        // the See your flower card while it is showing
    this.mineList = [];      // flowers marked as the shopper's own (halo)
    this.mineToken = 0;      // bumps on hideMine so a late lookup never reopens the card
    this.mineGen = 0;        // bumps when the share picture must be painted again (a new name)
    this.names = [];         // flower names: [{ key, first, qty, name }], first is 1-based
    this.nameHold = null;    // the shopper's own save, kept over an older /counts body for a moment
    this.nameForm = root.querySelector('[data-bloom-mine-name]');
    this.nameInput = root.querySelector('[data-bloom-mine-name-input]');
    this.nameButton = root.querySelector('[data-bloom-mine-name-save]');
    this.ctx = null;
    try { this.ctx = this.canvas && BF ? this.canvas.getContext('2d') : null; } catch (e) { this.ctx = null; }

    this.all = [];          // flowers, sorted back to front
    this.cache = new Map(); // bitmap cache
    this.pointer = null;
    this.W = 0; this.H = 0; this.dpr = 1;
    this.visible = true;
    this.raf = 0;
    this.loadedAt = now();
    this.lastTotal = null;
    this.petals = [];
    this.seeds = [];
    this.frames = 0; this.fpsAt = now(); this.costSum = 0;
    this.quality = 0;       // adaptive quality level: 0 full, 1 lighter, 2 lightest
    this.slowSince = 0; this.lastT = 0; this.dtAvg = 16;
    this.demo = '';

    this.bindDom();
    try { this.setupBouquet(); } catch (e) { debug('bouquet setup failed', e); }
    this.applyPhase(true);
    this.syncAll(false, true);
    this.updateText(false);
    this.layout();
    this.loadImages();
    var demo = urlParam('bloom_demo');
    if (demo) this.setDemo(demo);
    else this.startLive();
    this.every(60000, function () { self.applyPhase(false); });
    this.kick();
    this.startMine();
  }

  var G = Garden.prototype;

  G.addBox = function (b) {
    if (!b || !b.key || this.boxByKey[b.key]) return null;
    var BF = w.BloomFlowers, cfg = this.cfg;
    var c = (cfg.boxes && cfg.boxes[b.key]) || {};
    // Replaced species keep working: a product metafield that still says "daisy" or "peony" shows the tulip (BloomConfig.speciesAliases).
    var wanted = this.alias(b.species);
    var species = (BF && BF.species[wanted]) ? wanted : ((BF && BF.species[c.species]) ? c.species : 'tulip');
    var colors = (Array.isArray(c.colors) && c.colors.length ? c.colors : FALLBACK_COLORS)
      .map(function (e) { return BF ? BF.styleFor(e) : { color: '#E99A8C', alpha: 1 }; });
    var packed = b.packed == null ? null : num(b.packed, null);
    var base = isFinite(+b.planted) && b.planted !== null ? Math.max(0, Math.round(+b.planted))
      : (packed != null && isFinite(+b.inventory) ? Math.max(0, packed - Math.round(+b.inventory)) : 0);
    if (packed == null && b.planted == null) base = 0;
    var box = {
      key: String(b.key), hk: hashStr(b.key), title: cleanTitle(b.title || c.title || b.key), species: species,
      sp: (BF && BF.species[species]) || { height: 1, headRatio: 0.4 },
      colors: colors, glitter: !!c.glitter, scale: num(c.scale, 1) || 1,
      imageUrl: c.image || ((cfg.species && cfg.species[species]) || {}).image || '', img: null,
      baseline: base, server: null, flowers: [], packed: packed == null ? 0 : packed
    };
    this.boxes.push(box);
    this.boxByKey[box.key] = box;
    return box;
  };

  // Species helpers: aliases applied, display names (Tulip), lower case words (tulip, 2 tulips).
  G.alias = function (key) {
    var a = this.cfg.speciesAliases || SPECIES_ALIASES;
    key = String(key == null ? '' : key).trim().toLowerCase();
    return a[key] || key;
  };
  G.speciesName = function (key) {
    var n = this.cfg.speciesNames || SPECIES_NAMES;
    return n[key] || '';
  };
  G.speciesWord = function (key, count) {
    var name = this.speciesName(key).toLowerCase();
    if (!name) return '';
    if (count > 1) return fmt(count) + ' ' + ((this.cfg.speciesPlural || SPECIES_PLURAL)[key] || name + 's');
    return name;
  };
  // {flowers}: groups is [{ species, n }] in order. "tulip", "2 tulips", "2 tulips and daffodil".
  G.flowersPhrase = function (groups) {
    var self = this;
    return joinWords(groups.map(function (g) { return self.speciesWord(g.species, g.n); }).filter(Boolean));
  };

  G.on = function (target, type, fn, opts) {
    if (!target) return;
    target.addEventListener(type, fn, opts || false);
    this.listeners.push([target, type, fn, opts || false]);
  };
  G.every = function (ms, fn) { this.timers.push(setInterval(fn, ms)); };
  G.later = function (ms, fn) { var t = setTimeout(fn, ms); this.timers.push(t); return t; };

  G.destroy = function () {
    this.destroyed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.timers.forEach(function (t) { clearInterval(t); clearTimeout(t); });
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    if (this.es) { try { this.es.close(); } catch (e) { /* ignore */ } }
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
    this.cache.clear();
    try { this.clearMine(); } catch (e) { /* ignore */ }
    try { this.teardownBouquet(); } catch (e) { /* ignore */ }
    if (this.pinEl && this.pinEl.parentNode) this.pinEl.parentNode.removeChild(this.pinEl);
    if (this.raysEl && this.raysEl.parentNode) this.raysEl.parentNode.removeChild(this.raysEl);
    var i = instances.indexOf(this);
    if (i >= 0) instances.splice(i, 1);
    if (this.root) this.root.__flowerBoxSale = null;
  };

  /* ---------- DOM wiring ---------- */
  G.bindDom = function () {
    var self = this;
    this.bindBurst();
    this.bindMore();
    var root = this.root, share = root.querySelector('[data-bloom-mine-share]'), save = root.querySelector('[data-bloom-mine-save]');
    if (share) this.on(share, 'click', function (e) { e.preventDefault(); self.shareMine(); });
    if (save) this.on(save, 'click', function (e) { e.preventDefault(); self.saveMine(); });
    if (this.nameForm) this.on(this.nameForm, 'submit', function (e) { e.preventDefault(); self.saveName(); });
    // The pinned "Your tulip" tag stays until the shopper taps or clicks somewhere else. A tap on
    // one of their own flowers shows its numbered label instead, and the buttons in the card keep it.
    this.on(d, 'pointerdown', function (e) {
      if (!self.pinOn && !self.focusOn) return;
      if (self.mineEl && self.mineEl.contains(e.target)) return;
      if (e.target === self.canvas) {
        var r = self.canvas.getBoundingClientRect(), hit = self.hitAt(e.clientX - r.left, e.clientY - r.top, e.pointerType !== 'mouse');
        if (hit && hit.f.mine) return;
      }
      self.pinOn = false;
      self.placePin();
      if (self.focusOn) { self.focusOn = false; self.updateFocus(); }
    }, { passive: true });
    if (this.stage && 'ResizeObserver' in w) {
      this.ro = new ResizeObserver(function () { self.layout(); });
      this.ro.observe(this.stage);
    } else {
      this.on(w, 'resize', function () { self.layout(); });
    }
    if (this.stage && 'IntersectionObserver' in w) {
      this.io = new IntersectionObserver(function (en) {
        self.visible = en[0] ? en[0].isIntersecting : true;
        self.kick();
      }, { rootMargin: '120px' });
      this.io.observe(this.stage);
    }
    this.on(d, 'visibilitychange', function () { self.kick(); });
    try {
      var mq = w.matchMedia('(prefers-reduced-motion: reduce)');
      var onMq = function () { self.setReduced(prefersReduced()); };
      if (mq.addEventListener) this.on(mq, 'change', onMq);
    } catch (e) { /* ignore */ }
    var c = this.canvas;
    if (c) {
      var tap = null;
      var move = function (e) {
        var r = c.getBoundingClientRect(), touch = e.pointerType !== 'mouse';
        self.pointer = { x: e.clientX - r.left, y: e.clientY - r.top, touch: touch };
        if (!touch) self.hover(false);
        else if (e.type === 'pointerdown') tap = { x: e.clientX, y: e.clientY };
        self.kick();
      };
      // Passive listeners: the meadow never blocks one-finger scrolling.
      this.on(c, 'pointermove', move, { passive: true });
      this.on(c, 'pointerdown', move, { passive: true });
      // A touch shows the label only on a tap (finger lifted without moving), never while scrolling.
      this.on(c, 'pointerup', function (e) {
        if (tap && e.pointerType !== 'mouse' && Math.abs(e.clientX - tap.x) < 10 && Math.abs(e.clientY - tap.y) < 10) self.hover(true);
        tap = null;
      }, { passive: true });
      this.on(c, 'pointerleave', function (e) { if (e.pointerType !== 'touch') { self.pointer = null; self.showLabel(null); } });
      this.on(c, 'pointercancel', function () { self.pointer = null; tap = null; });
    }
  };

  /* ---------- phase ---------- */
  G.currentPhaseKey = function () {
    var keys = this.phases.map(function (p) { return p.key; });
    var want = this.forced || urlPhase() || this.data.phaseOverride || '';
    if (want && want !== 'auto' && keys.indexOf(want) >= 0) return want;
    var p = resolvePhase(this.phases, this.tz, Date.now());
    return p ? p.key : 'bloom';
  };
  G.applyPhase = function (first) {
    var key = this.currentPhaseKey();
    if (!first && key === this.phase) return;
    var root = this.root, prev = this.phase;
    this.phase = key;
    var cls = root.className.split(/\s+/).filter(function (c) { return c && c.indexOf('flower-box-sale--phase-') !== 0; });
    cls.push('flower-box-sale--phase-' + key);
    root.className = cls.join(' ');
    var ph = null;
    for (var i = 0; i < this.phases.length; i++) if (this.phases[i].key === key) ph = this.phases[i];
    var lab = root.querySelector('[data-bloom-phase-label]');
    if (lab && ph && ph.label) lab.textContent = ph.label;
    var panels = root.querySelectorAll('[data-bloom-phase-panel]');
    for (i = 0; i < panels.length; i++) panels[i].hidden = panels[i].getAttribute('data-bloom-phase-panel') !== key;
    this.pal = this.palette(key);
    if (prev !== null) { this.cache.clear(); this.unbake(); this.paintScenery(); }
    this.petals = [];
    this.renderRecent(false);
    if (this.mine && prev !== null) {
      if (key === 'tease') this.presentMine(false); // back to bare soil: the card waits
      else if (this.minePending) this.presentMine(false); // Seeds turned to Bloom: show it now
      else { this.fillMineCard(); this.refreshMineImage(true); }
    }
    this.placePin();
    if (key === 'tease') { try { this.setupBouquet(); this.bqKey = ''; this.drawBouquet(); } catch (e) { debug('bouquet failed', e); } }
    this.kick();
  };
  G.palette = function (key) {
    var m = this.cfg.meadow || {};
    var p = Object.assign({}, MEADOW, m.bloom || {}, m[key] || {});
    // Theme editor colors (payload "colors" or CSS custom properties on the section).
    var o = this.data.colors || {};
    var st = null;
    try { st = w.getComputedStyle(this.root); } catch (e) { /* ignore */ }
    // JSON "colors" first, then CSS custom properties (--bloom-sky or --bloom-color-sky, and so on).
    var pick = function (k) {
      var list = [o[k]];
      if (st) list.push(st.getPropertyValue('--bloom-' + k), st.getPropertyValue('--bloom-color-' + k));
      for (var i = 0; i < list.length; i++) {
        var x = String(list[i] || '').trim();
        if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(x)) return x;
      }
      return '';
    };
    var sky = pick('sky'), soil = pick('soil'), gold = pick('golden');
    if (sky && SKY_DEFAULTS.indexOf(sky.toLowerCase()) < 0 && (key === 'bloom' || key === 'tease')) {
      p.skyTop = sky;
      p.skyMid = css(mixHex(sky, p.skyBottom, 0.5));
    }
    if (!p.skyMid) p.skyMid = css(mixHex(p.skyTop, p.skyBottom, 0.5));
    if (soil) p.soil = soil;
    if (gold && key === 'full') { p.light = gold; p.tint = gold; }
    return p;
  };

  G.setPhase = function (key) {
    this.forced = key && key !== 'auto' ? String(key) : '';
    this.applyPhase(false);
    this.updateText(false);
  };

  G.setReduced = function (flag) {
    flag = !!flag;
    this.reduced = flag;
    this.root.classList.toggle('flower-box-sale--reduced-motion', flag);
    if (flag) for (var i = 0; i < this.all.length; i++) { var f = this.all[i]; if (f.mode !== 3) f.mode = 0; f.push = 0; }
    this.layerDirty = true; this.forceBake = true;
    this.kick();
  };

  /* ---------- Seeds bouquet ---------- */
  // The script adds the bouquet canvas to the Seeds panel itself, so the section markup never
  // needs changing. Without bloom-flowers.js there is no bouquet and the panel stays as it was.
  G.setupBouquet = function () {
    if (this.destroyed || !w.BloomFlowers || !w.BloomFlowers.drawFlower) return;
    var panel = this.root.querySelector('[data-bloom-phase-panel="tease"]');
    if (!panel) return;
    if (this.bqCanvas && this.bqCanvas.parentNode === panel) return;
    this.teardownBouquet();
    var self = this, cv = panel.querySelector('.flower-box-sale__bouquet');
    if (!cv) {
      cv = d.createElement('canvas');
      cv.className = 'flower-box-sale__bouquet';
      cv.width = cv.height = 0; // takes no room until the stylesheet sizes it
      var text = panel.querySelector('.flower-box-sale__tease-text');
      panel.insertBefore(cv, text && text.parentNode === panel ? text : panel.firstChild);
    }
    cv.setAttribute('aria-hidden', 'true');
    panel.classList.add('flower-box-sale__tease--bouquet');
    this.bqCanvas = cv;
    this.bqPanel = panel;
    this.bqKey = '';
    // Redraw when the canvas changes size (window resize, or the panel shown again) or the screen density changes.
    if ('ResizeObserver' in w) {
      this.bqRo = new ResizeObserver(function () { self.drawBouquet(); });
      this.bqRo.observe(cv);
    } else {
      this.on(w, 'resize', function () { self.drawBouquet(); });
    }
    this.watchBouquetDpr();
  };
  G.watchBouquetDpr = function () {
    var self = this;
    if (this.bqMq && this.bqMqFn) { try { this.bqMq.removeEventListener('change', this.bqMqFn); } catch (e) { /* ignore */ } }
    this.bqMq = null;
    if (!this.bqCanvas || !w.matchMedia) return;
    try {
      var mq = w.matchMedia('(resolution: ' + (w.devicePixelRatio || 1) + 'dppx)');
      if (!mq.addEventListener) return;
      this.bqMqFn = function () { self.drawBouquet(); self.watchBouquetDpr(); };
      mq.addEventListener('change', this.bqMqFn);
      this.bqMq = mq;
    } catch (e) { /* ignore */ }
  };
  G.teardownBouquet = function () {
    if (this.bqRo) { this.bqRo.disconnect(); this.bqRo = null; }
    if (this.bqMq && this.bqMqFn) { try { this.bqMq.removeEventListener('change', this.bqMqFn); } catch (e) { /* ignore */ } }
    this.bqMq = null; this.bqMqFn = null;
    if (this.bqCanvas && this.bqCanvas.parentNode) this.bqCanvas.parentNode.removeChild(this.bqCanvas);
    if (this.bqPanel) this.bqPanel.classList.remove('flower-box-sale__tease--bouquet');
    this.bqCanvas = null; this.bqPanel = null; this.bqKey = '';
  };
  // One flower per box in box order. With fewer than seven boxes the rest come from the
  // bloom-config.js boxes (then the seven default kinds), so the bunch always has seven.
  G.bouquetItems = function () {
    var BF = w.BloomFlowers, self = this, cb = this.cfg.boxes || {}, list = [], used = {};
    var add = function (species, raw, glitter) {
      if (list.length >= BQ_SLOTS.length || !BF.species[species]) return;
      var st = BF.styleFor(bouquetShade(species, raw));
      list.push({ species: species, color: st.color, accent: st.accent, alpha: st.alpha, glitter: !!glitter });
      used[species] = true;
    };
    this.boxes.forEach(function (b) { add(b.species, (cb[b.key] || {}).colors, b.glitter); });
    Object.keys(cb).forEach(function (key) {
      var c = cb[key] || {}, sp = self.alias(c.species);
      if (!used[sp]) add(sp, c.colors, c.glitter);
    });
    BQ_SPECIES.forEach(function (sp) { if (!used[sp]) add(sp, null, sp === 'cosmos'); });
    return list;
  };
  // Draws once at the screen density (capped at 2). Only while Seeds shows: the hidden panel costs nothing.
  G.drawBouquet = function () {
    var cv = this.bqCanvas;
    if (!cv || this.destroyed || this.phase !== 'tease' || !w.BloomFlowers) return;
    // The size the stylesheet gives it (the CSS float does not change it). Without the stylesheet
    // the canvas would follow its own pixel size, so it is left blank rather than redrawn larger and larger.
    var st = null;
    try { st = w.getComputedStyle(cv); } catch (e) { st = null; }
    if (!st || st.position !== 'absolute') return;
    var W = parseFloat(st.width) || cv.offsetWidth, H = parseFloat(st.height) || cv.offsetHeight;
    if (!(W > 0 && H > 0)) return;
    var dpr = Math.min(2, w.devicePixelRatio || 1), key = W.toFixed(2) + 'x' + H.toFixed(2) + '@' + dpr;
    if (key === this.bqKey) return;
    try {
      cv.width = Math.round(W * dpr);
      cv.height = Math.round(H * dpr);
      var ctx = cv.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, cv.width, cv.height);
      paintBouquet(ctx, W, H, dpr, this.bouquetItems(), this.palette('tease'));
      this.bqKey = key;
    } catch (e) { debug('bouquet failed', e); }
  };

  /* ---------- counts ---------- */
  // box.proved: flowers a /mine answer has shown to exist, so a slow or stale count never hides them.
  G.shown = function (box) { return Math.max(box.baseline, box.server == null ? 0 : box.server, box.proved || 0); };
  G.total = function () {
    var t = 0;
    for (var i = 0; i < this.boxes.length; i++) t += this.shown(this.boxes[i]);
    return t;
  };

  // Matches flowers on the canvas to the displayed counts.
  G.syncAll = function (live, initial) {
    for (var i = 0; i < this.boxes.length; i++) this.syncBox(this.boxes[i], live, initial);
    this.stats.flowers = this.countFlowers();
  };
  G.syncBox = function (box, live, initial) {
    var target = this.shown(box), t = now(), quiet = this.reduced;
    var settleNew = initial || (!live && t - this.loadedAt < 6000);
    while (box.flowers.length < target) {
      var f = this.makeFlower(box, box.flowers.length);
      if (quiet) f.mode = 0;
      else if (settleNew) { f.mode = 2; f.t0 = t + 30 + f.v * SETTLE_SPAN + h01(f.h, 9) * 80; f.ready = false; }
      else { f.mode = 1; f.t0 = t + Math.random() * 120; }
      box.flowers.push(f);
      this.insert(f);
    }
    while (box.flowers.length > target) {
      var g = box.flowers.pop();
      if (g.baked) { g.baked = false; this.layerDirty = true; this.forceBake = true; }
      if (quiet) this.remove(g); else { g.mode = 3; g.t0 = t; }
    }
    this.nameBox(box);
  };
  G.countFlowers = function () {
    var n = 0;
    for (var i = 0; i < this.boxes.length; i++) n += this.boxes[i].flowers.length;
    return n;
  };

  G.makeFlower = function (box, i) {
    var h = hash(this.seed, box.hk, i);
    var v = Math.pow(h01(h, 2), 1.12);
    var f = {
      box: box, i: i, h: h, ci: i % box.colors.length, vr: 0,
      u: h01(h, 1), v: v, jit: 0.86 + 0.28 * h01(h, 3), flip: h01(h, 4) < 0.5 ? -1 : 1,
      ph: h01(h, 5) * TAU, bucket: Math.min(DEPTHS - 1, Math.floor(v * DEPTHS)),
      mode: 0, t0: 0, push: 0, last: null, glowEnd: 0
    };
    // Seed variants so neighbours of the same shade differ: 3 in the front row, 2 just behind,
    // 1 further back where repeats are invisible. This keeps the bitmap cache bounded.
    f.vr = hash(h, 6) % (f.bucket >= DEPTHS - 1 ? VARIANTS : f.bucket >= DEPTHS - 3 ? 2 : 1);
    this.place(f);
    return f;
  };
  G.insert = function (f) {
    var a = this.all, lo = 0, hi = a.length;
    while (lo < hi) { var mid = (lo + hi) >> 1; if (a[mid].v <= f.v) lo = mid + 1; else hi = mid; }
    a.splice(lo, 0, f);
  };
  G.remove = function (f) {
    var i = this.all.indexOf(f);
    if (i >= 0) this.all.splice(i, 1);
    f.gone = true;
  };

  G.setServer = function (key, count, live) {
    var box = this.boxByKey[key];
    if (!box || !isFinite(+count)) return;
    box.server = Math.max(0, Math.round(+count));
    this.syncBox(box, live, false);
  };
  G.applySnapshot = function (body, live) {
    if (!body || typeof body !== 'object' || !body.boxes) return;
    var self = this;
    Object.keys(body.boxes).forEach(function (k) {
      var b = body.boxes[k];
      self.setServer(k, b && typeof b === 'object' ? b.count : b, live);
    });
    if (Array.isArray(body.recent) && body.recent[0]) this.setRecent(body.recent[0], false);
    if (Array.isArray(body.names)) this.setNames(body.names);
    this.afterChange();
  };
  G.afterChange = function () {
    this.stats.flowers = this.countFlowers();
    this.updateText(true);
    this.mineNameCheck();
    this.kick();
  };

  /* ---------- flower names (SPEC 3.8) ---------- */
  // One name per order, on every flower of that order. this.names holds what the service
  // shows ({ key, first, qty, name }); each flower carries its name as f.name, which the tap
  // label reads. Names are text only, so the bitmap cache never changes for them.
  function nameEntry(e, name) {
    if (!e || typeof e !== 'object' || !e.box) return null;
    return { key: String(e.box), first: Math.max(1, Math.round(num(e.first, 1))), qty: Math.max(1, Math.round(num(e.quantity, 1))), name: name };
  }
  // Every name, from a snapshot or /counts body (the list is complete: a missing order has no name).
  G.setNames = function (list) {
    var out = [];
    list.forEach(function (e) {
      var x = nameEntry(e, cleanName(e && e.name));
      if (x && x.name) out.push(x);
    });
    this.names = out;
    // A body fetched just before the shopper's own save must not take that name away again.
    var hold = this.nameHold;
    if (hold && now() < hold.until) this.upsertNames(hold.boxes, hold.name, true);
    else this.nameHold = null;
    this.applyNames();
  };
  // One order's name changed (a named event, or the shopper's own save): boxes is
  // [{ box, first, quantity }], an empty name removes it.
  G.upsertNames = function (boxes, name, quiet) {
    var self = this;
    name = cleanName(name);
    (Array.isArray(boxes) ? boxes : []).forEach(function (b) {
      var x = nameEntry(b, name);
      if (!x) return;
      self.names = self.names.filter(function (e) { return !(e.key === x.key && e.first === x.first); });
      if (name) self.names.push(x);
    });
    if (!quiet) { this.applyNames(); this.mineNameCheck(); }
  };
  G.applyNamed = function (m) {
    if (!m || typeof m !== 'object' || !Array.isArray(m.boxes)) return;
    this.upsertNames(m.boxes, typeof m.name === 'string' ? m.name : '');
  };
  G.applyNames = function () {
    for (var i = 0; i < this.boxes.length; i++) this.nameBox(this.boxes[i]);
  };
  // Flowers first .. first + qty - 1 of the box, each number clamped to the box's current count.
  G.nameBox = function (box) {
    var list = box.flowers, n = list.length, any = false, i, j, e;
    if (box.named) for (i = 0; i < n; i++) if (list[i].name) list[i].name = '';
    for (i = 0; i < this.names.length; i++) {
      e = this.names[i];
      if (e.key !== box.key || n < 1) continue;
      for (j = Math.min(e.first, n); j <= Math.min(e.first + e.qty - 1, n); j++) list[j - 1].name = e.name;
      any = true;
    }
    box.named = any;
  };

  // Seeds shows bare soil on purpose, so a preview that adds flowers moves to Bloom first
  // (only when no phase is already forced by the page, the address or the theme setting).
  G.leaveSeedsForPreview = function () {
    if (this.phase === 'tease' && !this.forced && !urlPhase() && !this.data.phaseOverride) this.setPhase('bloom');
  };

  G.simulate = function (key, delta) {
    var box = this.boxByKey[key];
    if (!box) return;
    if (delta > 0) this.leaveSeedsForPreview();
    var cur = this.shown(box);
    var next = Math.max(0, cur + delta);
    if (delta < 0) box.baseline = Math.min(box.baseline, next);
    box.server = next;
    this.syncBox(box, true, false);
    // Simulated sales come only from the preview page and QA (FlowerBoxSale.simulateSale). They
    // feed the "Just planted" line as if a service URL were set, even when the preview's URL is
    // blank, so the line can be checked offline. Real shoppers never trigger this path.
    if (delta > 0) this.setRecent({ box: key, species: box.species, timestamp: new Date().toISOString() }, true);
    this.afterChange();
  };

  /* ---------- demo preview mode (?bloom_demo=half|full|300) ---------- */
  // Fills the meadow with clearly labelled demo flowers so Lauren can preview launch day
  // before any sales exist. Never touches the live connection or any storage.
  G.setDemo = function (mode) {
    var self = this;
    mode = String(mode == null ? '' : mode).trim().toLowerCase();
    var on = !!mode && mode !== 'off' && mode !== '0' && mode !== 'false';
    if (on && !this.demo) {
      this.stopLive();
      this.saved = this.boxes.map(function (b) { return [b.key, b.baseline, b.server]; });
    }
    if (!on) {
      if (!this.demo) return;
      this.demo = '';
      var keep = {};
      (this.saved || []).forEach(function (x) { keep[x[0]] = x; });
      this.boxes.slice().forEach(function (b) {
        while (b.flowers.length) self.remove(b.flowers.pop());
        if (!keep[b.key]) { self.boxes.splice(self.boxes.indexOf(b), 1); delete self.boxByKey[b.key]; }
        else { b.baseline = keep[b.key][1]; b.server = keep[b.key][2]; b.proved = 0; }
      });
      this.pill(false);
      this.unbake(true);
      this.syncAll(false, true);
      this.lastTotal = null;
      this.updateText(false);
      this.startLive();
      this.kick();
      return;
    }
    this.demo = mode;
    this.leaveSeedsForPreview();
    // No products on the page yet: borrow the seven boxes from BloomConfig for the preview.
    if (!this.boxes.length) {
      Object.keys(this.cfg.boxes || {}).forEach(function (k) {
        var c = self.cfg.boxes[k] || {};
        var b = self.addBox({ key: k, title: c.title, species: c.species, packed: null, planted: 0 });
        if (b) b.demoOnly = true;
      });
    }
    var cap = 0;
    this.boxes.forEach(function (b) { cap += b.packed > 0 ? b.packed : 0; });
    var n = mode === 'half' ? (cap ? Math.round(cap / 2) : 250) : mode === 'full' ? (cap || 500) : Math.round(num(mode, 250));
    n = Math.max(0, Math.min(5000, n));
    var weights = cap ? this.boxes.map(function (b) { return b.packed > 0 ? b.packed : 0; }) : null;
    this.fillTo(n, weights);
    this.pill(true);
  };
  G.pill = function (show) {
    var el = this.pillEl;
    if (show && !el && this.stage) {
      el = this.pillEl = d.createElement('div');
      el.className = 'flower-box-sale__preview-pill';
      el.setAttribute('role', 'note');
      el.textContent = 'Preview: demo flowers, not real sales';
      this.stage.appendChild(el);
    }
    if (el) el.hidden = !show;
  };

  G.debugFill = function (n) {
    var self = this;
    this.fillTo(n, this.boxes.map(function (b) { return num(((self.cfg.boxes || {})[b.key] || {}).max, 1); }));
  };
  G.fillTo = function (n, weights) {
    var self = this;
    weights = weights || this.boxes.map(function () { return 1; });
    var sum = weights.reduce(function (a, b) { return a + b; }, 0) || 1, left = n, big = 0;
    this.boxes.forEach(function (b, i) {
      var k = Math.floor(n * weights[i] / sum);
      if (weights[i] > weights[big]) big = i;
      left -= k;
      b.baseline = k; b.server = null; b.proved = 0;
      while (b.flowers.length) self.remove(b.flowers.pop());
    });
    if (this.boxes.length) this.boxes[big].baseline += Math.max(0, left); // rounding remainder
    this.lastTotal = null;
    this.unbake(true);
    this.syncAll(false, true);
    this.updateText(false);
    this.kick();
  };

  /* ---------- text, milestones, label ---------- */
  G.updateText = function (announce) {
    var root = this.root, L = this.labels, total = this.total(), i;
    // During Seeds the big number is the seeds sown in the soil, not the (zero) flowers.
    var seeds = this.phase === 'tease';
    var nums = root.querySelectorAll('[data-bloom-count-number]');
    for (i = 0; i < nums.length; i++) nums[i].textContent = fmt(seeds ? this.seedTarget() : total);
    var finals = root.querySelectorAll('[data-bloom-count-final]');
    for (i = 0; i < finals.length; i++) finals[i].textContent = fmt(total);
    var words = root.querySelectorAll('[data-bloom-count-word]');
    for (i = 0; i < words.length; i++) words[i].textContent = seeds ? L.seedsSown : (total === 1 ? L.countOne : L.countMany);
    var fwords = root.querySelectorAll('[data-bloom-count-final-word]');
    for (i = 0; i < fwords.length; i++) fwords[i].textContent = total === 1 ? L.flowerOne : L.flowerMany;
    for (i = 0; i < this.boxes.length; i++) {
      var b = this.boxes[i], shown = this.shown(b), safeKey = b.key.replace(/"/g, '');
      var els = root.querySelectorAll('[data-bloom-box-count="' + safeKey + '"]');
      for (var j = 0; j < els.length; j++) els[j].textContent = fmt(shown);
      // Card hooks: --cb-box-pct (0 to 100, share of the packed boxes already planted) and the
      // number of boxes still available, for the glass bundle cards.
      var cards = root.querySelectorAll('[data-bloom-box="' + safeKey + '"]');
      var pct = b.packed > 0 ? Math.min(100, Math.round(shown / b.packed * 100)) : 0;
      for (j = 0; j < cards.length; j++) {
        cards[j].style.setProperty('--cb-box-pct', String(pct));
        var left = cards[j].querySelectorAll('[data-bloom-box-left]');
        for (var q = 0; q < left.length; q++) left[q].textContent = fmt(Math.max(0, b.packed - shown));
      }
    }
    this.updateMilestones(total, announce);
    this.lastTotal = total;
  };

  G.updateMilestones = function (total, announce) {
    var ms = this.milestones, root = this.root, L = this.labels;
    var wrap = root.querySelector('[data-bloom-milestones]');
    if (!ms.length) { if (wrap) wrap.hidden = true; return; }
    var max = ms[ms.length - 1].count;
    var track = root.querySelector('[data-bloom-milestone-track]');
    var fillEl = root.querySelector('[data-bloom-milestone-fill]');
    if (track) {
      track.setAttribute('aria-valuemin', '0');
      track.setAttribute('aria-valuemax', String(max));
      track.setAttribute('aria-valuenow', String(Math.min(total, max)));
      track.setAttribute('aria-valuetext', fmt(total) + ' of ' + fmt(max) + ' boxes');
      if (!track.querySelector('.flower-box-sale__milestone-tick')) {
        ms.forEach(function (m) {
          var t = d.createElement('span');
          t.className = 'flower-box-sale__milestone-tick';
          t.setAttribute('aria-hidden', 'true');
          t.setAttribute('data-bloom-tick', m.count);
          t.style.left = (m.count / max * 100) + '%';
          track.appendChild(t);
        });
      }
      var ticks = track.querySelectorAll('[data-bloom-tick]');
      for (var k = 0; k < ticks.length; k++) {
        ticks[k].classList.toggle('flower-box-sale__milestone-tick--reached', total >= +ticks[k].getAttribute('data-bloom-tick'));
      }
    }
    var pct = (Math.min(1, total / max) * 100).toFixed(2);
    if (fillEl) fillEl.style.width = pct + '%';
    // Progress hooks for the styles: --cb-progress on the section (0 to 100), the moving marker,
    // and the "N more boxes unlock the next reward" line.
    root.style.setProperty('--cb-progress', pct);
    var marker = root.querySelector('[data-bloom-milestone-marker]');
    if (marker) {
      marker.style.left = pct + '%';
      var mnum = marker.querySelector('[data-bloom-marker-count]');
      if (mnum) mnum.textContent = fmt(total);
    }
    var nextEl = root.querySelector('[data-bloom-next]');
    if (nextEl) {
      var nxt = null;
      for (var n = 0; n < ms.length; n++) if (total < ms[n].count) { nxt = ms[n]; break; }
      var txt = nxt
        ? fill(L.nextMilestone, { left: fmt(nxt.count - total), boxes: nxt.count - total === 1 ? 'box' : 'boxes', count: fmt(nxt.count) })
        : L.allMilestones;
      if (nextEl.textContent !== txt) nextEl.textContent = txt;
    }
    var list = root.querySelector('[data-bloom-milestone-list]');
    if (list) {
      var sig = ms.map(function (m) { return m.count + ':' + (total >= m.count ? 1 : 0) + ':' + m.reward + ':' + m.detail; }).join('|');
      if (list.__sig !== sig) {
        list.__sig = sig;
        list.innerHTML = '';
        ms.forEach(function (m) {
          var done = total >= m.count;
          var li = d.createElement('li');
          li.className = 'flower-box-sale__milestone' + (done ? ' flower-box-sale__milestone--reached' : '');
          li.setAttribute('data-bloom-milestone', m.count);
          li.setAttribute('data-bloom-milestone-count', m.count);
          var c = d.createElement('span');
          c.className = 'flower-box-sale__milestone-count';
          c.textContent = fill(L.milestoneItem, { count: fmt(m.count) });
          var r = d.createElement('span');
          r.className = 'flower-box-sale__milestone-reward';
          r.textContent = m.reward;
          li.appendChild(c); li.appendChild(r);
          if (m.detail) {
            var dt = d.createElement('span');
            dt.className = 'flower-box-sale__milestone-detail';
            dt.textContent = m.detail;
            li.appendChild(dt);
          }
          if (done) {
            var s = d.createElement('span');
            s.className = 'visually-hidden';
            s.textContent = ' ' + L.milestoneDone;
            li.appendChild(s);
          }
          list.appendChild(li);
        });
      }
    }
    if (announce && this.liveEl && this.lastTotal != null) {
      var prev = this.lastTotal;
      for (var i = 0; i < ms.length; i++) {
        if (prev < ms[i].count && total >= ms[i].count) {
          this.liveEl.textContent = fill(L.milestoneReached, { count: fmt(ms[i].count), reward: ms[i].reward }).trim();
        }
      }
    }
  };

  // "Tulip 31, planted by a Sculpt Studio Box" (number is the flower's place in its box, from 1).
  // Takes a flower; a box still works and gives the plain label. "a" becomes "an" before a vowel
  // sound (Iris 4, planted by an Ink Library).
  // A named flower reads "Salon Luna's tulip, planted by a Sculpt Studio Box" (the name exactly as
  // the service shows it, then 's, species in lower case); the a or an rule still applies to {box}.
  G.labelFor = function (f) {
    var box = f && f.box ? f.box : f, L = this.labels;
    if (!box) return '';
    var name = this.speciesName(box.species);
    if (f && f.box && name && f.name) return fillA(L.plantedByNamed || LABELS.plantedByNamed, { name: f.name, species: name.toLowerCase(), box: box.title });
    if (f && f.box && name) return fillA(L.plantedBy, { species: name, number: fmt(f.i + 1), box: box.title });
    return fillA(L.plantedByPlain || LABELS.plantedByPlain, { box: box.title });
  };

  // The tap bubble shows the label on two lines: what the flower is ("Tulip 31", or "Salon Luna's
  // tulip") and where it came from ("planted by a Sculpt Studio Box"), split at the template's
  // last comma so a comma inside a name stays on the first line. A template without a comma
  // stays on one line. The dot takes the flower's own shade.
  G.labelParts = function (f) {
    var text = this.labelFor(f), cut = text.lastIndexOf(', ');
    var box = f && f.box ? f.box : null;
    var col = box && box.colors && box.colors.length ? box.colors[f.ci % box.colors.length] : null;
    return { title: cut > 0 ? text.slice(0, cut) : text, sub: cut > 0 ? text.slice(cut + 2) : '', accent: col && col.color ? col.color : '' };
  };

  // Where a flower's bloom sits on the stage (css px): { x, y, r }, or null before it is drawn.
  // A shopper's own flower whose bloom the stage edge would cut is drawn a little further in
  // (f.nudge), for this visitor only and only while it is marked, so the whole bloom shows.
  G.headOf = function (f) {
    var b = f && f.last;
    if (!b || !b.hr) return null;
    var nx = 0;
    if (f.mine) {
      if (f.nudge == null && this.W) {
        var full = this.cache.get(this.key(f, GROW_STEPS));
        if (full && full.hr) {
          // at least 28 px in from the edge, so even a short tag's arrow can reach a small bloom
          var fx = f.x + f.fx * full.hx * f.k, m = Math.max(full.hr * f.k * 1.15, 28);
          f.nudge = fx < m ? m - fx : fx > this.W - m ? this.W - m - fx : 0;
        }
      }
      nx = f.nudge || 0;
    }
    return { x: f.x + nx + f.fx * b.hx * f.k, y: f.y + b.hy * f.k, r: b.hr * f.k };
  };
  // The flower whose bloom is under a point (stage css px), or null. The shopper's own flowers are
  // drawn in front of everything, so they are tested first; then the rest, front to back.
  G.hitAt = function (px, py, touch) {
    if (this.phase === 'tease') return null;
    var self = this, hit = null, i;
    var test = function (f) {
      var h = f.mode === 3 || f.gone ? null : self.headOf(f);
      if (!h) return null;
      var rr = h.r * 1.15 + (touch ? 12 : 2), dx = px - h.x, dy = py - h.y;
      return dx * dx + dy * dy < rr * rr ? { f: f, x: h.x, y: h.y - h.r * 0.7 } : null;
    };
    for (i = this.mineList.length - 1; i >= 0 && !hit; i--) hit = test(this.mineList[i]);
    for (i = this.all.length - 1; i >= 0 && !hit; i--) hit = test(this.all[i]);
    return hit;
  };
  G.hover = function (touch) {
    if (!this.pointer || this.phase === 'tease') { this.showLabel(null); return; }
    this.showLabel(this.hitAt(this.pointer.x, this.pointer.y, touch), touch);
  };
  G.showLabel = function (hit, touch) {
    var el = this.labelEl, self = this;
    if (!el) return;
    if (this.labelTimer) { clearTimeout(this.labelTimer); this.labelTimer = 0; }
    // While the numbered label shows on the shopper's own flower, the pinned tag steps aside.
    var hide = function () { el.hidden = true; if (self.pinSuppressed) { self.pinSuppressed = false; self.placePin(); } };
    if (!hit) { hide(); return; }
    var parts = this.labelParts(hit.f), key = parts.title + '\n' + parts.sub + '\n' + parts.accent;
    if (el.__key !== key) {
      el.__key = key;
      while (el.firstChild) el.removeChild(el.firstChild);
      var title = d.createElement('span');
      title.className = 'flower-box-sale__label-title';
      title.textContent = parts.title;
      el.appendChild(title);
      if (parts.sub) {
        var sub = d.createElement('span');
        sub.className = 'flower-box-sale__label-sub';
        if (parts.accent) {
          var dot = d.createElement('span');
          dot.className = 'flower-box-sale__label-dot';
          dot.style.background = parts.accent;
          sub.appendChild(dot);
        }
        sub.appendChild(d.createTextNode(parts.sub));
        el.appendChild(sub);
      }
    }
    el.hidden = false;
    this.aim(el, hit.x, hit.y);
    var sup = !!(this.pinOn && hit.f === this.pinF);
    if (sup !== !!this.pinSuppressed) { this.pinSuppressed = sup; this.placePin(); }
    if (touch) this.labelTimer = setTimeout(hide, 3200);
    else this.labelTimer = setTimeout(function () { if (!self.pointer) hide(); }, 4000);
  };

  // Places a label pill above a point: the pill stays inside the stage (measured once per text),
  // and its arrow (--cb-arrow-dx) still points at the bloom when the pill is held off an edge.
  G.aim = function (el, hx, hy) {
    if (el.__t !== el.textContent) { el.__t = el.textContent; el.__w = el.offsetWidth; el.__h = el.offsetHeight; }
    var half = Math.max(30, (el.__w || 140) / 2), W = this.W;
    var x = W > 2 * half + 16 ? Math.max(half + 8, Math.min(W - half - 8, hx)) : W / 2;
    var ax = Math.max(-(half - 16), Math.min(half - 16, hx - x));
    x = Math.round(x); ax = Math.round(ax);
    // A long (named) label can wrap to two lines; keep its top inside the stage as well.
    var y = Math.round(Math.max(34, (el.__h || 0) + 3, hy)), pos = x + ',' + y + ',' + ax;
    if (el.__pos === pos) return;
    el.__pos = pos;
    el.style.left = x + 'px';
    el.style.top = y + 'px';
    el.style.setProperty('--cb-arrow-dx', ax + 'px');
  };

  /* ---------- "Just planted" line (SPEC 3.7) ---------- */
  // Newest only: an entry replaces the current one unless it is older.
  G.setRecent = function (entry, simulated) {
    if (!entry || typeof entry !== 'object' || !entry.box) return;
    var ts = Date.parse(entry.timestamp);
    if (!isFinite(ts)) ts = Date.now();
    if (this.recent && ts < this.recent.ts) return;
    var changed = !this.recent || this.recent.box !== String(entry.box) || this.recent.ts !== ts;
    this.recent = { box: String(entry.box), species: entry.species, ts: ts };
    if (simulated) this.recentSim = true;
    this.renderRecent(changed);
  };
  G.agoText = function (ms) {
    var L = this.labels, m = Math.floor(Math.max(0, ms) / 60000), h = Math.floor(m / 60);
    if (m < 1) return L.agoNow;
    if (m < 2) return L.agoMinute;
    if (m < 60) return fill(L.agoMinutes, { n: m });
    if (h < 2) return L.agoHour;
    if (h < 24) return fill(L.agoHours, { n: h });
    if (h < 48) return L.agoDay;
    return fill(L.agoDays, { n: Math.floor(h / 24) });
  };
  G.recentText = function () {
    var R = this.recent;
    if (!R) return '';
    var box = this.boxByKey[R.box], c = (this.cfg.boxes || {})[R.box] || {};
    var title = box ? box.title : cleanTitle(c.title || '');
    var name = this.speciesName(box ? box.species : this.alias(R.species || c.species));
    if (!title || !name) return '';
    return fillA(this.labels.recent, { species: name.toLowerCase(), box: title, ago: this.agoText(Date.now() - R.ts) });
  };
  // Shown in Bloom and Full Bloom only, and only with a service URL (or after a simulated sale).
  G.renderRecent = function (fade) {
    var el = this.recentEl, self = this;
    if (!el) return;
    var live = !!String((this.data.service || {}).url || '').trim() || this.recentSim;
    var txt = live && (this.phase === 'bloom' || this.phase === 'full') ? this.recentText() : '';
    if (!txt) { if (!el.hidden) el.hidden = true; return; }
    this.tickRecent();
    var cls = 'flower-box-sale__recent--fade', still = this.reduced;
    if (el.hidden) {
      if (!still) el.classList.add(cls);
      el.textContent = txt;
      el.hidden = false;
      if (!still) { void el.offsetWidth; el.classList.remove(cls); }
      return;
    }
    if (el.textContent === txt) return;
    if (!fade || still) { el.textContent = txt; return; }
    el.classList.add(cls);
    if (this.recentTimer) clearTimeout(this.recentTimer);
    this.recentTimer = this.later(220, function () { self.recentTimer = 0; el.textContent = self.recentText() || txt; el.classList.remove(cls); });
  };

  // The next refresh lands just after the entry's age turns a minute, so "just now" never lasts
  // past 60 s and "2 minutes ago" changes on time.
  G.tickRecent = function () {
    var self = this, R = this.recent;
    if (this.recentTick) { clearTimeout(this.recentTick); this.recentTick = 0; }
    if (!R || this.destroyed) return;
    var age = Math.max(0, Date.now() - R.ts), wait = age < 3600000 ? 60000 - (age % 60000) + 50 : 60000;
    this.recentTick = this.later(wait, function () { self.recentTick = 0; self.renderRecent(false); });
  };

  /* ---------- "See your flower" (SPEC 3.6) ---------- */
  // ?bloom_order=<order id>.<order number> from the order email. Only the two numbers travel;
  // they are sent to the service once and never stored, logged or shown.
  G.startMine = function () {
    var m = /^(\d{1,20})\.(\d{1,20})$/.exec(urlParam('bloom_order'));
    if (!m) return;
    var self = this, token = this.mineToken, url = String((this.data.service || {}).url || '').trim().replace(/\/+$/, '');
    this.later(MINE_SETTLE_MS, function () {
      if (token !== self.mineToken) return;
      if (!url) { self.showMine(null); return; }
      var tries = 0;
      var attempt = function () {
        mineLookup(url, m[1], m[2], function (body) {
          if (self.destroyed || token !== self.mineToken) return;
          if (body && body.ok && body.cancelled) { self.clearMine(); return; }
          if (body && body.ok && Array.isArray(body.boxes) && body.boxes.length) { self.showMine(body); return; }
          if (tries < MINE_RETRY_MS.length) { self.later(MINE_RETRY_MS[tries++], attempt); return; }
          debug('order lookup missed, showing the fallback card');
          self.showMine(null);
        });
      };
      attempt();
    });
  };
  function mineLookup(url, id, n, done) {
    var finished = false, end = function (body) { if (!finished) { finished = true; done(body); } };
    if (!w.fetch) { end(null); return; }
    try {
      fetch(url + '/mine?order=' + encodeURIComponent(id) + '&n=' + encodeURIComponent(n), { cache: 'no-store', mode: 'cors', credentials: 'omit' })
        .then(function (r) { return r.status === 200 ? r.json() : null; })
        .then(function (body) { end(body && typeof body === 'object' ? body : null); }, function () { end(null); });
    } catch (e) { end(null); }
  }

  // answer: a /mine body (steps 3 to 6), or null for the bloom_flower fallback (step 7).
  G.showMine = function (answer) {
    this.clearMine();
    if (answer && answer.cancelled) return;
    if (answer && answer.ok !== false && Array.isArray(answer.boxes) && answer.boxes.length && this.applyMine(answer)) return;
    this.mineFallback();
  };
  G.hideMine = function () { this.mineToken++; this.clearMine(); };

  G.applyMine = function (answer) {
    var self = this, entries = [], flowers = [], groups = [], count = 0, raised = false;
    answer.boxes.forEach(function (e) {
      if (!e || typeof e !== 'object' || !e.box) return;
      var key = String(e.box), box = self.boxByKey[key], c = (self.cfg.boxes || {})[key] || {};
      var title = box ? box.title : cleanTitle(c.title || '');
      var sp = box ? box.species : self.alias(e.species || c.species);
      var first = Math.max(1, Math.round(num(e.first, 1))), qty = Math.max(1, Math.round(num(e.quantity, 1))), nfirst = first;
      if (!title || !self.speciesName(sp)) return;
      var fl = [];
      if (box) {
        // The answer proves these flowers exist. If the page has not caught up with the live
        // count yet (a slow /counts), raise the box to them first, so the flowers that glow are
        // the ones the card names.
        // A slow count lags by a few sales at most, so the raise is capped (20 above the count,
        // never past the packed count) and a malformed answer cannot flood the meadow.
        var need = first + qty - 1, cap = Math.min(box.packed > 0 ? box.packed : 1e9, self.shown(box) + 20);
        need = Math.min(need, Math.max(cap, self.shown(box)));
        if (need > self.shown(box)) { box.proved = Math.max(box.proved || 0, need); self.syncBox(box, false, false); raised = true; }
        // Flower number k is the k-th flower of the box. A number above the displayed count is
        // clamped to the newest flower (kept as a safety net).
        var shown = Math.min(self.shown(box), box.flowers.length);
        for (var k = 0; k < qty && shown > 0; k++) {
          var f = box.flowers[Math.min(first + k, shown) - 1];
          if (f && fl.indexOf(f) < 0) fl.push(f);
        }
        if (fl.length) first = fl[0].i + 1; // the card prints the numbers of the flowers that glow
      }
      // nfirst: the service's own number, which names (snapshot, named event, save) refer to.
      entries.push({ key: key, box: box, title: title, species: sp, first: first, nfirst: nfirst, qty: qty, flowers: fl });
      flowers = flowers.concat(fl);
      count += qty;
      var g = null;
      for (var j = 0; j < groups.length; j++) if (groups[j].species === sp) g = groups[j];
      if (g) g.n += qty; else groups.push({ species: sp, n: qty });
    });
    if (raised) this.afterChange();
    if (!entries.length || !flowers.length) return false;
    // name: the order's flower name from the service ("" for none). An answer without the field
    // (a service from before names, or the preview's simulated answer) leaves the names alone.
    var hasName = typeof answer.name === 'string';
    this.mine = {
      mode: 'order', entries: entries, groups: groups, count: count, date: dateText(answer.plantedAt), flowers: flowers,
      name: hasName ? cleanName(answer.name) : null, nameOk: hasName || !this.serviceUrl(), nameFilled: false
    };
    if (hasName) this.setOrderName(this.mine.name);
    this.presentMine(true);
    return true;
  };

  // Shows the card, marks the flowers and scrolls to them. In Seeds the meadow is bare soil, so
  // the card waits (hidden, no halo, no tag) and appears when the phase turns to Bloom.
  G.presentMine = function (scroll) {
    var M = this.mine;
    if (!M) return;
    if (this.phase === 'tease') {
      this.minePending = true;
      if (this.mineEl) this.mineEl.hidden = true;
      this.pinOn = false;
      this.placePin();
      this.updateFocus();
      return;
    }
    this.minePending = false;
    this.fillMineCard();
    this.markMine(M.flowers, M.mode === 'order');
    if (scroll) this.revealMine();
    else if (M.mode === 'order') this.prepareMineImage();
  };

  // No lookup: halo the newest flower of each box that plants a species named in bloom_flower.
  G.mineFallback = function () {
    var self = this, keys = [], entries = [], flowers = [], groups = [];
    urlParam('bloom_flower').split(',').forEach(function (s) {
      var k = self.alias(s);
      if (k && self.speciesName(k) && keys.indexOf(k) < 0) keys.push(k);
    });
    keys.forEach(function (k) {
      var hit = false;
      self.boxes.forEach(function (b) {
        if (b.species !== k) return;
        var n = Math.min(self.shown(b), b.flowers.length);
        if (n < 1) return;
        entries.push({ key: b.key, box: b, title: b.title, species: k, count: n });
        flowers.push(b.flowers[n - 1]);
        hit = true;
      });
      if (hit) groups.push({ species: k, n: 1 });
    });
    if (!entries.length) return;
    this.mine = { mode: 'fallback', entries: entries, groups: groups, count: groups.length, flowers: flowers };
    this.presentMine(true);
  };

  G.fillMineCard = function () {
    var M = this.mine, el = this.mineEl, L = this.labels;
    if (!M || !el) return;
    var phrase = this.flowersPhrase(M.groups), one = M.count === 1, pressed = this.phase === 'pressed';
    var title = M.mode === 'order'
      ? (one ? (pressed ? L.minePressedOne : L.mineTitleOne) : (pressed ? L.minePressedMany : L.mineTitleMany))
      : (one ? L.mineFallbackTitleOne : L.mineFallbackTitleMany);
    var lines = M.entries.map(function (x) {
      if (M.mode !== 'order') return fill(L.mineFallbackLine, { count: fmt(x.count), box: x.title });
      var last = x.first + x.qty - 1, t = x.qty === 1 ? L.mineLine : x.qty === 2 ? L.mineLineTwo : L.mineLineMany;
      return fill(t, { number: fmt(x.first), first: fmt(x.first), last: fmt(last), box: x.title, date: M.date });
    });
    var tEl = el.querySelector('[data-bloom-mine-title]'), xEl = el.querySelector('[data-bloom-mine-text]');
    if (tEl) tEl.textContent = fill(title, { flowers: phrase });
    if (xEl) {
      while (xEl.firstChild) xEl.removeChild(xEl.firstChild);
      lines.forEach(function (line, i) {
        if (i) xEl.appendChild(d.createElement('br'));
        xEl.appendChild(d.createTextNode(line));
      });
    }
    var save = el.querySelector('[data-bloom-mine-save]');
    if (save) save.hidden = M.mode !== 'order'; // the fallback has no picture of a specific flower
    // The name form: only after a real lookup (never in the fallback), filled once with the
    // order's name so a later refill (a phase change) never overwrites what the shopper types.
    var form = this.nameForm, input = this.nameInput, named = M.mode === 'order' && M.nameOk;
    if (form) form.hidden = !named;
    if (named && input && !M.nameFilled) { input.value = M.name != null ? M.name : this.orderName(); M.nameFilled = true; }
    if (this.pinEl) this.pinEl.textContent = fill(L.minePinned, { flowers: phrase });
    el.hidden = false;
  };

  // Marks the shopper's flowers. replay: sprout them again (staggered, glowing) and pin the tag.
  G.markMine = function (list, replay) {
    var t = now(), animate = replay && !this.reduced && this.phase !== 'tease', front = null, best = -1;
    for (var k = 0; k < list.length; k++) {
      var f = list[k];
      f.mine = true;
      f.nudge = null;
      // The tag goes on the most visible of these flowers: a bloom drawn fully inside the stage
      // first (headOf includes the edge nudge), then the nearest (largest v).
      var h = this.headOf(f), score = (h && h.x >= h.r && h.x <= this.W - h.r ? 2 : 0) + f.v;
      if (score > best) { best = score; front = f; }
      if (animate) {
        f.mode = 1; f.t0 = t + MINE_DELAY_MS + k * MINE_STAGGER_MS; f.ready = true; f.glowEnd = 0;
        f.haloT0 = f.t0 + SPROUT_MS;
      } else f.haloT0 = t;
    }
    this.mineList = list.slice();
    this.focusOn = true;
    this.unbake(false); // halos are drawn live, so these flowers leave the still layer
    if (replay && front) {
      var el = this.pinEl;
      if (!el && this.stage) {
        el = this.pinEl = d.createElement('div');
        el.className = 'flower-box-sale__label flower-box-sale__label--pinned';
        el.setAttribute('aria-hidden', 'true'); // the card below says the same for screen readers
        el.hidden = true;
        this.stage.appendChild(el);
      }
      if (el) el.textContent = fill(this.labels.minePinned, { flowers: this.flowersPhrase(this.mine.groups) });
      this.pinF = front; this.pinAt = front.haloT0; this.pinOn = true; this.pinSuppressed = false;
    }
    this.placePin();
    this.kick();
    var self = this;
    this.later(80, function () { self.updateFocus(); });
  };

  // In the hero layout the header panel sits over the meadow. While the shopper looks at their
  // flower, a header that covers it fades away (flower-box-sale--mine-focus); it comes back on
  // the next tap or click elsewhere, or when the card closes.
  G.updateFocus = function () {
    var hdr = this.root.querySelector('.flower-box-sale__header'), on = false;
    if (hdr && this.focusOn && this.mineList.length && this.canvas && this.phase !== 'tease') {
      var hr = hdr.getBoundingClientRect(), cr = this.canvas.getBoundingClientRect();
      for (var i = 0; i < this.mineList.length && !on; i++) {
        var f = this.mineList[i], h = this.headOf(f);
        var x = cr.left + (h ? h.x : f.x), y = cr.top + (h ? h.y : f.y - f.reach * 0.85);
        on = x > hr.left - 12 && x < hr.right + 12 && y > hr.top - 40 && y < hr.bottom + 12;
      }
    }
    this.root.classList.toggle('flower-box-sale--mine-focus', on);
  };

  // Halo strength for a mine flower: fades in after the sprout, then breathes slowly.
  G.haloAt = function (f, t) {
    if (this.reduced) return 0.7;
    var q = t - (f.haloT0 || 0);
    if (q < 0) return 0;
    return c01(q / 1400) * (0.66 + 0.26 * Math.sin(q / 1100));
  };

  // Keeps the pinned tag above the chosen flower, its arrow pointing at the bloom.
  G.placePin = function () {
    var el = this.pinEl, f = this.pinF;
    if (!el) return;
    var h = f && !f.gone && f.mode !== 3 ? this.headOf(f) : null;
    var show = !!(this.pinOn && h && !this.pinSuppressed && this.phase !== 'tease' && (this.reduced || now() >= this.pinAt));
    if (!show) { if (!el.hidden) el.hidden = true; return; }
    if (el.hidden) el.hidden = false;
    this.aim(el, h.x, h.y - h.r * 0.8);
  };

  // Brings the meadow and the card into view: both together, centred, when they fit the screen;
  // otherwise the card's buttons with the shopper's flower above them, if those fit; otherwise
  // the meadow centred.
  G.revealMine = function () {
    var self = this, st = this.stage, smooth = this.reduced ? 'auto' : 'smooth', y = null;
    try {
      if (st && st.getBoundingClientRect) {
        var vh = w.innerHeight || d.documentElement.clientHeight || 0, sr = st.getBoundingClientRect();
        var card = this.mineEl && !this.mineEl.hidden ? this.mineEl.getBoundingClientRect() : null;
        var top = sr.top, bottom = sr.bottom;
        if (card && card.height) { top = Math.min(top, card.top); bottom = Math.max(bottom, card.bottom); }
        if (vh && bottom - top <= vh) y = top - (vh - (bottom - top)) / 2;
        else if (vh && card && card.height) {
          var f = this.pinF || this.mineList[0], h = f && this.headOf(f);
          var fy = h ? sr.top + h.y - h.r - 48 : sr.top + sr.height * 0.4;
          if (card.bottom + 12 - fy <= vh) y = card.bottom + 12 - vh;
        }
        if (y == null) st.scrollIntoView({ block: 'center', behavior: smooth });
        else {
          var to = Math.max(0, (w.pageYOffset || d.documentElement.scrollTop || 0) + y);
          try { w.scrollTo({ top: to, behavior: smooth }); } catch (e) { w.scrollTo(0, to); }
        }
      }
    } catch (e) { /* ignore */ }
    if (this.mine && this.mine.mode === 'order') this.later(60, function () { self.prepareMineImage(); });
  };

  G.clearMine = function () {
    for (var i = 0; i < this.mineList.length; i++) { this.mineList[i].mine = false; this.mineList[i].haloT0 = 0; this.mineList[i].nudge = null; }
    this.mineList = [];
    this.mine = null;
    this.minePending = false;
    this.mineBlob = null; this.mineFile = null; this.mineBuilding = false;
    this.pinOn = false; this.pinF = null; this.pinSuppressed = false;
    this.placePin();
    this.focusOn = false;
    if (this.root) this.root.classList.remove('flower-box-sale--mine-focus');
    var el = this.mineEl;
    if (el) {
      el.hidden = true;
      var tEl = el.querySelector('[data-bloom-mine-title]'), xEl = el.querySelector('[data-bloom-mine-text]'), note = el.querySelector('[data-bloom-mine-note]');
      if (tEl) tEl.textContent = '';
      if (xEl) while (xEl.firstChild) xEl.removeChild(xEl.firstChild);
      if (note) { note.hidden = true; note.textContent = ''; }
    }
    if (this.nameForm) this.nameForm.hidden = true;
    if (this.nameInput) this.nameInput.value = '';
    this.layerDirty = true; this.forceBake = true;
    this.kick();
  };

  // stay: keep the note until the next action (the name notes); otherwise it fades after 3 s (Link copied).
  G.mineNote = function (text, stay) {
    var note = this.mineEl && this.mineEl.querySelector('[data-bloom-mine-note]');
    if (!note) return;
    if (this.noteTimer) { clearTimeout(this.noteTimer); this.noteTimer = 0; }
    note.textContent = text || '';
    note.hidden = !text;
    if (text && !stay) this.noteTimer = this.later(3000, function () { note.hidden = true; });
  };

  /* ---------- naming the shopper's flower (SPEC 3.8) ---------- */
  G.serviceUrl = function () { return String((this.data.service || {}).url || '').trim().replace(/\/+$/, ''); };
  // The order's boxes as the service numbers them, for names: [{ box, first, quantity }].
  G.mineNameBoxes = function () {
    var M = this.mine;
    if (!M || M.mode !== 'order') return [];
    return M.entries.map(function (x) { return { box: x.key, first: x.nfirst, quantity: x.qty }; });
  };
  G.setOrderName = function (name) {
    var boxes = this.mineNameBoxes();
    if (boxes.length) this.upsertNames(boxes, name);
  };
  // The name the order's flowers carry right now ("" for none).
  G.orderName = function () {
    var list = (this.mine && this.mine.flowers) || [];
    for (var i = 0; i < list.length; i++) if (list[i].name) return list[i].name;
    return '';
  };
  G.nameNote = function (key, name) {
    var L = this.labels, t = L[key] || LABELS[key];
    t = fill(t, { name: name || '' });
    // "Saved. Your flower is named Salon Luna Inc." keeps one closing period, not two.
    if (name && /\.$/.test(name)) t = t.replace(/\.\.$/, '.');
    this.mineNote(t, true);
  };
  // Save name: POST {service}/mine/name for the order in the link, then show the answer in the
  // card note and name the order's flowers at once (the named event confirms it for everyone).
  G.saveName = function () {
    var self = this, M = this.mine, input = this.nameInput, btn = this.nameButton, form = this.nameForm;
    if (!M || M.mode !== 'order' || !input || this.nameBusy) return;
    var name = cleanName(input.value);
    this.mineNote('', true); // the previous note stays only until this next action
    if (name.length > NAME_MAX) { this.nameNote('nameTooLong'); return; }
    var url = this.serviceUrl();
    if (!url) {
      // Preview only: without a service URL the card can only appear through
      // FlowerBoxSale.showMine(...) (the preview's "Simulate my flower"), so the name is applied
      // on this page alone and nothing is sent anywhere. A live page always has a service URL.
      this.setOrderName(name);
      input.value = name;
      this.nameNote(name ? 'nameSaved' : 'nameRemoved', name);
      return;
    }
    var m = /^(\d{1,20})\.(\d{1,20})$/.exec(urlParam('bloom_order'));
    if (!m || !w.fetch) { this.nameNote('nameFailed'); return; }
    var token = this.mineToken, focused = d.activeElement === btn, finished = false, ctl = null;
    this.nameBusy = true;
    if (btn) btn.disabled = true;
    if (form) form.setAttribute('aria-busy', 'true');
    var end = function (status, body) {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      self.nameBusy = false;
      if (btn) {
        btn.disabled = false;
        if (focused && (!d.activeElement || d.activeElement === d.body)) { try { btn.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
      }
      if (form) form.removeAttribute('aria-busy');
      if (self.destroyed || token !== self.mineToken || M !== self.mine) return;
      var err = body && typeof body === 'object' ? String(body.error || '') : '';
      if (status === 200 && body && body.ok) {
        var saved = cleanName(body.name);
        self.setOrderName(saved);
        self.nameHold = { until: now() + NAME_HOLD_MS, boxes: self.mineNameBoxes(), name: saved };
        M.name = saved;
        input.value = saved;
        self.nameNote(saved ? 'nameSaved' : 'nameRemoved', saved);
      } else if (status === 400 && err === 'name too long') self.nameNote('nameTooLong');
      else if (status === 400) self.nameNote('nameRejected');
      else self.nameNote('nameFailed'); // 404, 429, a network error or no answer in time
    };
    var timer = setTimeout(function () { if (ctl) { try { ctl.abort(); } catch (e) { /* ignore */ } } end(0, null); }, NAME_SAVE_MS);
    try {
      ctl = typeof AbortController === 'function' ? new AbortController() : null;
      fetch(url + '/mine/name', {
        method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: m[1], n: m[2], name: name }),
        signal: ctl ? ctl.signal : undefined
      }).then(function (r) {
        return r.json().then(function (b) { end(r.status, b); }, function () { end(r.status, null); });
      }, function () { end(0, null); });
    } catch (e) { end(0, null); }
  };
  // A new or removed name on the flower the share picture shows: paint the picture again.
  G.mineNameCheck = function () {
    var s = this.mineSubject();
    if (!s || this.minePending || !(this.mineBlob || this.mineBuilding)) return;
    if ((s.f.name || '') !== (this.mineImgName || '')) this.refreshMineImage(false);
  };
  // Drops the share picture so the next one is painted fresh (new name or new phase). force: paint
  // it now even if none was made yet.
  G.refreshMineImage = function (force) {
    var had = !!(this.mineBlob || this.mineBuilding);
    this.mineGen++;
    this.mineBlob = null; this.mineFile = null;
    if ((force || had) && this.mine && this.mine.mode === 'order' && !this.minePending) this.prepareMineImage();
  };

  // The species and flower the share picture shows: the first box of the order.
  G.mineSubject = function () {
    var M = this.mine;
    if (!M || M.mode !== 'order') return null;
    for (var i = 0; i < M.entries.length; i++) if (M.entries[i].flowers.length) return { entry: M.entries[i], f: M.entries[i].flowers[0] };
    return null;
  };
  // "My tulip in the Flower Box Sale", or "Salon Luna's tulip in the Flower Box Sale" when the
  // flower on the picture has a name.
  G.shareCaption = function () {
    var s = this.mineSubject(), sp = s ? s.entry.species : (this.mine && this.mine.groups[0] ? this.mine.groups[0].species : '');
    var word = this.speciesWord(sp, 1) || this.labels.flowerOne, L = this.labels;
    if (s && s.f.name) return fill(L.shareCaptionNamed || LABELS.shareCaptionNamed, { name: s.f.name, species: word });
    return fill(L.shareCaption, { species: word });
  };
  G.mineFileName = function () {
    var s = this.mineSubject();
    return 'my-' + String(s ? s.entry.species : 'flower').replace(/[^a-z0-9-]/gi, '') + '-flower-box-sale.png';
  };

  // Builds the 1080 by 1350 share picture once the card shows, so Share can hand it straight to
  // the share sheet inside the tap. Never throws; done(null) when anything is missing.
  G.prepareMineImage = function (done) {
    var self = this, cbs = this.mineWaiters || (this.mineWaiters = []);
    if (done) cbs.push(done);
    if (this.mineBlob) { this.flushMine(); return; }
    if (this.mineBuilding) return;
    var s = this.mineSubject(), BF = w.BloomFlowers;
    if (!s || !BF) { this.flushMine(); return; }
    this.mineBuilding = true;
    this.mineImgName = s.f.name || ''; // the name this picture carries (mineNameCheck compares it)
    var token = this.mineToken, M = this.mine, gen = this.mineGen;
    var fam = function (el, f) { try { return (el && w.getComputedStyle(el).fontFamily) || f; } catch (e) { return f; } };
    var head = fam(this.root.querySelector('.flower-box-sale__heading'), 'Georgia, serif') + ', Georgia, serif';
    var body = fam(this.root, 'system-ui, sans-serif') + ', system-ui, sans-serif';
    var go = function () {
      if (self.destroyed || token !== self.mineToken || M !== self.mine) return;
      var blob = null;
      var finish = function (b) {
        self.mineBuilding = false;
        if (token !== self.mineToken || M !== self.mine) return;
        if (gen !== self.mineGen) { self.prepareMineImage(); return; } // the name changed while painting
        self.mineBlob = b || null;
        try { self.mineFile = b && typeof File === 'function' ? new File([b], self.mineFileName(), { type: 'image/png' }) : null; } catch (e) { self.mineFile = null; }
        self.flushMine();
      };
      try {
        var cv = self.paintShare(s, head, body);
        if (cv.toBlob) { cv.toBlob(function (b) { finish(b); }, 'image/png'); return; }
        var url = cv.toDataURL('image/png'), bin = atob(url.split(',')[1]), arr = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        blob = new Blob([arr], { type: 'image/png' });
      } catch (e) { debug('share picture unavailable', e && e.message); }
      finish(blob);
    };
    // Wait (briefly) for the theme fonts so the caption is set in them.
    var started = false, start = function () { if (!started) { started = true; go(); } };
    try {
      if (d.fonts && d.fonts.load) {
        Promise.all([d.fonts.load('500 60px ' + head), d.fonts.load('400 28px ' + body)]).then(start, start);
        setTimeout(start, 1500);
        return;
      }
    } catch (e) { /* fall through */ }
    start();
  };
  G.flushMine = function () {
    var cbs = this.mineWaiters || [], b = this.mineBlob;
    this.mineWaiters = [];
    cbs.forEach(function (cb) { try { cb(b); } catch (e) { /* ignore */ } });
  };

  // The share picture: phase sky, soft light, a band of meadow, the shopper's flower at about
  // 700 px and a paper caption. No logo and no external image, so the canvas stays exportable.
  G.paintShare = function (s, headFont, bodyFont) {
    var W = 1080, H = 1350, p = this.pal || MEADOW, f = s.f, box = f.box, L = this.labels, i;
    var cv = d.createElement('canvas');
    cv.width = W; cv.height = H;
    var g = cv.getContext('2d');
    var rnd = (function (x) { return function () { x = (x * 16807) % 2147483647; return x / 2147483647; }; })((f.h % 2147483646) + 1);
    var sky = g.createLinearGradient(0, 0, 0, 1000);
    sky.addColorStop(0, p.skyTop); sky.addColorStop(0.55, p.skyMid || p.skyBottom); sky.addColorStop(1, p.skyBottom);
    g.fillStyle = sky; g.fillRect(0, 0, W, H);
    var lg = g.createRadialGradient(W * 0.72, 760, 0, W * 0.72, 760, 900);
    lg.addColorStop(0, css(rgbOf(p.light), 0.6)); lg.addColorStop(0.35, css(rgbOf(p.light), 0.2)); lg.addColorStop(1, css(rgbOf(p.light), 0));
    g.fillStyle = lg; g.fillRect(0, 0, W, H);
    // far hills, then the meadow
    g.fillStyle = css(mixHex(p.hillsFar, p.skyBottom, 0.35));
    g.beginPath(); g.moveTo(0, H);
    for (i = 0; i <= W; i += 12) g.lineTo(i, 950 - 46 * Math.sin(i / W * 4.1 + 0.6) - 18 * Math.sin(i / W * 9.3 + 1.2));
    g.lineTo(W, H); g.closePath(); g.fill();
    var gr = g.createLinearGradient(0, 990, 0, H);
    gr.addColorStop(0, css(mixHex(p.grassBack, p.skyBottom, 0.2))); gr.addColorStop(0.35, p.grassMid); gr.addColorStop(1, p.grassFront);
    g.fillStyle = gr;
    g.beginPath(); g.moveTo(0, H);
    for (i = 0; i <= W; i += 12) g.lineTo(i, 1010 + 14 * Math.sin(i / W * 6.2 + 2));
    g.lineTo(W, H); g.closePath(); g.fill();
    var greens = [rgbOf(p.grassBack), rgbOf(p.grassMid), rgbOf(p.grassFront)];
    g.lineCap = 'round';
    for (i = 0; i < 700; i++) {
      var gx = rnd() * W, gy = 1010 + Math.pow(rnd(), 0.8) * 340, len = 6 + (gy - 1000) * 0.08 * (0.5 + rnd());
      g.strokeStyle = css(greens[(rnd() * 3) | 0], 0.7);
      g.lineWidth = 1 + (gy - 1000) / 140;
      g.beginPath(); g.moveTo(gx, gy); g.quadraticCurveTo(gx + (rnd() - 0.5) * len * 0.4, gy - len * 0.6, gx + (rnd() - 0.5) * len * 0.8, gy - len); g.stroke();
    }
    // the flower: same shade, seed and species as in the meadow, full grown, standing still
    var col = box.colors[f.ci] || box.colors[0];
    g.save();
    g.translate(W / 2, 1090);
    w.BloomFlowers.drawFlower(g, {
      species: box.species, color: col.color, accent: col.accent, alpha: col.alpha, glitter: box.glitter,
      growth: 1, sway: 0, seed: hash(this.seed, box.hk, f.ci, f.bucket, f.vr || 0), size: 700,
      time: box.glitter ? 2600 : null, image: null, glow: 0
    });
    g.restore();
    for (i = 0; i < 90; i++) { // a few blades in front of the stem
      var bx = W / 2 + (rnd() - 0.5) * 520, bl = 26 + rnd() * 46;
      g.strokeStyle = css(greens[1 + ((rnd() * 2) | 0)], 0.85);
      g.lineWidth = 2 + rnd() * 2;
      g.beginPath(); g.moveTo(bx, 1150); g.quadraticCurveTo(bx + (rnd() - 0.5) * 10, 1150 - bl * 0.6, bx + (rnd() - 0.5) * 22, 1150 - bl); g.stroke();
    }
    // caption on paper. A long caption (a named flower) breaks into two lines at the space that
    // balances them best, and the paper grows upward to hold the second line.
    var px = 90, py = 1150, pw = W - 180, ph = 150, rr = 34, maxW = pw - 80;
    var size = 60, cap = this.shareCaption(), lines = [cap], extra = 0;
    var setFont = function (n) { size = n; g.font = '500 ' + n + 'px ' + headFont; };
    var widest = function (list) { var m = 0; for (var q = 0; q < list.length; q++) m = Math.max(m, g.measureText(list[q]).width); return m; };
    setFont(60);
    while (size > 34 && widest(lines) > maxW) setFont(size - 2);
    var words = cap.split(' ');
    if (widest(lines) > maxW && words.length > 1) {
      var best = null;
      for (i = 1; i < words.length; i++) {
        var two = [words.slice(0, i).join(' '), words.slice(i).join(' ')], wd = widest(two);
        if (!best || wd < best.w) best = { l: two, w: wd };
      }
      lines = best.l;
      setFont(56);
      while (size > 30 && widest(lines) > maxW) setFont(size - 2);
      extra = Math.round(size * 1.14);
      py -= extra; ph += extra;
    }
    g.save();
    g.shadowColor = 'rgba(44,44,42,0.22)'; g.shadowBlur = 30; g.shadowOffsetY = 10;
    g.fillStyle = 'rgba(251,250,247,0.94)';
    g.beginPath();
    g.moveTo(px + rr, py); g.lineTo(px + pw - rr, py); g.quadraticCurveTo(px + pw, py, px + pw, py + rr);
    g.lineTo(px + pw, py + ph - rr); g.quadraticCurveTo(px + pw, py + ph, px + pw - rr, py + ph);
    g.lineTo(px + rr, py + ph); g.quadraticCurveTo(px, py + ph, px, py + ph - rr);
    g.lineTo(px, py + rr); g.quadraticCurveTo(px, py, px + rr, py); g.closePath(); g.fill();
    g.restore();
    g.textAlign = 'center';
    g.textBaseline = 'alphabetic';
    g.fillStyle = '#2C2C2A';
    g.font = '500 ' + size + 'px ' + headFont;
    for (i = 0; i < lines.length; i++) g.fillText(lines[i], W / 2, py + 76 + i * extra);
    g.fillStyle = '#5F5E5A';
    g.font = '400 28px ' + bodyFont;
    g.fillText(L.shareSite, W / 2, py + 120 + extra);
    return cv;
  };

  // Share: the picture through the share sheet when the browser can share files, else the page
  // link through the share sheet, else the link copied with a short "Link copied" note.
  G.shareMine = function () {
    var self = this, M = this.mine, link = pageLink(), nav = w.navigator || {};
    if (!M) return;
    var caption = this.shareCaption(), file = M.mode === 'order' ? this.mineFile : null;
    var fallback = function (err) { if (!err || err.name !== 'AbortError') self.copyLink(link); };
    try {
      if (file && nav.share && nav.canShare && nav.canShare({ files: [file] })) {
        nav.share({ files: [file], title: caption, text: caption }).catch(fallback);
        return;
      }
      if (nav.share) { nav.share({ url: link, title: caption }).catch(fallback); return; }
    } catch (e) { debug('share sheet unavailable'); }
    this.copyLink(link);
  };
  G.copyLink = function (link) {
    var self = this, done = function () { self.mineNote(self.labels.mineCopied); };
    var legacy = function () {
      try {
        var ta = d.createElement('textarea');
        ta.value = link;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed'; ta.style.top = '-1000px'; ta.style.opacity = '0';
        d.body.appendChild(ta);
        ta.select();
        var ok = d.execCommand && d.execCommand('copy');
        d.body.removeChild(ta);
        if (ok) done();
      } catch (e) { /* nothing to show */ }
    };
    try {
      if (w.navigator.clipboard && w.navigator.clipboard.writeText) { w.navigator.clipboard.writeText(link).then(done, legacy); return; }
    } catch (e) { /* fall through */ }
    legacy();
  };
  // Save a picture: downloads the same PNG as my-tulip-flower-box-sale.png.
  G.saveMine = function () {
    var self = this;
    if (!this.mine || this.mine.mode !== 'order') return;
    this.prepareMineImage(function (blob) {
      if (!blob || !w.URL || !URL.createObjectURL) return;
      var url = URL.createObjectURL(blob), a = d.createElement('a');
      a.href = url;
      a.download = self.mineFileName();
      a.rel = 'noopener';
      a.style.display = 'none';
      d.body.appendChild(a);
      a.click();
      d.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
    });
  };

  /* ---------- layout ---------- */
  G.layout = function () {
    if (!this.stage || !this.canvas) return;
    this.bleed();
    var r = this.stage.getBoundingClientRect();
    var W = Math.max(1, Math.round(r.width)), H = Math.max(1, Math.round(r.height));
    var dpr = Math.min([2, 1.5, 1][this.quality] || 1, w.devicePixelRatio || 1);
    if (W === this.W && H === this.H && dpr === this.dpr) return;
    var sizeChanged = H !== this.H, scaleChanged = dpr !== this.dpr;
    // Resizing a canvas clears it. Keep the last picture, stretched, until the next frame paints,
    // so a resize or a step down in render quality never flashes an empty stage.
    var snap = null;
    if (this.bg && this.ctx && this.canvas.width > 1 && this.canvas.height > 1) {
      try {
        snap = d.createElement('canvas');
        snap.width = this.canvas.width; snap.height = this.canvas.height;
        snap.getContext('2d').drawImage(this.canvas, 0, 0);
      } catch (e) { snap = null; }
    }
    this.W = W; this.H = H; this.dpr = dpr;
    this.canvas.width = Math.round(W * dpr);
    this.canvas.height = Math.round(H * dpr);
    if (snap) {
      try { this.ctx.setTransform(1, 0, 0, 1, 0, 0); this.ctx.drawImage(snap, 0, 0, this.canvas.width, this.canvas.height); } catch (e) { /* ignore */ }
    }
    var phone = W < 600;
    this.horizon = H * (phone ? 0.3 : 0.36);
    this.groundTop = this.horizon + H * 0.02;
    this.groundBot = H * 0.985;
    // Phones draw the flowers smaller against the stage width, so more of the meadow shows
    // (zoomed out) and the tap bubbles and the header card have room.
    this.unit = Math.min(H * 0.29, W * (phone ? 0.23 : 0.17));
    this.road = this.roadGeometry();
    for (var i = 0; i < this.all.length; i++) this.place(this.all[i]);
    for (i = 0; i < this.mineList.length; i++) this.mineList[i].nudge = null;
    if (sizeChanged) { this.cache.clear(); this.unbake(true); }
    // Render scale only (adaptive quality): keep showing the previous bitmaps, scaled,
    // until the new ones are built, so the meadow never blinks.
    else if (scaleChanged) { this.cache.clear(); this.unbake(false); }
    this.paintScenery();
    this.kick();
    if (this.focusOn) { var self = this; this.later(60, function () { self.updateFocus(); }); }
  };
  // The stage is full width through CSS. If a theme wrapper still pads the section,
  // stretch the stage to the viewport (clientWidth excludes any scrollbar, so no sideways scroll).
  G.bleed = function () {
    var st = this.stage, root = this.root;
    if (!st || !root.getBoundingClientRect) return;
    var vw = d.documentElement.clientWidth, rr = root.getBoundingClientRect();
    if (!vw || rr.width >= vw - 1) {
      if (st.style.width) { st.style.width = ''; st.style.marginLeft = ''; }
      return;
    }
    st.style.width = vw + 'px';
    st.style.marginLeft = -Math.round(rr.left) + 'px';
  };
  G.sizeAt = function (v) { return this.unit * (0.3 + 0.7 * Math.pow(v, 1.15)); };
  G.place = function (f) {
    if (!this.W) return;
    f.y = lerp(this.groundTop, this.groundBot, f.v);
    f.reach = this.sizeAt(f.v) * f.jit * f.box.sp.height * f.box.scale;
    f.amp = 0.03 + 0.03 * f.v;
    // The widest bloom (a poppy, or a painted cutout) reaches 0.29 of the flower's height either
    // side of its stem, and the wind sways it out by up to f.amp of the height more.
    f.x = this.roadsideX(f.u, f.y, f.v, f.reach * (0.29 + f.amp) + 2);
    f.k = this.sizeAt(f.v) * f.jit / this.sizeAt((f.bucket + 0.5) / DEPTHS);
    f.fx = f.box.img ? 1 : f.flip; // painted cutouts are never mirrored
  };

  /* ---------- scenery (cached background and foreground) ---------- */
  // Painted once per layout or phase change. this.bg holds everything behind the flowers and
  // this.fg the grass in front of them. Back to front: the sky (stars, the galaxy and a crescent
  // moon at twilight, the sun and a rainbow by day, the low golden sun at golden hour), clouds,
  // far mountains and a cliff, the glowing emerald city at the end of the road, rolling hills,
  // the ground (grass with sunlit patches, or sown soil during Seeds) and the winding yellow brick
  // road. Nothing else rests on the ground. The sun's rays (paintLight) are drawn over the
  // flowers each frame. Everything is deterministic from the garden seed.

  // Where the road runs. t is depth: 0 at the horizon (the city gate), 1 at the bottom edge.
  // It leaves the city, bulges gently to one side on the way down and arrives wide at the front.
  G.roadGeometry = function () {
    var W = this.W, phone = W < 600;
    var vx = W * (phone ? 0.5 : 0.61), bx = W * (phone ? 0.5 : 0.55);
    var bend = W * (phone ? 0.05 : 0.05), wTop = W * 0.004, wBot = W * (phone ? 0.3 : 0.2);
    return {
      vx: vx, hz: this.horizon,
      cx: function (t) { return vx + (bx - vx) * Math.pow(t, 1.2) + bend * Math.sin(t * Math.PI); },
      hw: function (t) { return wTop + (wBot - wTop) * Math.pow(t, 1.7); }
    };
  };

  // Flowers (and seeds) keep off the road. The usual x span, a little wider than the stage, minus
  // the road and a verge, with u spread evenly over what is left: density stays even and the
  // layout stays identical for everyone.
  // m (flowers only) is how far in from the stage edge a stem must stand for its whole bloom to
  // show. The span runs past both edges, which cut the outermost blooms in half (on a phone most
  // of all) and could hide a small one at the back entirely. So a band along each edge, three
  // margins wide and never reaching the road, is gently squeezed until its outermost stem stands
  // m in from the edge. Flowers past the band keep their exact places, and every flower keeps its
  // side of the road and its left to right order, so neighbours stay neighbours, only a little
  // closer, and a flower brought in from the edge never jumps past them.
  G.roadsideX = function (u, y, v, m) {
    var W = this.W, x0 = -0.03 * W, x1 = 1.03 * W, rd = this.road;
    if (!rd) return x0 + (x1 - x0) * u;
    var t = c01((y - rd.hz) / Math.max(1, this.H - rd.hz));
    var cx = rd.cx(t), hw = rd.hw(t) + this.sizeAt(v) * 0.1 + 2;
    var L = Math.max(0, cx - hw - x0), R = Math.max(0, x1 - (cx + hw));
    var s = u * (L + R), x = s < L ? x0 + s : cx + hw + (s - L), a;
    if (!(m > 0)) return x;
    if (s < L) {
      a = Math.min(cx - hw, x0 + 3 * (m - x0));
      if (x < a && a - m >= 1) x = m + (x - x0) * (a - m) / (a - x0);
    } else {
      a = Math.max(cx + hw, x1 - 3 * (x1 - (W - m)));
      if (x > a && W - m - a >= 1) x = W - m - (x1 - x) * (W - m - a) / (x1 - a);
    }
    return x;
  };

  G.paintScenery = function () {
    if (!this.ctx || !this.W) return;
    var W = this.W, H = this.H, dpr = this.dpr, p = this.pal, key = this.phase;
    var mg = this.magic = MAGIC[key] || MAGIC.bloom;
    var bg = this.bg || (this.bg = d.createElement('canvas'));
    bg.width = Math.round(W * dpr); bg.height = Math.round(H * dpr);
    var g = bg.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    var rnd = (function (s) { return function () { s = (s * 16807) % 2147483647; return s / 2147483647; }; })(this.seed % 2147483646 + 1);
    var hz = this.horizon, phone = W < 600, tease = key === 'tease', full = key === 'full', press = key === 'pressed';
    var rd = this.road || (this.road = this.roadGeometry());
    var lightC = rgbOf(p.light), su = this.sunAt();
    var x, y, i, k;

    // Sky: deep overhead, pale and warm at the horizon, with the light of the day
    var sky = g.createLinearGradient(0, 0, 0, hz + H * 0.04);
    sky.addColorStop(0, p.skyTop); sky.addColorStop(0.55, p.skyMid); sky.addColorStop(1, p.skyBottom);
    g.fillStyle = sky; g.fillRect(0, 0, W, H);
    // A deeper, richer zenith and a glowing band of warm light along the horizon
    var zc = rgbOf(mg.zenith), zen = g.createLinearGradient(0, 0, 0, hz * 0.62);
    zen.addColorStop(0, css(zc, mg.zenithA)); zen.addColorStop(1, css(zc, 0));
    g.fillStyle = zen; g.fillRect(0, 0, W, hz);
    var hc = rgbOf(mg.horizon), band = g.createLinearGradient(0, hz * 0.38, 0, hz + H * 0.03);
    band.addColorStop(0, css(hc, 0)); band.addColorStop(0.72, css(hc, mg.horizonA)); band.addColorStop(1, css(hc, mg.horizonA * 0.55));
    g.fillStyle = band; g.fillRect(0, 0, W, hz + H * 0.03);
    var lx = W * (full ? 0.8 : tease ? rd.vx / W : 0.2), ly = hz * (full ? 0.8 : tease ? 0.95 : 0.25);
    var lr = Math.max(W, H) * (full ? 0.6 : 0.5);
    var light = g.createRadialGradient(lx, ly, 0, lx, ly, lr);
    light.addColorStop(0, css(lightC, full ? 0.8 : tease ? 0.55 : 0.5));
    light.addColorStop(0.3, css(lightC, full ? 0.3 : 0.16));
    light.addColorStop(1, css(lightC, 0));
    g.fillStyle = light; g.fillRect(0, 0, W, H);

    // First stars at twilight: most high in the deep blue, thinning and fading toward the warm
    // horizon, a few brighter ones with a soft glow, some faintly blue and some faintly gold.
    if (p.stars) {
      var starCols = ['255,246,228', '214,226,255', '255,232,200'];
      for (i = 0; i < (phone ? 80 : 190); i++) {
        var sx = rnd() * W, sy = Math.pow(rnd(), 1.6) * hz * 0.74, up = 1 - sy / (hz * 0.78);
        var big = rnd() < 0.06, ss = big ? 1.05 + rnd() * 0.5 : 0.3 + rnd() * 0.65;
        var sa = (0.3 + rnd() * 0.6) * (0.3 + 0.7 * up), scol = starCols[(rnd() * 3) | 0];
        if (big) {
          var sgl = g.createRadialGradient(sx, sy, 0, sx, sy, ss * 6);
          sgl.addColorStop(0, 'rgba(' + scol + ',' + (sa * 0.4).toFixed(3) + ')'); sgl.addColorStop(1, 'rgba(' + scol + ',0)');
          g.fillStyle = sgl; g.fillRect(sx - ss * 6, sy - ss * 6, ss * 12, ss * 12);
        }
        g.fillStyle = 'rgba(' + scol + ',' + sa.toFixed(2) + ')';
        g.beginPath(); g.arc(sx, sy, ss, 0, TAU); g.fill();
      }
    }
    // The Seeds night: a faint band of the galaxy across the sky, dusted with tiny stars
    if (mg.milky) {
      g.save();
      g.translate(W * 0.6, hz * 0.32); g.rotate(phone ? -0.62 : -0.34);
      var mwL = Math.max(W, H) * 1.1, mwH = hz * (phone ? 0.2 : 0.17);
      var mw = g.createLinearGradient(0, -mwH, 0, mwH);
      mw.addColorStop(0, 'rgba(206,186,255,0)'); mw.addColorStop(0.42, 'rgba(226,206,255,0.1)'); mw.addColorStop(0.5, 'rgba(255,226,236,0.15)');
      mw.addColorStop(0.58, 'rgba(226,206,255,0.1)'); mw.addColorStop(1, 'rgba(206,186,255,0)');
      g.fillStyle = mw; g.fillRect(-mwL / 2, -mwH, mwL, mwH * 2);
      for (i = 0; i < (phone ? 120 : 300); i++) {
        var mu = (rnd() - 0.5) * mwL, mv = (rnd() + rnd() + rnd() - 1.5) / 1.5 * mwH * 0.85;
        g.fillStyle = 'rgba(255,248,238,' + (0.12 + rnd() * 0.5).toFixed(2) + ')';
        g.fillRect(mu, mv, 0.9, 0.9);
      }
      g.restore();
    }
    // A slim crescent moon over the Seeds field, in its own pale halo
    if (mg.moon) {
      var mx = W * mg.moon.x, my = hz * mg.moon.y, mr = Math.max(8, Math.min(W, H) * 0.026);
      var mgl = g.createRadialGradient(mx, my, 0, mx, my, mr * 7);
      mgl.addColorStop(0, 'rgba(255,242,218,0.4)'); mgl.addColorStop(0.28, 'rgba(226,214,255,0.14)'); mgl.addColorStop(1, 'rgba(226,214,255,0)');
      g.fillStyle = mgl; g.fillRect(mx - mr * 7, my - mr * 7, mr * 14, mr * 14);
      var mc = this.moonCv || (this.moonCv = d.createElement('canvas')), ms = Math.ceil(mr * 2 + 4);
      mc.width = Math.max(1, Math.round(ms * dpr)); mc.height = mc.width;
      var mcx = mc.getContext('2d');
      mcx.setTransform(dpr, 0, 0, dpr, 0, 0);
      mcx.clearRect(0, 0, ms, ms);
      var mf = mcx.createRadialGradient(ms / 2 - mr * 0.4, ms / 2 - mr * 0.2, 0, ms / 2, ms / 2, mr);
      mf.addColorStop(0, '#FFFCF2'); mf.addColorStop(0.7, '#FBEBC8'); mf.addColorStop(1, '#EED39E');
      mcx.fillStyle = mf; mcx.beginPath(); mcx.arc(ms / 2, ms / 2, mr, 0, TAU); mcx.fill();
      mcx.globalCompositeOperation = 'destination-out';
      mcx.beginPath(); mcx.arc(ms / 2 + mr * 0.48, ms / 2 - mr * 0.24, mr * 0.9, 0, TAU); mcx.fill();
      mcx.globalCompositeOperation = 'source-over';
      g.drawImage(mc, mx - ms / 2, my - ms / 2, ms, ms);
    }
    // The sun: a bright disc in a wide glow. By day it rides high on the left; at golden hour it
    // sits low on the right, half behind the cliff. Its rays are painted on their own sheet
    // (paintLight) and drawn over the meadow every frame.
    if (su) {
      var sgw = g.createRadialGradient(su.x, su.y, 0, su.x, su.y, su.r * 8);
      sgw.addColorStop(0, css(rgbOf(mg.sun.core), 0.95)); sgw.addColorStop(0.1, css(lightC, 0.62));
      sgw.addColorStop(0.36, css(lightC, 0.18)); sgw.addColorStop(1, css(lightC, 0));
      g.fillStyle = sgw; g.fillRect(su.x - su.r * 8, su.y - su.r * 8, su.r * 16, su.r * 16);
      var disc = g.createRadialGradient(su.x - su.r * 0.25, su.y - su.r * 0.25, 0, su.x, su.y, su.r);
      disc.addColorStop(0, '#FFFFFF'); disc.addColorStop(0.55, css(rgbOf(mg.sun.core))); disc.addColorStop(1, css(mixHex(mg.sun.core, p.light, full ? 0.75 : 0.4)));
      g.fillStyle = disc; g.beginPath(); g.arc(su.x, su.y, su.r, 0, TAU); g.fill();
    }

    // A rainbow arching over the city, a faint second bow outside it, and the sky a touch
    // brighter inside the arc, as it is in a real rainbow.
    var rbA = Math.min(0.44, (p.rainbow || 0) * (mg.rainbowBoost || 1));
    if (rbA > 0) {
      var rcx = rd.vx, rcy = hz * 1.35, r1 = hz * 1.26, r0 = r1 * 0.8, rh = Math.ceil(hz + H * 0.02);
      var bands = ['#A77BD6', '#6D6DD8', '#5FA8E8', '#7CC27A', '#F2D45B', '#F09A4A', '#E85D5D'];
      // Painted on its own sheet, then faded toward the horizon so its feet melt into the haze.
      var rbc = this.rainbowCv || (this.rainbowCv = d.createElement('canvas'));
      rbc.width = Math.round(W * dpr); rbc.height = Math.round(rh * dpr);
      var rx = rbc.getContext('2d');
      rx.setTransform(dpr, 0, 0, dpr, 0, 0);
      rx.clearRect(0, 0, W, rh);
      var inner = rx.createRadialGradient(rcx, rcy, 0, rcx, rcy, r0 * 1.04);
      inner.addColorStop(0.55, 'rgba(255,255,255,0)'); inner.addColorStop(1, 'rgba(255,255,255,' + (rbA * 0.32).toFixed(3) + ')');
      rx.fillStyle = inner; rx.fillRect(0, 0, W, rh);
      var rg = rx.createRadialGradient(rcx, rcy, r0, rcx, rcy, r1);
      rg.addColorStop(0, css(mixHex(bands[0], p.skyMid, 0.15), 0));
      for (i = 0; i < 7; i++) rg.addColorStop(0.08 + 0.84 * (i + 0.5) / 7, css(mixHex(bands[i], p.skyMid, 0.15), rbA));
      rg.addColorStop(1, css(mixHex(bands[6], p.skyMid, 0.15), 0));
      rx.fillStyle = rg; rx.fillRect(0, 0, W, rh);
      var q0 = r1 * 1.1, q1 = r1 * 1.32, rg2 = rx.createRadialGradient(rcx, rcy, q0, rcx, rcy, q1);
      rg2.addColorStop(0, css(mixHex(bands[6], p.skyMid, 0.3), 0));
      for (i = 0; i < 7; i++) rg2.addColorStop(0.1 + 0.8 * (i + 0.5) / 7, css(mixHex(bands[6 - i], p.skyMid, 0.3), rbA * 0.3));
      rg2.addColorStop(1, css(mixHex(bands[0], p.skyMid, 0.3), 0));
      rx.fillStyle = rg2; rx.fillRect(0, 0, W, rh);
      rx.globalCompositeOperation = 'destination-in';
      var rf = rx.createLinearGradient(0, Math.max(0, rcy - q1), 0, hz);
      rf.addColorStop(0, 'rgba(0,0,0,1)'); rf.addColorStop(0.5, 'rgba(0,0,0,0.85)'); rf.addColorStop(1, 'rgba(0,0,0,0.12)');
      rx.fillStyle = rf; rx.fillRect(0, 0, W, rh);
      rx.globalCompositeOperation = 'source-over';
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.drawImage(rbc, 0, 0);
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    // Soft clouds, kept away from the city, plus a few high wisps
    if (!tease) {
      // Each cloud is a row of soft puffs, tallest in the middle, painted on its own sheet: lit
      // from above and brightest on the side facing the sun, shaded underneath (rose and violet at
      // golden hour), with a soft flat base.
      var cloudA = full ? 0.8 : press ? 0.62 : 0.86;
      var under = full ? css(mixHex('#B4609A', p.skyMid, 0.3), 0.46) : css(mixHex(p.skyMid, p.mountains, 0.5), 0.3);
      var csheet = this.cloudCv || (this.cloudCv = d.createElement('canvas'));
      var nCl = phone ? 4 : 5;
      for (var wsp = 0; wsp < (press ? 0 : phone ? 2 : 3); wsp++) {
        var wx = (0.12 + rnd() * 0.76) * W, wy = hz * (0.08 + rnd() * 0.2), wl = W * (0.12 + rnd() * 0.14);
        g.save(); g.translate(wx, wy); g.rotate((rnd() - 0.5) * 0.12); g.scale(1, 0.06);
        var wg = g.createRadialGradient(0, 0, 0, 0, 0, wl);
        wg.addColorStop(0, full ? 'rgba(255,214,190,0.5)' : 'rgba(255,255,255,0.42)'); wg.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = wg; g.beginPath(); g.arc(0, 0, wl, 0, TAU); g.fill();
        g.restore();
      }
      for (var cl = 0; cl < nCl; cl++) {
        var cx = (0.03 + (0.94 / nCl) * cl + rnd() * 0.08) * W, cy = hz * (0.14 + rnd() * 0.38), cw = W * (phone ? 0.15 : 0.1) * (1 + rnd() * 0.7);
        // Clouds keep clear of the city: one drifting over it moves aside, and none is cut by the top edge.
        if (Math.abs(cx - rd.vx) < W * 0.12 + cw * 0.5) cx = rd.vx + (cx < rd.vx ? -1 : 1) * (W * 0.14 + cw * 0.6);
        cy = Math.max(cy, cw * 0.5 + 6);
        var cwid = cw * 1.5, chei = cw * 0.66, base = cw * 0.52;
        csheet.width = Math.max(1, Math.round(cwid * dpr)); csheet.height = Math.max(1, Math.round(chei * dpr));
        var cc = csheet.getContext('2d');
        cc.setTransform(dpr, 0, 0, dpr, 0, 0);
        cc.clearRect(0, 0, cwid, chei);
        var nP = 8 + ((rnd() * 4) | 0);
        for (var pf = 0; pf < nP; pf++) {
          var pu = pf / (nP - 1), bump = Math.sin(pu * Math.PI);
          var pr = cw * (0.09 + 0.14 * bump + rnd() * 0.05), px = cw * 0.25 + pu * cw, py = base - pr * 0.5 - bump * cw * 0.06 - (pf % 2) * cw * 0.03;
          var cg = cc.createRadialGradient(px - pr * 0.2, py - pr * 0.25, 0, px, py, pr);
          cg.addColorStop(0, 'rgba(255,255,255,0.98)'); cg.addColorStop(0.55, 'rgba(255,255,255,0.82)'); cg.addColorStop(1, 'rgba(255,255,255,0)');
          cc.fillStyle = cg;
          cc.beginPath(); cc.arc(px, py, pr, 0, TAU); cc.fill();
        }
        cc.save(); cc.translate(cw * 0.75, base); cc.scale(1, 0.16);
        var cbase = cc.createRadialGradient(0, 0, 0, 0, 0, cw * 0.6);
        cbase.addColorStop(0, 'rgba(255,255,255,0.9)'); cbase.addColorStop(1, 'rgba(255,255,255,0)');
        cc.fillStyle = cbase; cc.beginPath(); cc.arc(0, 0, cw * 0.6, 0, TAU); cc.fill();
        cc.restore();
        cc.globalCompositeOperation = 'source-atop';
        var tint = cc.createLinearGradient(0, base - cw * 0.42, 0, base + cw * 0.06);
        tint.addColorStop(0, full ? 'rgba(255,222,160,0.62)' : 'rgba(255,255,255,0)'); tint.addColorStop(0.5, 'rgba(255,255,255,0)'); tint.addColorStop(1, under);
        cc.fillStyle = tint; cc.fillRect(0, 0, cwid, chei);
        if (su) { // the side that faces the sun catches its light
          var sideX = su.x < cx ? 0 : cwid, sl = cc.createRadialGradient(sideX, base - cw * 0.25, 0, sideX, base - cw * 0.25, cwid * 0.7);
          sl.addColorStop(0, css(mixHex(p.light, '#FFFFFF', full ? 0.2 : 0.6), full ? 0.55 : 0.4)); sl.addColorStop(1, css(lightC, 0));
          cc.fillStyle = sl; cc.fillRect(0, 0, cwid, chei);
        }
        cc.globalCompositeOperation = 'destination-in';
        var flat = cc.createLinearGradient(0, 0, 0, chei);
        flat.addColorStop(0, 'rgba(0,0,0,1)'); flat.addColorStop(c01((base - cw * 0.03) / chei), 'rgba(0,0,0,1)'); flat.addColorStop(c01((base + cw * 0.07) / chei), 'rgba(0,0,0,0)'); flat.addColorStop(1, 'rgba(0,0,0,0)');
        cc.fillStyle = flat; cc.fillRect(0, 0, cwid, chei);
        cc.globalCompositeOperation = 'source-over';
        g.globalAlpha = cloudA;
        g.drawImage(csheet, cx - cwid / 2, cy - base, cwid, chei);
        g.globalAlpha = 1;
      }
    }

    // Far mountains, blue with distance, their faces lit from above and hazier at the foot
    var ridge = function (col, base, amp, f1, f2, ph, sharp) {
      var rgr = g.createLinearGradient(0, base - amp, 0, base + H * 0.02);
      rgr.addColorStop(0, css(mixHex(col, p.light, 0.14))); rgr.addColorStop(0.55, css(col)); rgr.addColorStop(1, css(mixHex(col, p.skyBottom, 0.3)));
      g.fillStyle = rgr;
      g.beginPath(); g.moveTo(0, H);
      for (x = 0; x <= W + 8; x += 8) {
        if (sharp) y = base - amp * (0.62 * Math.abs(Math.sin(x / W * f1 + ph)) + 0.38 * Math.abs(Math.sin(x / W * f2 + ph * 1.7)));
        else y = base - amp * (0.55 * Math.sin(x / W * f1 + ph) + 0.3 * Math.sin(x / W * f2 + ph * 2) + 0.15 * Math.sin(x / W * 23 + ph));
        g.lineTo(x, y);
      }
      g.lineTo(W, H); g.closePath(); g.fill();
    };
    ridge(mixHex(p.mountains, p.skyBottom, 0.6), hz - H * 0.03, H * 0.11, 3.4, 8.9, 1.9, true);
    ridge(mixHex(p.mountains, p.skyBottom, 0.4), hz - H * 0.015, H * 0.07, 5.6, 13.1, 0.4, true);

    // A grey cliff standing at the right edge
    var cx0 = W * (phone ? 0.8 : 0.83), ctop = hz - H * (phone ? 0.11 : 0.19);
    var pts = [[cx0, hz + H * 0.03], [cx0 + W * 0.018, hz - H * 0.04], [cx0 + W * 0.034, ctop + H * 0.07], [cx0 + W * 0.058, ctop + H * 0.025],
      [cx0 + W * 0.08, ctop], [cx0 + W * 0.108, ctop + H * 0.035], [cx0 + W * 0.14, ctop - H * 0.012], [W + 10, ctop + H * 0.012], [W + 10, hz + H * 0.03]];
    var cliffG = g.createLinearGradient(0, ctop, 0, hz);
    cliffG.addColorStop(0, css(mixHex(p.cliff, p.skyBottom, 0.12))); cliffG.addColorStop(1, css(mixHex(p.cliff, p.skyBottom, 0.5)));
    g.fillStyle = cliffG; g.beginPath(); g.moveTo(pts[0][0], pts[0][1]);
    for (i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
    g.closePath(); g.fill();
    g.lineWidth = 1;
    g.lineCap = 'round';
    for (i = 0; i < 18; i++) {
      var fx = cx0 + W * 0.03 + rnd() * (W - cx0 - W * 0.03), fy = ctop + H * 0.04 + rnd() * H * 0.06, fl = H * (0.02 + rnd() * 0.05);
      g.strokeStyle = rnd() < 0.5 ? 'rgba(255,255,255,0.1)' : css(mixHex(p.cliff, '#2A2E3A', 0.5), 0.12);
      g.beginPath(); g.moveTo(fx, fy); g.lineTo(fx + (rnd() - 0.5) * 4, fy + fl); g.stroke();
    }

    // The emerald city at the end of the road, glowing inside a soft halo
    var cityH = H * (phone ? 0.17 : 0.21), cityW = W * (phone ? 0.2 : 0.11), gx = rd.vx, gy = hz - H * 0.005;
    // In Pressed the city rests like the rest of the paper: softer contrast, a faint glow, no twinkle.
    var glow = rgbOf(p.cityGlow), cityC = mixHex(p.city, p.haze, press ? 0.32 : 0.18), cityL = mixHex(p.cityLight, p.haze, press ? 0.3 : 0.08), cityD = mixHex(p.city, '#0B2F25', press ? 0.18 : 0.45);
    // Seeds night: two soft searchlights of emerald light sweep up from the city into the stars.
    if (mg.beams) {
      for (var bm = -1; bm <= 1; bm += 2) {
        g.save();
        g.translate(gx + bm * cityW * 0.12, gy - cityH * 0.55); g.rotate(bm * 0.24);
        var bl = hz * 1.05, bw = cityW * (phone ? 0.3 : 0.26);
        var bgr = g.createLinearGradient(0, 0, 0, -bl);
        bgr.addColorStop(0, css(glow, 0.26)); bgr.addColorStop(0.5, css(glow, 0.08)); bgr.addColorStop(1, css(glow, 0));
        g.fillStyle = bgr;
        g.beginPath(); g.moveTo(-bw * 0.12, 0); g.lineTo(-bw, -bl); g.lineTo(bw, -bl); g.lineTo(bw * 0.12, 0); g.closePath(); g.fill();
        g.restore();
      }
    }
    var halo = g.createRadialGradient(gx, gy - cityH * 0.4, 0, gx, gy - cityH * 0.4, cityH * 1.8);
    halo.addColorStop(0, css(glow, tease ? 0.75 : press ? 0.22 : 0.52)); halo.addColorStop(0.45, css(glow, tease ? 0.32 : press ? 0.08 : 0.18)); halo.addColorStop(1, css(glow, 0));
    g.fillStyle = halo; g.fillRect(gx - cityH * 1.9, gy - cityH * 2.3, cityH * 3.8, cityH * 2.4);
    // The city's glass dome: a soft sheen that fades out toward its feet, not a drawn outline.
    var ringA = tease ? 0.22 : press ? 0.06 : 0.13, ringY = gy - cityH * 0.1, ringR = cityH * 1.12;
    var ringG = g.createLinearGradient(0, ringY - ringR, 0, ringY);
    ringG.addColorStop(0, 'rgba(255,255,255,' + ringA + ')'); ringG.addColorStop(0.55, 'rgba(255,255,255,' + (ringA * 0.45).toFixed(3) + ')'); ringG.addColorStop(1, 'rgba(255,255,255,0)');
    g.strokeStyle = ringG;
    g.globalAlpha = 0.35; g.lineWidth = 5;
    g.beginPath(); g.arc(gx, ringY, ringR, Math.PI, TAU); g.stroke();
    g.globalAlpha = 1; g.lineWidth = 1;
    g.beginPath(); g.arc(gx, ringY, ringR, Math.PI, TAU); g.stroke();
    var n = phone ? 9 : 13, spires = [];
    for (i = 0; i < n; i++) {
      var u = (i + 0.5) / n, env = Math.pow(1 - Math.abs(u - 0.5) * 2, 0.7), hs = hash(this.seed, 91, i);
      spires.push({ x: gx + (u - 0.5) * cityW, h: cityH * (0.3 + 0.7 * env) * (0.72 + 0.5 * h01(hs, 1)), w: cityW / n * (0.6 + 0.5 * h01(hs, 2)), lit: h01(hs, 3) });
    }
    spires.sort(function (a, b) { return Math.abs(b.x - gx) - Math.abs(a.x - gx); });
    this.cityStars = [];
    for (i = 0; i < spires.length; i++) {
      var sp = spires[i], top = gy - sp.h, hw2 = sp.w / 2;
      var sg = g.createLinearGradient(sp.x - hw2, 0, sp.x + hw2, 0);
      sg.addColorStop(0, css(mixHex(cityC, '#FFFFFF', 0.1))); sg.addColorStop(0.3, css(mixHex(cityC, '#FFFFFF', 0.5)));
      sg.addColorStop(0.45, css(cityL)); sg.addColorStop(0.65, css(cityC)); sg.addColorStop(1, css(cityD));
      g.fillStyle = sg;
      g.beginPath(); g.moveTo(sp.x - hw2, gy + 2); g.lineTo(sp.x - hw2, top + hw2); g.arc(sp.x, top + hw2, hw2, Math.PI, TAU); g.lineTo(sp.x + hw2, gy + 2); g.closePath(); g.fill();
      g.fillStyle = css(cityL);
      g.beginPath(); g.moveTo(sp.x - hw2 * 0.25, top + hw2 * 0.6); g.lineTo(sp.x, top - sp.h * 0.14); g.lineTo(sp.x + hw2 * 0.25, top + hw2 * 0.6); g.closePath(); g.fill();
      if (!phone) {
        g.strokeStyle = 'rgba(255,255,255,0.3)'; g.lineWidth = 0.8;
        g.beginPath(); g.moveTo(sp.x - hw2 * 0.3, top + hw2 * 1.2); g.lineTo(sp.x - hw2 * 0.3, gy); g.stroke();
      }
      if (sp.lit > 0.4) this.cityStars.push({ x: sp.x, y: top - sp.h * 0.12, ph: sp.lit * TAU, s: 0.5 + sp.lit * 0.6 });
    }
    for (k = 0; k < (phone ? 3 : 5); k++) {
      var dx = gx + (k - (phone ? 1 : 2)) * cityW * 0.3 + (rnd() - 0.5) * cityW * 0.1, dr = cityW * (0.1 + rnd() * 0.06);
      var dg = g.createRadialGradient(dx - dr * 0.3, gy - dr * 0.5, 0, dx, gy, dr * 1.2);
      dg.addColorStop(0, css(cityL)); dg.addColorStop(0.5, css(cityC)); dg.addColorStop(1, css(cityD));
      g.fillStyle = dg; g.beginPath(); g.ellipse(dx, gy + 1, dr, dr * 0.75, 0, Math.PI, TAU); g.fill();
    }
    var spark = sparkSprite();
    for (i = 0; i < this.cityStars.length; i++) {
      var st = this.cityStars[i], sz = (phone ? 5 : 8) * st.s;
      g.globalAlpha = press ? 0.35 : 0.85; g.drawImage(spark, st.x - sz / 2, st.y - sz / 2, sz, sz);
    }
    g.globalAlpha = 1;

    // Rolling hills, dipping where the road comes through, their crests catching the light
    var dip = function (xx) { var dd = (xx - gx) / (W * 0.09); return 1 - 0.55 * Math.exp(-dd * dd); };
    var hill = function (col, base, amp, f1, f2, ph) {
      var hg = g.createLinearGradient(0, base - amp, 0, base + amp * 0.6);
      hg.addColorStop(0, css(mixHex(col, p.light, 0.2))); hg.addColorStop(1, css(col));
      g.fillStyle = hg;
      g.beginPath(); g.moveTo(0, H);
      for (x = 0; x <= W + 8; x += 8) {
        y = base - amp * dip(x) * (0.55 * Math.sin(x / W * f1 + ph) + 0.3 * Math.sin(x / W * f2 + ph * 2) + 0.15 * Math.sin(x / W * 23 + ph));
        g.lineTo(x, y);
      }
      g.lineTo(W, H); g.closePath(); g.fill();
    };
    hill(mixHex(p.hillsFar, p.skyBottom, 0.3), hz - H * 0.012, H * 0.05, 5.1, 11.3, 0.7);
    hill(mixHex(p.hills, p.skyBottom, 0.12), hz + H * 0.006, H * 0.035, 7.3, 15.7, 2.1);

    // Ground
    var gr = g.createLinearGradient(0, hz, 0, H);
    if (tease) {
      gr.addColorStop(0, css(mixHex(p.soilHighlight, p.skyBottom, 0.35)));
      gr.addColorStop(0.35, p.soilHighlight);
      gr.addColorStop(1, p.soil);
    } else {
      gr.addColorStop(0, css(mixHex(p.grassBack, p.skyBottom, 0.25)));
      gr.addColorStop(0.3, p.grassBack);
      gr.addColorStop(0.7, p.grassMid);
      gr.addColorStop(1, p.grassFront);
    }
    g.fillStyle = gr;
    g.beginPath(); g.moveTo(0, H);
    for (x = 0; x <= W + 8; x += 8) g.lineTo(x, hz + H * 0.012 + Math.sin(x / W * 9.4 + 1.3) * H * 0.006);
    g.lineTo(W, H); g.closePath(); g.fill();

    if (tease) {
      // Sown field: evenly spaced furrows in perspective, one seed per box of the largest
      // milestone (500 by default), each seed resting in a furrow on a lightly jittered grid
      // so markers never pile up. Furrow rows get closer together toward the horizon.
      var seedTotal = this.seedTarget();
      var gTop = this.groundTop, gBot = this.groundBot;
      var cols = Math.max(6, Math.round(Math.sqrt(seedTotal * (W / Math.max(1, gBot - gTop)) * 0.9)));
      var rows = Math.max(3, Math.ceil(seedTotal / cols));
      var rowV = [];
      // Rows a little unevenly spaced (never crossing), as a field is sown by hand.
      for (var row = 0; row < rows; row++) rowV.push(Math.pow((row + 0.5 + (h01(hash(this.seed, 79, row), 1) - 0.5) * 0.36) / rows, 1.35));
      // Soft soil shading: lighter where the light falls, darker toward the front
      var shade = g.createLinearGradient(0, gTop, 0, H);
      shade.addColorStop(0, 'rgba(255,236,210,0.16)'); shade.addColorStop(0.5, 'rgba(255,236,210,0)'); shade.addColorStop(1, 'rgba(40,28,20,0.22)');
      g.fillStyle = shade; g.fillRect(0, gTop - 2, W, H - gTop + 2);
      // The city's emerald glow spills down the field around the road, and the moon silvers the far rows
      var spill = g.createRadialGradient(rd.vx, gTop, 0, rd.vx, gTop, W * 0.42);
      spill.addColorStop(0, css(glow, 0.2)); spill.addColorStop(1, css(glow, 0));
      g.save(); g.translate(rd.vx, gTop); g.scale(1, 0.55); g.translate(-rd.vx, -gTop);
      g.fillStyle = spill; g.beginPath(); g.arc(rd.vx, gTop, W * 0.42, 0, TAU); g.fill();
      g.restore();
      // A furrow's line across the field: a slow wobble plus a gentle bow, so the rows follow the
      // curve of the ground instead of ruling it like paper.
      var furrowY = function (rw, xx) {
        var v = rowV[rw], u = (xx - W * 0.5) / W;
        return lerp(gTop, H, v) + Math.sin(xx / W * 5.2 + rw * 0.9) * (0.6 + v * 2.2) + u * u * (4 + v * 26);
      };
      for (row = 0; row < rows; row++) {
        var v = rowV[row], lw = 1.2 + v * 6.5, fx0 = -12;
        // Each furrow in broken lengths of turned soil, each length a little different in weight.
        while (fx0 < W + 12) {
          var segL = W * (0.1 + rnd() * 0.3), fx1 = Math.min(W + 12, fx0 + segL), k1 = 0.6 + rnd() * 0.55;
          g.lineCap = 'round';
          g.strokeStyle = css(rgbOf('#3A2C22'), (0.16 + v * 0.16) * k1);
          g.lineWidth = lw * (0.8 + rnd() * 0.45);
          g.beginPath();
          for (x = fx0; x <= fx1; x += 10) g.lineTo(x, furrowY(row, x));
          g.stroke();
          g.strokeStyle = css(rgbOf(p.soilHighlight), 0.3 * k1);
          g.lineWidth = Math.max(1, lw * 0.4);
          g.beginPath();
          for (x = fx0; x <= fx1; x += 10) g.lineTo(x, furrowY(row, x) - lw * 0.75);
          g.stroke();
          fx0 = fx1 + (rnd() < 0.55 ? W * (0.004 + rnd() * 0.018) : 0);
        }
      }
      // Fine soil grain, quiet and even
      for (k = 0; k < W * H / 1400; k++) {
        var vv = rnd(), py2 = lerp(gTop, H, vv), sz2 = 0.5 + vv * 1.6 * rnd();
        g.fillStyle = rnd() < 0.5 ? css(rgbOf('#3E3026'), 0.2) : css(rgbOf('#A58B72'), 0.22);
        g.beginPath(); g.ellipse(rnd() * W, py2, sz2 * 1.5, sz2, 0, 0, TAU); g.fill();
      }
      this.paintRoad(g, rd, p, rnd);
      // Seeds: jittered grid along the furrows, kept off the road, all drawn into the still layer; the glow animates
      this.seeds = [];
      var placed = 0;
      for (row = 0; row < rows && placed < seedTotal; row++) {
        var rv = rowV[row], rry = lerp(gTop, H, rv), rw2 = (1.2 + rv * 6.5);
        var inRow = Math.min(cols, seedTotal - placed), shift = h01(hash(this.seed, 78, row), 1);
        for (var c = 0; c < inRow; c++, placed++) {
          // Each row starts at its own offset and every seed drifts within its spot, so the seeds
          // never line up into columns; they wrap round the row instead of piling at its ends.
          var hs2 = hash(this.seed, 77, row * 1000 + c);
          var su2 = (c + 0.5 + shift + (h01(hs2, 1) - 0.5) * 0.85) / inRow;
          su2 -= Math.floor(su2);
          var sx2 = this.roadsideX(su2, rry, rv);
          var sy2 = furrowY(row, sx2) + (h01(hs2, 2) - 0.5) * rw2 * 0.6;
          var sc = (0.35 + 0.65 * Math.pow(rv, 1.1)) * (0.88 + 0.24 * h01(hs2, 4));
          var seedObj = { x: sx2, y: sy2, s: sc, ph: h01(hs2, 3) * TAU, tip: h01(hs2, 5) < 0.3, glow: h01(hs2, 6) < 0.4 };
          this.seeds.push(seedObj);
          this.paintSeed(g, seedObj);
        }
      }
    } else {
      var gTop2 = this.groundTop;
      // Light in the field: wide sunlit patches and the soft shadows of passing clouds
      for (k = 0; k < (phone ? 5 : 7); k++) {
        var lpx = rnd() * W, lpv = 0.08 + rnd() * 0.72, lpy = lerp(gTop2, H, lpv), lpr = W * (0.12 + rnd() * 0.18);
        var lit = k % 3 !== 2, lc = lit ? lightC : rgbOf('#1C3A1E');
        var lp = g.createRadialGradient(lpx, lpy, 0, lpx, lpy, lpr);
        lp.addColorStop(0, css(lc, lit ? (full ? 0.24 : press ? 0.1 : 0.16) : (press ? 0.05 : 0.12))); lp.addColorStop(1, css(lc, 0));
        g.save(); g.translate(lpx, lpy); g.scale(1, 0.28 + lpv * 0.22); g.translate(-lpx, -lpy);
        g.fillStyle = lp; g.beginPath(); g.arc(lpx, lpy, lpr, 0, TAU); g.fill();
        g.restore();
      }
      if (su) { // the low light rakes across the grass from the sun's side
        var rake = g.createLinearGradient(su.x < W / 2 ? 0 : W, 0, su.x < W / 2 ? W : 0, 0);
        rake.addColorStop(0, css(lightC, full ? 0.26 : 0.12)); rake.addColorStop(0.6, css(lightC, 0));
        g.fillStyle = rake; g.fillRect(0, gTop2 - 2, W, H - gTop2 + 2);
      }
      // Grass texture: short strokes, longer toward the front. Batched by color and depth band so
      // the dense field paints quickly; about one blade in seven catches the light on its tip.
      var nG = Math.min(9000, Math.round(W * H / 165)), BANDS = 4, buckets = {};
      var greens = [rgbOf(p.grassBack), rgbOf(p.grassMid), rgbOf(p.grassFront), rgbOf('#8DB06F')];
      var tones = [rgbOf('#2F4F2C'), rgbOf('#C9D6A6'), lightC], hazeC = rgbOf(p.haze);
      for (k = 0; k < nG; k++) {
        var tv = Math.pow(rnd(), 0.8), gy2 = lerp(gTop2, H + 4, tv), gx2 = rnd() * W;
        var len = 2 + tv * tv * H * 0.05 * (0.5 + rnd()), bend = (rnd() - 0.5) * len * 0.6;
        var ci = (rnd() * 4) | 0, tr = rnd(), ti = tr < 0.45 ? 0 : tr < 0.86 ? 1 : 2;
        var bk = Math.min(BANDS - 1, (tv * BANDS) | 0) * 100 + ci * 10 + ti;
        (buckets[bk] || (buckets[bk] = [])).push(gx2, gy2, gx2 + bend * 0.3, gy2 - len * 0.6, gx2 + bend, gy2 - len);
      }
      g.lineCap = 'round';
      Object.keys(buckets).map(Number).sort(function (a, b) { return a - b; }).forEach(function (bk) {
        var bandI = (bk / 100) | 0, cI = ((bk % 100) / 10) | 0, tI = bk % 10, tvm = (bandI + 0.5) / BANDS, list = buckets[bk];
        var cc2 = mixHex(greens[cI], tones[tI], tI === 2 ? 0.42 : 0.3);
        cc2 = mixHex(cc2, hazeC, (1 - tvm) * 0.35);
        g.strokeStyle = css(cc2, tI === 2 ? 0.85 : 0.75);
        g.lineWidth = 0.6 + tvm * 1.6;
        g.beginPath();
        for (var q = 0; q < list.length; q += 6) { g.moveTo(list[q], list[q + 1]); g.quadraticCurveTo(list[q + 2], list[q + 3], list[q + 4], list[q + 5]); }
        g.stroke();
      });
      this.paintRoad(g, rd, p, rnd);
    }
    // Pressed paper grain
    if (press) {
      for (k = 0; k < W * H / 260; k++) {
        g.fillStyle = rnd() < 0.5 ? 'rgba(90,70,50,0.05)' : 'rgba(255,255,255,0.12)';
        g.fillRect(rnd() * W, rnd() * H, 1 + rnd(), 1 + rnd());
      }
    }

    // Foreground grass, drawn in front of the flowers (none on the road): dense blades, a few
    // taller seed heads, and tips lit gold against the low sun at golden hour.
    var fg = this.fg || (this.fg = d.createElement('canvas'));
    var fh = Math.round(H * 0.13);
    fg.width = Math.round(W * dpr); fg.height = Math.round(fh * dpr);
    var f = fg.getContext('2d');
    f.setTransform(dpr, 0, 0, dpr, 0, 0);
    f.clearRect(0, 0, W, fh);
    f.lineCap = 'round';
    var blades = Math.round(W / (tease ? 3.6 : 2.2));
    var fgCols = tease ? [rgbOf('#5E8A52'), rgbOf(p.grassMid), rgbOf('#7FA866'), rgbOf(p.grassFront)] : [rgbOf(p.grassFront), rgbOf(p.grassMid), rgbOf('#2F5530'), rgbOf('#7FA866'), rgbOf('#3E6E36')];
    var roadL = rd.cx(1) - rd.hw(1) + 4, roadR = rd.cx(1) + rd.hw(1) - 4, tipC = full ? mixHex(p.light, '#FFE7A0', 0.4) : mixHex(p.light, '#E9F5C8', 0.5);
    for (k = 0; k < blades; k++) {
      var bx = rnd() * W, bl = (tease ? 0.22 : 0.33) * fh + rnd() * fh * (tease ? 0.4 : 0.66), bb = (rnd() - 0.5) * bl * 0.7;
      var bcol = fgCols[(rnd() * fgCols.length) | 0], bwid = 1 + rnd() * 2.2, tipLit = !tease && rnd() < (full ? 0.3 : 0.16);
      if (bx > roadL && bx < roadR) continue;
      f.strokeStyle = css(bcol, 0.94);
      f.lineWidth = bwid;
      f.beginPath(); f.moveTo(bx, fh + 2); f.quadraticCurveTo(bx + bb * 0.2, fh - bl * 0.6, bx + bb, fh - bl); f.stroke();
      if (tipLit) {
        f.strokeStyle = css(tipC, full ? 0.75 : 0.5); f.lineWidth = Math.max(0.8, bwid * 0.6);
        f.beginPath(); f.moveTo(bx + bb * 0.62, fh - bl * 0.8); f.quadraticCurveTo(bx + bb * 0.85, fh - bl * 0.92, bx + bb, fh - bl); f.stroke();
      }
    }
    if (!tease) {
      for (k = 0; k < Math.round(W / (phone ? 38 : 30)); k++) {
        var hx2 = rnd() * W, hl = fh * (0.78 + rnd() * 0.3), hb2 = (rnd() - 0.5) * hl * 0.3;
        if (hx2 > roadL - 6 && hx2 < roadR + 6) continue;
        f.strokeStyle = css(mixHex(p.grassMid, p.light, 0.25), 0.85); f.lineWidth = 1;
        f.beginPath(); f.moveTo(hx2, fh + 2); f.quadraticCurveTo(hx2 + hb2 * 0.3, fh - hl * 0.6, hx2 + hb2, fh - hl); f.stroke();
        f.save(); f.translate(hx2 + hb2, fh - hl); f.rotate(hb2 / hl * 1.2);
        var sh2 = f.createLinearGradient(0, 5, 0, -6);
        sh2.addColorStop(0, css(mixHex(p.grassMid, p.light, 0.35))); sh2.addColorStop(1, css(mixHex(p.light, '#FFFFFF', 0.35), full ? 1 : 0.85));
        f.fillStyle = sh2; f.beginPath(); f.ellipse(0, -1, phone ? 1.6 : 2, phone ? 5 : 6.5, 0, 0, TAU); f.fill();
        f.restore();
      }
    }
    this.fgH = fh;
    this.paintLight();
    this.layerDirty = true; this.forceBake = true;

    if (!tease) this.seeds = [];
  };

  // Where the sun stands, in stage px, or null in a phase without one.
  G.sunAt = function () {
    var mg = this.magic || MAGIC[this.phase] || MAGIC.bloom, s = mg.sun;
    if (!s || !this.W) return null;
    return { x: this.W * s.x, y: this.horizon * s.y, r: Math.max(this.W < 600 ? 10 : 12, Math.min(this.W, this.H) * s.r) };
  };

  // The light sheet, painted with the scenery at half resolution (it is all soft gradients):
  // shafts of sunlight fanning out from the sun, with the sun's own bloom. It is its own canvas
  // over the meadow (canvas.flower-box-sale__rays, added here), screened onto the flowers, and the
  // CSS makes it breathe and turn very slightly around the sun, so the frame loop pays nothing for
  // it. The vignette is a CSS layer too (.flower-box-sale__stage::before). The road glints are
  // placed here as well.
  G.paintLight = function () {
    var W = this.W, H = this.H, mg = this.magic || MAGIC.bloom, p = this.pal, su = this.sunAt(), i, rs = 0.5;
    var lightC = rgbOf(p.light), full = this.phase === 'full', phone = W < 600;
    var rnd = (function (s) { return function () { s = (s * 16807) % 2147483647; return s / 2147483647; }; })((this.seed + 4049) % 2147483646 + 1);
    var rc = this.raysEl;
    if (su && mg.sun.rays > 0 && this.stage) {
      if (!rc) {
        rc = this.raysEl = d.createElement('canvas');
        rc.className = 'flower-box-sale__rays';
        rc.setAttribute('aria-hidden', 'true');
        this.stage.insertBefore(rc, this.canvas ? this.canvas.nextSibling : null);
      }
      rc.hidden = false;
      // The CSS turns the sheet a fraction of a degree around the sun.
      rc.style.transformOrigin = (su.x / W * 100).toFixed(2) + '% ' + (su.y / H * 100).toFixed(2) + '%';
      rc.width = Math.max(1, Math.round(W * rs)); rc.height = Math.max(1, Math.round(H * rs));
      var r = rc.getContext('2d');
      r.setTransform(rs, 0, 0, rs, 0, 0);
      r.clearRect(0, 0, W, H);
      var L = Math.sqrt(W * W + H * H) * 1.05, nR = full ? 15 : 10, a = mg.sun.rays;
      for (i = 0; i < nR; i++) {
        // Shafts spread all round the sun, each three nested wedges so its edges fade softly.
        var ang = (i + rnd() * 0.8) / nR * TAU, wid = 0.03 + rnd() * 0.05, len = L * (0.5 + rnd() * 0.5);
        var ra = a * (0.035 + rnd() * 0.045);
        var rg = r.createRadialGradient(su.x, su.y, su.r * 0.6, su.x, su.y, len);
        rg.addColorStop(0, css(lightC, ra * 1.6)); rg.addColorStop(0.22, css(lightC, ra)); rg.addColorStop(1, css(lightC, 0));
        r.fillStyle = rg;
        for (var nest = 1; nest <= 3; nest++) {
          var wn = wid * nest / 3;
          r.beginPath(); r.moveTo(su.x, su.y);
          r.arc(su.x, su.y, len, ang - wn, ang + wn);
          r.closePath(); r.fill();
        }
      }
      var bloom = r.createRadialGradient(su.x, su.y, 0, su.x, su.y, su.r * (full ? 7 : 5));
      bloom.addColorStop(0, css(mixHex(p.light, '#FFFFFF', 0.5), 0.42 * a)); bloom.addColorStop(0.3, css(lightC, 0.12 * a)); bloom.addColorStop(1, css(lightC, 0));
      r.fillStyle = bloom; r.fillRect(0, 0, W, H);
    } else if (rc) {
      rc.hidden = true;
    }
    // Glints that twinkle on the bricks of the road (gold dust at golden hour)
    var rd = this.road, list = [];
    if (rd && mg.glints > 0) {
      var nGl = Math.round((phone ? 10 : 18) * mg.glints);
      for (i = 0; i < nGl; i++) {
        var t = 0.08 + Math.pow(rnd(), 0.7) * 0.9, hw = rd.hw(t);
        list.push({ x: rd.cx(t) + (rnd() - 0.5) * 1.6 * hw, y: rd.hz + (H - rd.hz) * t, s: 0.35 + t * 0.9, ph: rnd() * TAU, sp: 0.6 + rnd() * 0.9 });
      }
    }
    this.glints = list;
    this.air = null; // the drifting air is set up again for the new phase and size
  };

  // The yellow brick road: a perspective ribbon with brick rows that tighten toward the horizon.
  G.paintRoad = function (g, rd, p, rnd) {
    var W = this.W, H = this.H, hz = rd.hz, N = 64, i, j, t;
    var yAt = function (tt) { return hz + (H - hz) * tt; };
    var roadC = rgbOf(p.road), deepC = rgbOf(p.roadDeep), lineC = rgbOf(p.roadLine);
    var ribbon = function (frac) {
      g.beginPath();
      for (i = 0; i <= N; i++) { t = i / N; g.lineTo(rd.cx(t) - rd.hw(t) * frac, yAt(t)); }
      for (i = N; i >= 0; i--) { t = i / N; g.lineTo(rd.cx(t) + rd.hw(t) * frac, yAt(t)); }
      g.closePath();
    };
    ribbon(1);
    var fill = g.createLinearGradient(0, hz, 0, H);
    fill.addColorStop(0, css(mixHex(p.road, p.haze, 0.6))); fill.addColorStop(0.2, css(mixHex(p.road, p.haze, 0.25)));
    fill.addColorStop(0.55, css(roadC)); fill.addColorStop(1, css(deepC));
    g.fillStyle = fill; g.fill();
    g.save();
    g.clip();
    // Crown: light down the middle, shade along both edges
    ribbon(0.5);
    var crown = g.createLinearGradient(0, hz, 0, H);
    crown.addColorStop(0, 'rgba(255,248,214,0.05)'); crown.addColorStop(1, 'rgba(255,248,214,0.2)');
    g.fillStyle = crown; g.fill();
    for (var side = -1; side <= 1; side += 2) {
      for (i = 1; i <= N; i++) {
        t = i / N; var t0 = (i - 1) / N, t1 = Math.min(1, t + 0.4 / N);
        g.fillStyle = css(lineC, 0.06 + 0.16 * t);
        g.beginPath();
        g.moveTo(rd.cx(t0) + side * rd.hw(t0), yAt(t0)); g.lineTo(rd.cx(t0) + side * rd.hw(t0) * 0.82, yAt(t0));
        g.lineTo(rd.cx(t1) + side * rd.hw(t1) * 0.82, yAt(t1)); g.lineTo(rd.cx(t1) + side * rd.hw(t1), yAt(t1));
        g.closePath(); g.fill();
      }
    }
    // Brick rows, from the front back to where they are too small to see
    t = 1;
    var row = 0;
    while (t > 0.002 && row < 80) {
      var hw = rd.hw(t), y = yAt(t), cx = rd.cx(t);
      var rowH = Math.max(1.5, hw * 0.17);
      if (rowH < 2.4) break;
      var tn = Math.max(0, t - rowH / (H - hz)), yn = yAt(tn), cxn = rd.cx(tn), hwn = rd.hw(tn);
      var depth = 0.35 + 0.65 * t;
      g.lineWidth = Math.max(0.6, rowH * 0.1);
      g.strokeStyle = css(lineC, 0.08 + 0.18 * depth);
      g.beginPath(); g.moveTo(cx - hw - 3, y); g.quadraticCurveTo(cx, y - rowH * 0.16, cx + hw + 3, y); g.stroke();
      g.strokeStyle = 'rgba(255,246,205,' + (0.05 + 0.12 * depth).toFixed(3) + ')';
      var lip = rowH * 0.1 + g.lineWidth;
      g.beginPath(); g.moveTo(cx - hw - 3, y + lip); g.quadraticCurveTo(cx, y - rowH * 0.16 + lip, cx + hw + 3, y + lip); g.stroke();
      var bw = rowH * 2.1, off = ((row % 2) * 0.5 + 0.25) * bw, nb = Math.ceil(hw * 2 / bw) + 2;
      g.lineWidth = Math.max(0.5, rowH * 0.08);
      g.strokeStyle = css(lineC, 0.06 + 0.14 * depth);
      g.beginPath();
      for (j = 0; j < nb; j++) {
        var bx = cx - hw + off + j * bw;
        if (bx <= cx - hw || bx >= cx + hw) continue;
        g.moveTo(bx, y); g.lineTo(cxn + (bx - cx) / hw * hwn, yn);
      }
      g.stroke();
      if (rowH > 5) {
        var picks = 1 + ((rnd() * 3) | 0);
        for (j = 0; j < picks; j++) {
          var jj = (rnd() * nb) | 0, bx0 = cx - hw + off + jj * bw, bx1 = bx0 + bw;
          if (bx0 < cx - hw) bx0 = cx - hw;
          if (bx1 > cx + hw) bx1 = cx + hw;
          if (bx1 - bx0 < 2) continue;
          g.fillStyle = rnd() < 0.5 ? 'rgba(255,240,190,0.16)' : css(deepC, 0.18);
          g.beginPath(); g.moveTo(bx0, y); g.lineTo(bx1, y); g.lineTo(cxn + (bx1 - cx) / hw * hwn, yn); g.lineTo(cxn + (bx0 - cx) / hw * hwn, yn); g.closePath(); g.fill();
        }
      }
      t = tn; row++;
    }
    g.restore();
    // Edges: a dark rim on each side so the road sits into the grass
    for (side = -1; side <= 1; side += 2) {
      g.strokeStyle = css(lineC, 0.35); g.lineWidth = 1.2;
      g.beginPath();
      for (i = 0; i <= N; i++) { t = i / N; g.lineTo(rd.cx(t) + side * rd.hw(t), yAt(t)); }
      g.stroke();
    }
  };

  // Seeds to show during the Seeds phase: one per box of the largest milestone (500 by default).
  G.seedTarget = function () {
    var ms = this.milestones, n = ms.length ? ms[ms.length - 1].count : 500;
    var cfg = num((this.data || {}).seedCount, 0);
    if (cfg > 0) n = cfg;
    return Math.max(40, Math.min(900, Math.round(n)));
  };

  // One seed marker in the still soil layer: a small mound with a lit rim and the seed itself.
  G.paintSeed = function (g, sd) {
    var z = (this.W < 600 ? 7 : 9) * sd.s * Math.min(1.4, Math.max(0.8, this.W / 1200));
    g.fillStyle = 'rgba(52,38,28,0.55)';
    g.beginPath(); g.ellipse(sd.x, sd.y + z * 0.08, z * 0.62, z * 0.2, 0, 0, TAU); g.fill();
    g.fillStyle = 'rgba(132,104,80,0.9)';
    g.beginPath(); g.ellipse(sd.x, sd.y, z * 0.5, z * 0.15, 0, Math.PI, TAU); g.fill();
    g.save();
    g.translate(sd.x, sd.y - z * 0.08);
    g.rotate(-0.45 + sd.ph * 0.12);
    var sg = g.createLinearGradient(-z * 0.16, 0, z * 0.16, 0);
    sg.addColorStop(0, '#F6E7C8'); sg.addColorStop(1, '#B8905F');
    g.fillStyle = sg;
    g.beginPath(); g.ellipse(0, 0, z * 0.17, z * 0.095, 0, 0, TAU); g.fill();
    g.restore();
    if (sd.tip) {
      var gh = z * 0.34;
      g.strokeStyle = '#7FA866'; g.lineWidth = Math.max(0.8, z * 0.07); g.lineCap = 'round';
      g.beginPath(); g.moveTo(sd.x + z * 0.05, sd.y - z * 0.1); g.quadraticCurveTo(sd.x + z * 0.1, sd.y - gh * 0.6, sd.x + z * 0.02, sd.y - gh); g.stroke();
    }
  };

  /* ---------- bitmap cache ---------- */
  G.bitmap = function (f, step) {
    var box = f.box, key = this.key(f, step);
    var e = this.cache.get(key);
    if (e) return e;
    if (now() > this.budgetEnd && this.builtThisFrame > 0) return null;
    this.builtThisFrame++;
    e = this.build(box, f.ci, f.bucket, step, f.vr);
    this.cache.set(key, e);
    this.stats.bitmaps = this.cache.size;
    return e;
  };
  G.key = function (f, step) { return f.box.key + '|' + f.ci + '|' + f.bucket + '|' + f.vr + '|' + step; };
  G.build = function (box, ci, bucket, step, vr) {
    var BF = w.BloomFlowers, dpr = this.dpr, col = box.colors[ci] || box.colors[0];
    var S = this.sizeAt((bucket + 0.5) / DEPTHS) * box.sp.height * box.scale;
    var B = BF.bounds({ species: box.species, size: S });
    // Draw on a scratch canvas, then copy only the painted pixels into the cached bitmap.
    var cv = this.scratch || (this.scratch = d.createElement('canvas'));
    var cw = cv.width = Math.max(1, Math.ceil(B.width * dpr) + 2);
    var ch = cv.height = Math.max(1, Math.ceil(B.height * dpr) + 2);
    var ox = Math.round(-B.left * dpr) + 1, oy = Math.round(-B.top * dpr) + 1;
    var c = this.scratchCtx || (this.scratchCtx = cv.getContext('2d', { willReadFrequently: true }));
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.clearRect(0, 0, cw, ch);
    c.setTransform(dpr, 0, 0, dpr, ox, oy);
    var geo = { hx: 0, hy: 0, hr: 0 };
    try {
      geo = BF.drawFlower(c, {
        species: box.species, color: col.color, accent: col.accent, alpha: col.alpha, glitter: box.glitter,
        growth: step / GROW_STEPS, sway: 0, seed: hash(this.seed, box.hk, ci, bucket, vr || 0), size: S,
        time: null, image: box.img, glow: 0
      }) || geo;
    } catch (err) { debug('draw failed', err); }
    c.setTransform(1, 0, 0, 1, 0, 0);
    this.light(c, cv, cw, ch, ox, oy, S, geo);
    // Depth haze toward the back, and the phase tint (golden hour, pressed paper).
    var p = this.pal, haze = 0.42 * Math.pow(1 - (bucket + 0.5) / DEPTHS, 1.5);
    c.globalCompositeOperation = 'source-atop';
    if (haze > 0.01) { c.fillStyle = css(rgbOf(p.haze), haze); c.fillRect(0, 0, cv.width, cv.height); }
    if (p.tint && p.tintAmount) { c.fillStyle = css(rgbOf(p.tint), p.tintAmount); c.fillRect(0, 0, cv.width, cv.height); }
    c.globalCompositeOperation = 'source-over';
    var x0 = 0, y0 = 0, x1 = cw - 1, y1 = ch - 1;
    try {
      var px = c.getImageData(0, 0, cw, ch).data, x, y, row;
      x0 = cw; x1 = -1; y0 = ch; y1 = -1;
      for (y = 0; y < ch; y++) {
        row = y * cw * 4;
        for (x = 0; x < cw; x++) {
          if (px[row + x * 4 + 3] > 6) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; y1 = y; }
        }
      }
      if (x1 < 0) { x0 = y0 = 0; x1 = y1 = 0; }
    } catch (e) { x0 = 0; y0 = 0; x1 = cw - 1; y1 = ch - 1; }
    var out = d.createElement('canvas');
    out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
    out.getContext('2d').drawImage(cv, -x0, -y0);
    return { cv: out, ox: ox - x0, oy: oy - y0, hx: geo.hx, hy: geo.hy, hr: geo.hr, res: dpr };
  };
  // Lights one flower in its bitmap (Oct 23), once, when the bitmap is built, so it costs nothing
  // per frame. The sun sits behind and above the meadow, so a bright rim runs along the top edge
  // of every petal, leaf and stem (the flower's silhouette minus itself moved down a pixel or
  // two). A soft gloss rounds the bloom and the foot of the stem falls into the shade of the
  // grass. Everything is painted source-atop, so the flower keeps its exact outline and the
  // translucent shades keep their alpha. The rim is symmetric, so mirrored flowers match.
  G.light = function (c, cv, cw, ch, ox, oy, S, geo) {
    var mg = this.magic || MAGIC[this.phase] || MAGIC.bloom, dpr = this.dpr;
    if (mg.rimA > 0) {
      var rc = this.rimCv || (this.rimCv = d.createElement('canvas'));
      rc.width = cw; rc.height = ch;
      var r = rc.getContext('2d');
      r.drawImage(cv, 0, 0);
      r.globalCompositeOperation = 'source-in';
      r.fillStyle = mg.rim; r.fillRect(0, 0, cw, ch);
      r.globalCompositeOperation = 'destination-out';
      r.drawImage(cv, 0, Math.max(1, Math.min(4, Math.round(S * dpr * 0.011))));
      r.globalCompositeOperation = 'source-over';
      c.globalCompositeOperation = 'source-atop';
      c.globalAlpha = mg.rimA;
      c.drawImage(rc, 0, 0);
      c.globalAlpha = 1;
    }
    c.globalCompositeOperation = 'source-atop';
    if (mg.sheenA > 0 && geo && geo.hr > 0) {
      var hx = ox + geo.hx * dpr, hy = oy + (geo.hy - geo.hr * 0.4) * dpr, hr = geo.hr * dpr * 1.3;
      var sh = c.createRadialGradient(hx, hy, 0, hx, hy, hr);
      sh.addColorStop(0, 'rgba(255,252,240,' + mg.sheenA + ')'); sh.addColorStop(1, 'rgba(255,252,240,0)');
      c.fillStyle = sh; c.fillRect(hx - hr, hy - hr, hr * 2, hr * 2);
    }
    if (mg.shadeA > 0) {
      var top = oy - S * 0.42 * dpr, sg = c.createLinearGradient(0, top, 0, oy + 2);
      sg.addColorStop(0, 'rgba(18,32,16,0)'); sg.addColorStop(1, 'rgba(18,32,16,' + mg.shadeA + ')');
      c.fillStyle = sg; c.fillRect(0, top, cw, oy + 2 - top);
    }
    c.globalCompositeOperation = 'source-over';
  };

  // Dense meadows: back rows (tiny sway) are baked once into one layer with the scenery,
  // so each frame draws one image plus the front rows and any animating flowers.
  G.bake = function (t) {
    var n = this.all.length;
    var split = this.reduced ? DEPTHS : (n >= 1100 ? 5 : n >= 600 ? 4 : n >= 260 ? 3 : 0);
    // Lower quality levels let fewer rows sway: more of the meadow is baked into the still layer.
    if (!this.reduced && this.quality) split = Math.min(DEPTHS - 1, Math.max(split, n >= 60 ? 3 + this.quality : 0));
    if (split !== this.split) { this.split = split; this.layerDirty = true; this.forceBake = true; }
    if (!split) {
      if (this.layerReady) { this.layerReady = false; for (var i = 0; i < n; i++) this.all[i].baked = false; }
      return;
    }
    if (!this.layerDirty) return;
    var clock = now(); // real time: t is frozen while motion is reduced
    if (!this.forceBake && this.layerReady && clock - this.lastBake < 450) { this.pending = true; return; }
    var L = this.layer || (this.layer = d.createElement('canvas'));
    if (L.width !== this.bg.width || L.height !== this.bg.height) { L.width = this.bg.width; L.height = this.bg.height; }
    var c = L.getContext('2d'), dpr = this.dpr;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.globalAlpha = 1;
    c.drawImage(this.bg, 0, 0);
    for (i = 0; i < n; i++) {
      var f = this.all[i];
      f.baked = false;
      if (f.bucket >= split || f.mode !== 0 || f.mine || (!this.reduced && f.glowEnd > t)) continue;
      var b = this.cache.get(this.key(f, GROW_STEPS));
      if (!b) continue;
      c.setTransform(f.k * f.fx, 0, 0, f.k, f.x * dpr, f.y * dpr);
      c.drawImage(b.cv, -b.ox, -b.oy);
      f.baked = true; f.last = b; f.push = 0;
    }
    this.layerDirty = false; this.forceBake = false; this.layerReady = true; this.lastBake = clock;
  };

  G.unbake = function (dropLast) {
    for (var i = 0; i < this.all.length; i++) { this.all[i].baked = false; if (dropLast) this.all[i].last = null; }
    this.layerReady = false; this.layerDirty = true; this.forceBake = true;
  };

  G.trimCache = function () {
    var self = this;
    var full = '|' + GROW_STEPS;
    this.cache.forEach(function (v, k) { if (k.slice(-full.length) !== full) self.cache.delete(k); });
  };

  G.loadImages = function () {
    var self = this, BF = w.BloomFlowers;
    var want = this.boxes.some(function (b) { return b.imageUrl; });
    if (!want || !BF || !BF.loadImages) return;
    var p = BF.loadImages(this.cfg);
    if (!p || !p.then) return;
    p.then(function (map) {
      if (self.destroyed) return;
      self.boxes.forEach(function (b) { if (b.imageUrl && map.get(b.imageUrl)) b.img = map.get(b.imageUrl); });
      self.cache.clear();
      self.unbake();
      self.all.forEach(function (f) { self.place(f); });
      self.kick();
    });
  };

  /* ---------- live connection (SPEC 3.4) ---------- */
  G.startLive = function () {
    var svc = this.data.service || {};
    this.url = String(svc.url || '').trim().replace(/\/+$/, '');
    this.pollMs = Math.max(10, num(svc.pollSeconds, 30)) * 1000;
    if (!this.url) return;
    this.openStream();
  };
  G.openStream = function () {
    var self = this;
    if (this.destroyed || !this.url) return;
    if (!('EventSource' in w)) { this.startPolling(); return; }
    var es;
    try { es = new EventSource(this.url + '/stream'); } catch (e) { debug('stream unavailable', e); this.startPolling(); return; }
    this.es = es;
    var parse = function (e) { try { return JSON.parse(e.data); } catch (x) { return null; } };
    es.addEventListener('open', function () { self.stopPolling(); });
    // A snapshot can arrive at any time (the server re-sends one after an admin backfill).
    es.addEventListener('snapshot', function (e) { self.stopPolling(); self.applySnapshot(parse(e), now() - self.loadedAt > 6000); });
    var one = function (e) {
      var m = parse(e);
      if (!m || !m.box) return;
      self.setServer(m.box, m.count, true);
      if (e.type === 'planted') self.setRecent(m, false);
      self.afterChange();
    };
    es.addEventListener('planted', one);
    es.addEventListener('removed', one);
    // A shopper named (or unnamed) the flowers of an order; Lauren hiding a name sends "" too.
    es.addEventListener('named', function (e) { self.applyNamed(parse(e)); });
    es.onerror = function () {
      debug('stream closed, polling instead');
      try { es.close(); } catch (x) { /* ignore */ }
      if (self.es === es) self.es = null;
      self.startPolling();
      if (!self.retryTimer) {
        self.retryTimer = self.later(STREAM_RETRY_MS, function () { self.retryTimer = 0; self.openStream(); });
      }
    };
  };
  G.startPolling = function () {
    var self = this;
    if (this.pollTimer || this.destroyed || !this.url) return;
    var poll = function () {
      if (!w.fetch) return;
      fetch(self.url + '/counts', { cache: 'no-store', credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (body) { if (body && !self.destroyed) self.applySnapshot(body, now() - self.loadedAt > 6000); })
        .catch(function (e) { debug('counts unavailable', e && e.message); });
    };
    poll();
    this.pollTimer = setInterval(poll, this.pollMs);
    this.timers.push(this.pollTimer);
  };
  G.stopLive = function () {
    this.url = '';
    if (this.es) { try { this.es.close(); } catch (e) { /* ignore */ } this.es = null; }
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = 0; }
    this.stopPolling();
  };
  G.stopPolling = function () {
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = 0; }
  };

  /* ---------- animation loop ---------- */
  G.kick = function () {
    var self = this;
    if (this.raf || this.destroyed || !this.ctx) return;
    if (d.hidden || !this.visible) return;
    this.raf = requestAnimationFrame(function (t) {
      self.raf = 0;
      try { self.frame(t); } catch (e) { debug('frame error', e); return; }
      if (!self.reduced || self.pending) self.kick();
    });
  };

  G.frame = function (t) {
    var start = now();
    this.budgetEnd = start + (this.reduced ? 14 : (t - this.loadedAt < 1500 ? 10 : 6));
    this.builtThisFrame = 0;
    this.pending = false;
    if (this.reduced) t = this.loadedAt + 1e5;
    var ctx = this.ctx, dpr = this.dpr, W = this.W, H = this.H;
    if (!W || !this.bg) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    if (this.phase === 'tease') {
      ctx.drawImage(this.bg, 0, 0);
      this.drawCityStars(t);
      this.drawGlints(t);
      this.drawSeeds(t);
    } else {
      this.bake(t);
      ctx.drawImage(this.layerReady ? this.layer : this.bg, 0, 0);
      this.drawCityStars(t);
      this.drawGlints(t);
      this.drawFlowers(t);
      if (this.layerDirty) this.pending = true;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    if (this.fg) ctx.drawImage(this.fg, 0, Math.round((H - this.fgH) * dpr));
    if (this.phase === 'full' && !this.reduced) this.drawPetals(t);
    this.drawAir(t);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    if (this.pointer && this.pointer.touch === false) this.hover(false);
    if (this.pinOn) this.placePin();

    // Adaptive quality: if frames stay slower than about 22 ms for 2 s, render lighter.
    if (!this.reduced) {
      var dt = this.lastT ? t - this.lastT : 16;
      this.lastT = t;
      if (dt > 0 && dt < 250 && !this.pending) {
        this.dtAvg += (dt - this.dtAvg) * 0.1;
        if (this.dtAvg > 22 && this.quality < 2) {
          if (!this.slowSince) this.slowSince = t;
          else if (t - this.slowSince > 2000) this.setQuality(this.quality + 1);
        } else this.slowSince = 0;
      } else if (dt >= 250) this.slowSince = 0;
    }

    // stats
    this.frames++;
    this.costSum += now() - start;
    if (t - this.fpsAt >= 1000) {
      this.stats.fps = Math.round(this.frames * 1000 / (t - this.fpsAt));
      this.stats.frameMs = +(this.costSum / this.frames).toFixed(2);
      this.frames = 0; this.costSum = 0; this.fpsAt = t;
    }
  };

  G.setQuality = function (q) {
    this.quality = Math.max(0, Math.min(2, q));
    this.stats.quality = this.quality;
    this.root.classList.toggle('flower-box-sale--lite', this.quality > 1); // the lightest level drops the sun's rays
    this.slowSince = 0; this.dtAvg = 16;
    this.W = 0; // force a relayout at the new render scale
    this.layerDirty = true; this.forceBake = true;
    this.layout();
  };

  G.drawFlowers = function (t) {
    var ctx = this.ctx, dpr = this.dpr, all = this.all, reduced = this.reduced;
    var p = this.pointer, pushR = Math.max(70, this.unit * 0.55);
    var calm = this.phase === 'pressed' ? 0.35 : 1;
    var glow = glowSprite(), spark = sparkSprite();
    var growing = false;
    var split = this.layerReady ? this.split : 0;
    // A tap on the phase pill sends a short gust through the meadow.
    var gust = (this.gustEnd && this.gustEnd > t) ? (this.gustEnd - t) / GUST_MS : 0;
    // The shopper's own flowers are drawn last, in front of every neighbour, halo and all.
    var order = all;
    if (this.mineList.length) {
      var rest = [], mine = [];
      for (var m = 0; m < all.length; m++) (all[m].mine && all[m].mode !== 3 ? mine : rest).push(all[m]);
      order = rest.concat(mine);
    }
    for (var i = 0; i < order.length; i++) {
      var f = order[i], step = GROW_STEPS, alpha = 1, sc = f.k, gl = 0;
      if (f.baked) continue;
      if (f.bucket < split && f.mode === 0 && !f.mine && (reduced || f.glowEnd <= t)) this.layerDirty = true;
      if (!reduced && f.mode) {
        var q = (t - f.t0);
        if (f.mode === 1) {
          if (q < 0) continue;
          growing = true;
          var pr = q / SPROUT_MS;
          if (pr < 1) { step = Math.max(1, Math.round(pr * GROW_STEPS)); gl = Math.min(1, q / 500); }
          else { f.mode = 0; f.glowEnd = f.t0 + SPROUT_MS + GLOW_MS; }
        } else if (f.mode === 2) {
          if (!f.ready) {
            if (!this.cache.get(this.key(f, GROW_STEPS))) {
              if (!this.bitmap(f, GROW_STEPS)) { this.pending = true; continue; }
            }
            f.ready = true;
            if (f.t0 < t) f.t0 = t;
            q = t - f.t0;
          }
          if (q < 0) continue;
          var a = q / SETTLE_MS;
          if (a >= 1) f.mode = 0;
          else { alpha = a; sc *= 0.86 + 0.14 * easeOut(a); }
        } else if (f.mode === 3) {
          var lq = q / LEAVE_MS;
          if (lq >= 1) { this.remove(f); if (order === all) i--; continue; }
          alpha = 1 - lq; sc *= 1 - 0.35 * lq;
        }
      } else if (reduced && f.mode === 3) { this.remove(f); if (order === all) i--; continue; }
      if (!reduced && f.glowEnd > t) gl = Math.max(gl, (f.glowEnd - t) / GLOW_MS);

      var b = this.bitmap(f, step);
      if (!b) { b = f.last; this.pending = true; if (!b) continue; }
      f.last = b;
      var res = b.res || dpr;
      if (res !== dpr) sc *= dpr / res; // bitmap from an earlier render scale

      // Wind plus a gentle push away from the pointer.
      var sway = 0;
      if (!reduced) {
        sway = calm * f.amp * (0.6 * Math.sin(t * 0.0011 + f.ph) + 0.3 * Math.sin(t * 0.00047 - f.x * 0.004) + 0.1 * Math.sin(t * 0.0029 + f.ph * 2));
        if (gust) sway += gust * gust * 0.14 * Math.sin(t * 0.011 + f.ph * 3 + f.x * 0.015);
        var target = 0;
        if (p) {
          var dx = f.x - p.x, dy = (f.y - f.reach * 0.75) - p.y, dd = dx * dx + dy * dy;
          if (dd < pushR * pushR) target = (dx >= 0 ? 1 : -1) * (1 - Math.sqrt(dd) / pushR) * 0.32;
        }
        if (target || f.push) {
          f.push += (target - f.push) * 0.12;
          if (Math.abs(f.push) < 0.0005 && !target) f.push = 0;
        }
        sway += f.push;
      }
      var cs = Math.cos(sway) * sc, sn = Math.sin(sway) * sc, fxp = f.x;
      if (f.mine) { if (f.nudge == null) this.headOf(f); fxp += f.nudge || 0; }
      ctx.setTransform(cs * f.fx, sn * f.fx, -sn, cs, fxp * dpr, f.y * dpr);
      if (f.mine && b.hr) { // the shopper's own flower: a slow, soft halo behind the bloom
        var hk = this.haloAt(f, t);
        if (hk > 0.01) {
          // at least about 34 css px across, so a flower far back in the meadow still shows its halo
          var hr2 = Math.max(b.hr * res * 4, 34 * dpr / Math.max(0.05, sc));
          ctx.globalAlpha = hk * alpha;
          ctx.drawImage(glow, b.hx * res - hr2, b.hy * res - hr2, hr2 * 2, hr2 * 2);
        }
      }
      if (gl > 0.01 && b.hr) {
        var gr = b.hr * res * 2.6;
        ctx.globalAlpha = gl * 0.9;
        ctx.drawImage(glow, b.hx * res - gr, b.hy * res - gr, gr * 2, gr * 2);
      }
      ctx.globalAlpha = alpha;
      ctx.drawImage(b.cv, -b.ox, -b.oy);
      if (f.box.glitter && !reduced && b.hr * sc > 3) {
        for (var s = 0; s < 2; s++) {
          var tw = Math.sin(t * 0.0032 + f.ph * (3 + s * 4) + s * 2.1);
          if (tw > 0.55) {
            var k = (tw - 0.55) / 0.45, ss = b.hr * res * 0.55 * k;
            var ang = f.ph * (5 + s * 3);
            ctx.globalAlpha = alpha * k;
            ctx.drawImage(spark, (b.hx + Math.cos(ang) * b.hr * 0.6) * res - ss / 2, (b.hy + Math.sin(ang) * b.hr * 0.4) * res - ss / 2, ss, ss);
          }
        }
      }
    }
    ctx.globalAlpha = 1;
    if (!growing && this.hadGrowing) this.trimCache();
    this.hadGrowing = growing;
  };

  G.drawSeeds = function (t) {
    var ctx = this.ctx, dpr = this.dpr, still = this.reduced, W = this.W;
    var list = this.seeds, n = list.length;
    if (!n) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    var scale = Math.min(1.4, Math.max(0.8, W / 1200)), base = (W < 600 ? 7 : 9) * scale;
    // Lighter quality levels glow every second or third seed only.
    var stride = 1 + this.quality;
    var glow = glowSprite();
    // About two seeds in five glow, each on its own slow breath, so the light is scattered
    // through the soil rather than laid out in a grid.
    for (var i = 0; i < n; i += stride) {
      var sd = list[i], z = base * sd.s;
      if (!sd.glow) continue;
      var pulse = still ? 0.5 : (Math.sin(t / 1600 + sd.ph) + 1) / 2;
      ctx.globalAlpha = (0.1 + 0.32 * pulse * pulse) * Math.min(1, sd.s + 0.3);
      ctx.drawImage(glow, sd.x - z * 1.3, sd.y - z, z * 2.6, z * 2);
    }
    if (!still) {
      // About one seed in twelve carries a slow, soft ring, staggered so a few are alive at once.
      ctx.strokeStyle = 'rgba(255,240,205,1)';
      ctx.lineWidth = 1;
      for (i = 0; i < n; i += 12) {
        sd = list[i]; z = base * sd.s;
        var ring = ((t / 3800 + sd.ph / TAU) % 1);
        ctx.globalAlpha = (1 - ring) * 0.5 * Math.min(1, sd.s + 0.2);
        ctx.beginPath(); ctx.ellipse(sd.x, sd.y, z * (0.5 + ring * 1.6), z * (0.16 + ring * 0.55), 0, 0, TAU); ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  };

  G.drawPetals = function (t) {
    var ctx = this.ctx, dpr = this.dpr, W = this.W, H = this.H, list = this.petals;
    var want = (W < 600 ? 18 : 34) >> (this.quality > 1 ? 1 : 0);
    if (list.length < want) {
      var cols = [];
      this.boxes.forEach(function (b) { b.colors.forEach(function (c) { var q = rgbOf(c.color), hi = Math.max(q[0], q[1], q[2]), lo = Math.min(q[0], q[1], q[2]); if (c.alpha > 0.6 && (0.299 * q[0] + 0.587 * q[1] + 0.114 * q[2]) > 120 && (hi - lo > 34 || lo > 228)) cols.push(c.color); }); });
      if (!cols.length) cols = ['#F0B9C4', '#E99A8C', '#F2B5B5'];
      while (list.length < want) {
        var c = cols[(Math.random() * cols.length) | 0];
        list.push({
          x: Math.random() * W, y: (Math.random() * 1.1 - 0.15) * H, vy: 14 + Math.random() * 22, vx: 6 + Math.random() * 14,
          r: Math.random() * TAU, vr: (Math.random() - 0.5) * 1.6, s: 7 + Math.random() * 8, ph: Math.random() * TAU,
          spr: this.petalSprite(c)
        });
      }
    }
    var dt = Math.min(0.05, (t - (this.lastPetalT || t)) / 1000);
    this.lastPetalT = t;
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      p.y += p.vy * dt; p.x += (p.vx + Math.sin(t / 900 + p.ph) * 12) * dt; p.r += p.vr * dt;
      if (p.y > H + 12 || p.x > W + 20) { p.y = -12; p.x = Math.random() * W * 1.1 - W * 0.1; }
      if (p.y < -10) continue;
      var flipY = Math.sin(t / 700 + p.ph), cs = Math.cos(p.r), sn = Math.sin(p.r), k = p.s / 32 * dpr;
      ctx.setTransform(cs * k, sn * k, -sn * k * flipY, cs * k * flipY, p.x * dpr, p.y * dpr);
      ctx.globalAlpha = 0.85 * c01((H - p.y) / (H * 0.25));
      ctx.drawImage(p.spr, -16, -16);
    }
  };
  // A falling petal: deeper at its base, lighter and lit gold at the tip, with a faint midrib and
  // a soft sheen, so it reads as silk catching the low sun as it turns.
  G.petalSprite = function (hex) {
    return sprite('petal' + hex, 32, function (g) {
      var c = rgbOf(hex);
      var shape = function () { g.beginPath(); g.moveTo(16, 30); g.bezierCurveTo(4, 24, 4, 6, 12, 3); g.quadraticCurveTo(16, 5, 20, 3); g.bezierCurveTo(28, 6, 28, 24, 16, 30); };
      var gr = g.createLinearGradient(16, 30, 16, 2);
      gr.addColorStop(0, css([c[0] * 0.72, c[1] * 0.66, c[2] * 0.7]));
      gr.addColorStop(0.55, css(c));
      gr.addColorStop(1, css([lerp(c[0], 255, 0.45), lerp(c[1], 244, 0.42), lerp(c[2], 214, 0.4)]));
      shape(); g.fillStyle = gr; g.fill();
      g.save(); shape(); g.clip();
      var sh = g.createRadialGradient(11, 11, 0, 11, 11, 13);
      sh.addColorStop(0, 'rgba(255,250,236,0.55)'); sh.addColorStop(1, 'rgba(255,250,236,0)');
      g.fillStyle = sh; g.fillRect(0, 0, 32, 32);
      g.strokeStyle = css([c[0] * 0.8, c[1] * 0.74, c[2] * 0.78], 0.35); g.lineWidth = 0.8;
      g.beginPath(); g.moveTo(16, 28); g.quadraticCurveTo(15, 16, 16, 6); g.stroke();
      g.restore();
      g.strokeStyle = 'rgba(255,236,180,0.6)'; g.lineWidth = 0.9;
      g.beginPath(); g.moveTo(12, 3.4); g.quadraticCurveTo(16, 5.4, 20, 3.4); g.stroke();
    });
  };

  /* ---------- the city lights and the bloom burst ---------- */
  // Twinkles on the city spires, drawn over the still scenery each frame (cheap: a few sprites).
  G.drawCityStars = function (t) {
    var list = this.cityStars;
    if (!list || !list.length || this.reduced || this.phase === 'pressed') return;
    var ctx = this.ctx, dpr = this.dpr, spark = sparkSprite(), base = this.W < 600 ? 7 : 11;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (var i = 0; i < list.length; i++) {
      var s = list[i], tw = Math.sin(t * 0.0025 + s.ph);
      if (tw < 0.2) continue;
      var k = (tw - 0.2) / 0.8, sz = base * s.s * k;
      ctx.globalAlpha = 0.9 * k;
      ctx.drawImage(spark, s.x - sz / 2, s.y - sz / 2, sz, sz);
    }
    ctx.globalAlpha = 1;
  };

  // Glints on the yellow brick road: tiny stars that wink on the bricks, placed in paintLight.
  G.drawGlints = function (t) {
    var list = this.glints;
    if (!list || !list.length || this.reduced) return;
    var ctx = this.ctx, dpr = this.dpr, spark = sparkSprite(), base = this.W < 600 ? 6 : 9;
    var step = this.quality > 1 ? 2 : 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (var i = 0; i < list.length; i += step) {
      var g = list[i], tw = Math.sin(t * 0.0021 * g.sp + g.ph);
      if (tw < 0.45) continue;
      var k = (tw - 0.45) / 0.55, sz = base * g.s * k;
      ctx.globalAlpha = 0.95 * k;
      ctx.drawImage(spark, g.x - sz / 2, g.y - sz / 2, sz, sz);
    }
    ctx.globalAlpha = 1;
  };

  // What drifts through the air (Oct 23). Positions follow the clock directly, so the motion is
  // smooth whatever the frame rate and a tab coming back from the background simply carries on.
  G.setupAir = function () {
    var mg = this.magic || MAGIC.bloom, phone = this.W < 600, i, list = [], bf = [];
    var r = (function (s) { return function () { s = (s * 16807) % 2147483647; return s / 2147483647; }; })((this.seed + 911) % 2147483646 + 1);
    var n = mg.air === 'fireflies' ? (phone ? 14 : 26) : mg.air === 'pollen' ? (phone ? 16 : 30) : mg.air === 'gold' ? (phone ? 20 : 36) : 0;
    for (i = 0; i < n; i++) list.push({ x: r(), y: r(), s: r(), ph: r() * TAU, sp: 0.6 + r() * 0.8, dx: r() - 0.5, big: r() < (mg.air === 'gold' ? 0.4 : 0.12) });
    if (mg.butterflies) {
      for (i = 0; i < (phone ? 2 : 3); i++) bf.push({ u: r(), v: 0.12 + r() * 0.5, ph: r() * TAU, sp: 0.75 + r() * 0.5, col: BUTTERFLIES[(i * 2 + ((r() * 5) | 0)) % BUTTERFLIES.length], dir: i % 2 ? -1 : 1, s: 0.85 + r() * 0.4 });
    }
    this.air = list;
    this.flutter = bf;
    this.airW = this.W; this.airH = this.H;
  };
  G.drawAir = function (t) {
    if (this.reduced || !this.magic || !this.W) return;
    if (!this.air || this.airW !== this.W || this.airH !== this.H) this.setupAir();
    var ctx = this.ctx, dpr = this.dpr, W = this.W, H = this.H, mg = this.magic, list = this.air, phone = W < 600;
    var step = this.quality ? 2 : 1, i, m, x, y, z, tt;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'lighter';
    if (mg.air === 'fireflies') {
      // Fireflies over the sown field: each wanders on its own slow loop and glows, fades, glows.
      var ff = fireflySprite(), gTop = this.groundTop;
      for (i = 0; i < list.length; i += step) {
        m = list[i]; tt = t * 0.001 * m.sp;
        var blink = Math.sin(tt * 1.7 + m.ph * 3);
        if (blink < -0.1) continue;
        x = (m.x * 1.1 - 0.05) * W + Math.sin(tt * 0.6 + m.ph) * W * 0.035 + Math.sin(tt * 2.1 + m.ph * 2) * 6;
        y = lerp(gTop - H * 0.05, H * 0.94, m.y) + Math.sin(tt * 0.8 + m.ph * 1.3) * H * 0.03;
        z = (phone ? 12 : 18) * (0.55 + m.s * 0.7) * (0.55 + 0.6 * m.y);
        ctx.globalAlpha = Math.pow((blink + 0.1) / 1.1, 1.4);
        ctx.drawImage(ff, x - z, y - z, z * 2, z * 2);
      }
      this.drawShootingStar(t);
    } else if (mg.air) {
      // Pollen by day, golden motes and soft bokeh at golden hour: rising slowly through the light,
      // drifting sideways, twinkling, and fading in and out at the bottom and the top.
      var gold = mg.air === 'gold', speck = moteSprite(gold), bok = bokehSprite();
      for (i = 0; i < list.length; i += step) {
        m = list[i];
        var cyc = (m.y + t * 0.0000115 * m.sp * (gold ? 0.8 : 1.3)) % 1;
        y = H * (1.04 - cyc * 1.12);
        x = (m.x + Math.sin(t * 0.00035 * m.sp + m.ph) * 0.035 + m.dx * cyc * 0.14) * W;
        var tw = 0.55 + 0.45 * Math.sin(t * 0.0019 * m.sp + m.ph), fade = c01(cyc * 6) * c01((1 - cyc) * 5);
        if (m.big && gold) {
          // the soft orbs stay low, in the warm air over the flowers, and thin out toward the sky
          z = (phone ? 9 : 15) * (0.6 + m.s * 0.9);
          ctx.globalAlpha = 0.5 * fade * (0.6 + 0.4 * tw) * c01((y - this.horizon * 0.7) / (this.horizon * 0.6));
          ctx.drawImage(bok, x - z, y - z, z * 2, z * 2);
        } else {
          z = (m.big ? (phone ? 4 : 6) : (phone ? 2.4 : 3.4)) * (0.6 + m.s * 0.8);
          ctx.globalAlpha = (m.big ? 0.55 : 0.9) * fade * tw;
          ctx.drawImage(speck, x - z, y - z, z * 2, z * 2);
        }
      }
    }
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    if (this.flutter && this.flutter.length && this.quality < 2) this.drawButterflies(t);
  };
  // Now and then a shooting star crosses the Seeds sky, high above the hills.
  G.drawShootingStar = function (t) {
    var ctx = this.ctx, W = this.W, hz = this.horizon;
    if (!this.shootAt) this.shootAt = t + 2200 + Math.random() * 3000;
    if (t < this.shootAt) return;
    var q = (t - this.shootAt) / 1000;
    if (q >= 1) { this.shootAt = t + 5500 + Math.random() * 7000; this.shootFrom = null; return; }
    var s = this.shootFrom || (this.shootFrom = { x: (0.18 + Math.random() * 0.6) * W, y: (0.05 + Math.random() * 0.14) * hz, a: 0.28 + Math.random() * 0.3, dir: Math.random() < 0.5 ? -1 : 1 });
    var run = Math.min(W * 0.32, hz * 1.1), hx = s.x + s.dir * Math.cos(s.a) * run * q, hy = s.y + Math.sin(s.a) * run * q;
    if (hy > hz * 0.7) return;
    var tl = run * 0.38 * Math.sin(Math.min(1, q * 1.6) * Math.PI / 2), tx = hx - s.dir * Math.cos(s.a) * tl, ty = hy - Math.sin(s.a) * tl;
    var lg = ctx.createLinearGradient(hx, hy, tx, ty);
    lg.addColorStop(0, 'rgba(255,250,236,0.95)'); lg.addColorStop(1, 'rgba(255,250,236,0)');
    ctx.globalAlpha = Math.sin(q * Math.PI);
    ctx.strokeStyle = lg; ctx.lineWidth = 1.6; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(tx, ty); ctx.stroke();
    var z = 5;
    ctx.drawImage(sparkSprite(), hx - z, hy - z, z * 2, z * 2);
  };
  // Butterflies wander across the meadow at the height of the blooms, a few loops and dips on the
  // way, wings beating and tilting with their turns. Each wing is one cached sprite.
  G.drawButterflies = function (t) {
    var ctx = this.ctx, dpr = this.dpr, W = this.W, list = this.flutter, base = W < 600 ? 6.5 : 10;
    var gTop = this.groundTop, gBot = this.groundBot;
    for (var i = 0; i < list.length; i++) {
      var b = list[i], tt = t * 0.001 * b.sp;
      var u = (b.u + b.dir * tt * 0.011) % 1;
      if (u < 0) u += 1;
      var wob = Math.sin(tt * 0.9 + b.ph), x = (u * 1.24 - 0.12) * W + wob * W * 0.045;
      var v = b.v + Math.sin(tt * 0.55 + b.ph * 2) * 0.07;
      var y = lerp(gTop, gBot, v) - this.sizeAt(v) * (0.95 + 0.22 * Math.sin(tt * 1.6 + b.ph)) + Math.sin(tt * 6.1 + b.ph) * 3;
      var vx = b.dir * 0.011 * 1.24 * W + Math.cos(tt * 0.9 + b.ph) * 0.9 * W * 0.045;
      var tilt = Math.max(-0.45, Math.min(0.45, vx / (W * 0.08) * 0.35));
      var size = base * b.s * (0.55 + 0.75 * v), k = size / 30, flap = 0.16 + 0.84 * Math.abs(Math.sin(tt * 10.5 + b.ph));
      var spr = wingSprite(b.col), cs = Math.cos(tilt), sn = Math.sin(tilt);
      ctx.globalAlpha = 1;
      for (var side = -1; side <= 1; side += 2) {
        var ax = side * k * flap;
        ctx.setTransform(cs * ax * dpr, sn * ax * dpr, -sn * k * dpr, cs * k * dpr, x * dpr, y * dpr);
        ctx.drawImage(spr, -2, -32);
      }
      ctx.setTransform(cs * dpr, sn * dpr, -sn * dpr, cs * dpr, x * dpr, y * dpr);
      ctx.fillStyle = 'rgba(40,26,22,0.92)';
      ctx.beginPath(); ctx.ellipse(0, 0, Math.max(0.8, size * 0.06), size * 0.36, 0, 0, TAU); ctx.fill();
      ctx.strokeStyle = 'rgba(40,26,22,0.7)'; ctx.lineWidth = 0.7;
      ctx.beginPath(); ctx.moveTo(0, -size * 0.3); ctx.quadraticCurveTo(-size * 0.12, -size * 0.55, -size * 0.2, -size * 0.62);
      ctx.moveTo(0, -size * 0.3); ctx.quadraticCurveTo(size * 0.12, -size * 0.55, size * 0.2, -size * 0.62); ctx.stroke();
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  };

  // Tapping the bloom button or the phase pill (both [data-bloom-burst]) pops it, sends a gust
  // through the meadow and scatters a burst of small flowers and sparkles from it. Nothing
  // happens to the counts.
  G.bindBurst = function () {
    var self = this, els = this.root.querySelectorAll('[data-bloom-burst]');
    for (var i = 0; i < els.length; i++) {
      (function (el) { self.on(el, 'click', function () { self.burst(el); }); })(els[i]);
    }
  };
  // The More or Less toggle in the hero header (phones only, by CSS): opens the clamped intro.
  G.bindMore = function () {
    var self = this, els = this.root.querySelectorAll('[data-bloom-more]');
    for (var i = 0; i < els.length; i++) {
      (function (el) {
        self.on(el, 'click', function () {
          var hdr = el.closest ? el.closest('.flower-box-sale__header') : el.parentNode, open;
          if (!hdr) return;
          open = hdr.classList.toggle('flower-box-sale__header--open');
          el.setAttribute('aria-expanded', open ? 'true' : 'false');
          var txt = el.querySelector('[data-bloom-more-text]');
          if (txt) txt.textContent = open ? 'Less' : 'More';
          self.later(60, function () { self.updateFocus(); });
        });
      })(els[i]);
    }
  };
  G.burstColors = function () {
    var cols = [];
    for (var i = 0; i < this.boxes.length; i++) {
      var list = this.boxes[i].colors;
      for (var j = 0; j < list.length; j++) if (list[j].alpha > 0.6 && cols.indexOf(list[j].color) < 0) cols.push(list[j].color);
    }
    if (cols.length < 4) cols = cols.concat(['#F0B9C4', '#E99A8C', '#B497D6', '#F4845F', '#43AA8B', '#e3b540']);
    return cols;
  };
  G.burst = function (el) {
    var t = now();
    if (this.lastBurst && t - this.lastBurst < 220) return;
    this.lastBurst = t;
    el.classList.remove('flower-box-sale__pop');
    void el.offsetWidth;
    el.classList.add('flower-box-sale__pop');
    this.later(700, function () { el.classList.remove('flower-box-sale__pop'); });
    if (this.reduced || !d.body || !('animate' in Element.prototype)) return;
    this.gustEnd = t + GUST_MS;
    this.kick();
    var r = el.getBoundingClientRect();
    var layer = d.createElement('div');
    layer.className = 'flower-box-sale__burst';
    layer.setAttribute('aria-hidden', 'true');
    layer.style.left = (r.left + r.width / 2) + 'px';
    layer.style.top = (r.top + r.height / 2) + 'px';
    // From a button near a screen edge (the phone's top right corner) the burst leans inward,
    // so the petals fly across the meadow instead of off the screen.
    var vw = d.documentElement.clientWidth || w.innerWidth || 0, ox = r.left + r.width / 2;
    var lean = vw && ox < 90 ? 1 : vw && ox > vw - 90 ? -1 : 0;
    var cols = this.burstColors(), flowers = 16, sparks = 12, total = flowers + sparks, done = 0;
    var finish = function () { if (++done >= total && layer.parentNode) layer.parentNode.removeChild(layer); };
    for (var i = 0; i < total; i++) {
      var flower = i < flowers, el2 = d.createElement('span');
      el2.className = 'flower-box-sale__burst-' + (flower ? 'flower' : 'spark');
      var size = flower ? 16 + Math.random() * 16 : 7 + Math.random() * 8;
      el2.innerHTML = flower ? burstFlowerSvg(cols[(Math.random() * cols.length) | 0]) : burstSparkSvg(SPARK_COLS[i % SPARK_COLS.length]);
      el2.style.width = el2.style.height = size.toFixed(1) + 'px';
      layer.appendChild(el2);
      var ang = ((flower ? i : i - flowers) / (flower ? flowers : sparks)) * TAU + Math.random() * 0.7;
      var dist = (flower ? 64 : 44) + Math.random() * (flower ? 116 : 70);
      var dx = Math.cos(ang) * dist, dy = Math.sin(ang) * dist * 0.8 - 22, rot = (Math.random() - 0.5) * 540;
      if (lean) dx = lean * (Math.abs(dx) * 0.9 + 14);
      var anim = el2.animate([
        { transform: 'translate(-50%, -50%) scale(0.15) rotate(0deg)', opacity: 1 },
        { transform: 'translate(calc(-50% + ' + (dx * 0.8).toFixed(1) + 'px), calc(-50% + ' + (dy * 0.8).toFixed(1) + 'px)) scale(1) rotate(' + (rot * 0.7).toFixed(0) + 'deg)', opacity: 1, offset: 0.55 },
        { transform: 'translate(calc(-50% + ' + dx.toFixed(1) + 'px), calc(-50% + ' + (dy + 48).toFixed(1) + 'px)) scale(0.85) rotate(' + rot.toFixed(0) + 'deg)', opacity: 0 }
      ], { duration: 900 + Math.random() * 600, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'forwards' });
      anim.onfinish = finish;
    }
    d.body.appendChild(layer);
    this.later(2400, function () { if (layer.parentNode) layer.parentNode.removeChild(layer); });
  };
  function burstFlowerSvg(col) {
    return '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><g fill="' + col + '"><circle cx="10" cy="5.2" r="3.7"/><circle cx="14.6" cy="8.5" r="3.7"/><circle cx="12.8" cy="13.9" r="3.7"/><circle cx="7.2" cy="13.9" r="3.7"/><circle cx="5.4" cy="8.5" r="3.7"/></g><circle cx="10" cy="10" r="2.4" fill="#e3b540"/></svg>';
  }
  // Burst sparkles in gold, cream, rose, lilac and mint.
  var SPARK_COLS = ['#FFF3C4', '#F6CF6A', '#FFC2DD', '#D9C4FF', '#C8F5E2'];
  function burstSparkSvg(col) {
    return '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M10 0.5 12 8l7.5 2-7.5 2-2 7.5-2-7.5L0.5 10 8 8z" fill="' + (col || '#FFF3C4') + '"/></svg>';
  }


  /* ---------- public API ---------- */
  function init(root) {
    if (!root || !root.querySelector) return null;
    try {
      if (root.__flowerBoxSale) root.__flowerBoxSale.destroy();
      var g = new Garden(root);
      root.__flowerBoxSale = g;
      if (g.reduced) g.setReduced(true);
      instances.push(g);
      return g;
    } catch (e) {
      debug('init failed', e);
      return null;
    }
  }
  function initAll(scope) {
    var list = (scope || d).querySelectorAll ? (scope || d).querySelectorAll('[data-flower-box-sale]') : [];
    if (scope && scope.matches && scope.matches('[data-flower-box-sale]')) init(scope);
    for (var i = 0; i < list.length; i++) init(list[i]);
  }
  function pickBox(withFlowers) {
    var g = instances[0];
    if (!g || !g.boxes.length) return null;
    var pool = g.boxes.filter(function (b) { return !withFlowers || g.shown(b) > 0; });
    return pool.length ? pool[(Math.random() * pool.length) | 0].key : null;
  }
  function each(fn) { instances.slice().forEach(function (g) { try { fn(g); } catch (e) { debug(e); } }); }

  w.FlowerBoxSale = {
    init: init,
    instances: instances,
    simulateSale: function (key) { key = key || pickBox(false); if (key) each(function (g) { g.simulate(key, 1); }); return key; },
    simulateRemoval: function (key) { key = key || pickBox(true); if (key) each(function (g) { g.simulate(key, -1); }); return key; },
    setPhase: function (key) { each(function (g) { g.setPhase(key); }); },
    // See your flower: answer is a /mine body, or null for the bloom_flower fallback.
    showMine: function (answer) { each(function (g) { g.hideMine(); g.showMine(answer); }); },
    hideMine: function () { each(function (g) { g.hideMine(); }); },
    setReducedMotion: function (flag) {
      reducedOverride = flag == null ? null : !!flag;
      each(function (g) { g.setReduced(prefersReduced()); });
    },
    debugFill: function (n) { each(function (g) { g.debugFill(Math.max(0, n | 0)); }); },
    demo: function (mode) { each(function (g) { g.setDemo(mode); }); },
    setQuality: function (q) { each(function (g) { g.setQuality(q | 0); }); },
    resolvePhase: function (data, at) {
      data = data || {};
      var p = resolvePhase((data.phases || []).filter(Boolean), data.timezone || 'America/New_York', at == null ? Date.now() : +at);
      return p ? p.key : null;
    },
    zonedInstant: zonedInstant
  };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', function () { initAll(d); });
  else initAll(d);
  d.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  d.addEventListener('shopify:section:unload', function (e) {
    var list = e.target && e.target.querySelectorAll ? e.target.querySelectorAll('[data-flower-box-sale]') : [];
    for (var i = 0; i < list.length; i++) if (list[i].__flowerBoxSale) list[i].__flowerBoxSale.destroy();
  });
})(window, document);
